import { useState, useEffect, createContext, useContext, useRef, useCallback, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { HelmetProvider, Helmet } from "react-helmet-async";
import ScrollToTop from "@/components/ScrollToTop";
import { LazySection, SectionSkeleton, IdleLoad, InteractionLoad } from "@/components/LazySection";
import { SmartPhoneLink } from "@/components/SmartPhoneLink";

// Lazy load heavy components - loaded after interaction
const StaticWizard = lazy(() => import("@/components/StaticWizard").then(m => ({ default: m.StaticWizard })));

// Lazy load framer-motion - NOT imported at startup
const LazyMotion = lazy(() => import("framer-motion").then(m => ({ 
  default: ({ children, ...props }) => <m.motion.div {...props}>{children}</m.motion.div>
})));

// Toaster removed for stability - not needed for this site

// Minimal motion wrapper - no animation at first paint
const motion = {
  div: ({ children, initial, animate, variants, whileHover, whileTap, ...props }) => <div {...props}>{children}</div>,
  a: ({ children, ...props }) => <a {...props}>{children}</a>,
  button: ({ children, ...props }) => <button {...props}>{children}</button>,
  span: ({ children, ...props }) => <span {...props}>{children}</span>,
  p: ({ children, ...props }) => <p {...props}>{children}</p>,
};
const AnimatePresence = ({ children }) => children;

import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Star, 
  ChevronRight, 
  Thermometer, 
  Zap, 
  Flame, 
  Bug, 
  Shield, 
  FileCheck, 
  Home, 
  AlertTriangle, 
  Droplets,
  Menu,
  X,
  CheckCircle,
  Eye,
  Clock3,
  BadgeCheck,
  MessageCircle,
  Lock,
  Scale,
  ExternalLink,
  Moon,
  Sun,
  ChevronDown,
  ChevronUp,
  Send,
  User,
  Building,
  FileText,
  ArrowLeft,
  Newspaper,
  RefreshCw,
  HelpCircle,
  Bot,
  Loader2
} from "lucide-react";

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};

const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme');
      // Si localStorage.theme existe, l'utiliser. Sinon, dark par défaut.
      return saved ? saved === 'dark' : true;
    }
    return true; // Dark par défaut
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  const toggleTheme = () => {
    const newValue = !isDark;
    setIsDark(newValue);
    localStorage.setItem('theme', newValue ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ isDark, setIsDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// UI Sound Hook - Soft click feedback for interactions
const useUISound = () => {
  const audioContextRef = useRef(null);

  const playClickSound = useCallback(() => {
    try {
      // Create audio context on first interaction (required by browsers)
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      
      // Resume context if suspended (required after page load)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      
      // Create a soft "glass tap" sound
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      // High frequency for a crisp, subtle click
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(1800, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.08);
      
      // Very low volume (-25dB perceived)
      gainNode.gain.setValueAtTime(0.08, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.1); // 100ms duration
      
    } catch (e) {
      // Silently fail if audio not supported
      console.log('Audio feedback not available');
    }
  }, []);

  return { playClickSound };
};

// Social Icons Components
const TwitterIcon = () => (
 <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InstagramIcon = () => (
 <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const LinkedInIcon = () => (
 <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// ============================================
// GA4 TRACKING HELPER - Événements avancés
// ============================================
const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...eventParams,
      send_to: 'G-2J21ZVQ4MJ'
    });
  }
};

// Track phone clicks
const trackPhoneClick = (location = 'unknown') => {
  trackEvent('phone_click', {
    event_category: 'contact',
    event_label: location,
    value: 1
  });
};

// Track form submissions
const trackFormSubmit = (formType = 'devis') => {
  trackEvent('form_submit', {
    event_category: 'conversion',
    event_label: formType,
    value: 1
  });
};

// Track page views
const trackPageView = (pageName, pageTitle) => {
  trackEvent('page_view', {
    page_title: pageTitle,
    page_location: window.location.href,
    page_path: window.location.pathname
  });
};

// Track diagnostic page views
const trackDiagnosticView = (diagnosticName) => {
  trackEvent('diagnostic_view', {
    event_category: 'engagement',
    diagnostic_name: diagnosticName,
    value: 1
  });
};

// Track scroll depth
const trackScrollDepth = (percentage) => {
  trackEvent('scroll_depth', {
    event_category: 'engagement',
    percent_scrolled: percentage,
    value: percentage
  });
};

// Track CTA clicks
const trackCTAClick = (ctaName, ctaLocation) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    cta_name: ctaName,
    cta_location: ctaLocation,
    value: 1
  });
};

// ============================================
// VILLES DATA - Pages géolocalisées SEO
// ============================================
const villesData = [
  // Haute-Loire (43)
  { slug: "le-puy-en-velay", name: "Le Puy-en-Velay", cp: "43000", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "18500", isChefLieu: true },
  { slug: "yssingeaux", name: "Yssingeaux", cp: "43200", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "7200" },
  { slug: "monistrol-sur-loire", name: "Monistrol-sur-Loire", cp: "43120", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "8800" },
  { slug: "sainte-sigolene", name: "Sainte-Sigolène", cp: "43600", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "6200" },
  { slug: "aurec-sur-loire", name: "Aurec-sur-Loire", cp: "43110", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "6000" },
  { slug: "langeac", name: "Langeac", cp: "43300", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "3800" },
  { slug: "craponne-sur-arzon", name: "Craponne-sur-Arzon", cp: "43500", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "2200" },
  { slug: "retournac", name: "Retournac", cp: "43130", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "2600" },
  { slug: "saugues", name: "Saugues", cp: "43170", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "1800" },
  { slug: "pradelles", name: "Pradelles", cp: "43420", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "500" },
  { slug: "cayres", name: "Cayres", cp: "43510", dept: "Haute-Loire", deptNum: "43", region: "Auvergne-Rhône-Alpes", population: "700" },
  // Lozère (48)
  { slug: "langogne", name: "Langogne", cp: "48300", dept: "Lozère", deptNum: "48", region: "Occitanie", population: "2800", hasBureau: true },
  { slug: "auroux", name: "Auroux", cp: "48600", dept: "Lozère", deptNum: "48", region: "Occitanie", population: "400" },
  { slug: "chaudeyrac", name: "Chaudeyrac", cp: "48170", dept: "Lozère", deptNum: "48", region: "Occitanie", population: "200" },
  { slug: "la-bastide-puylaurent", name: "La Bastide-Puylaurent", cp: "48250", dept: "Lozère", deptNum: "48", region: "Occitanie", population: "150" },
  { slug: "grandrieu", name: "Grandrieu", cp: "48600", dept: "Lozère", deptNum: "48", region: "Occitanie", population: "700" },
  { slug: "chateauneuf-de-randon", name: "Châteauneuf-de-Randon", cp: "48170", dept: "Lozère", deptNum: "48", region: "Occitanie", population: "500" },
  { slug: "le-malzieu-ville", name: "Le Malzieu-Ville", cp: "48140", dept: "Lozère", deptNum: "48", region: "Occitanie", population: "800" }
];

// ============================================
// COMPTEUR ANIMÉ - Statistiques
// ============================================
const AnimatedCounter = ({ end, duration = 2, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
 <span ref={ref} className="tabular-nums">
      {prefix}{count}{suffix}
    </span>
  );
};

// ============================================
// EXIT INTENT POPUP - Capture de leads
// ============================================
const ExitIntentPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Check if already shown in this session
    if (sessionStorage.getItem('exitPopupShown')) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShown) {
        setShowPopup(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
        trackEvent('exit_intent_shown', { event_category: 'engagement' });
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShown]);

  if (!showPopup) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
 className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={() => setShowPopup(false)}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", damping: 20 }}
 className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-md w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
 <div className="text-center">
 <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
 <Phone className="w-10 h-10 text-white" />
            </div>
 <h3 className="font-bold text-2xl text-gray-800 dark:text-white mb-3">
              Besoin d'un devis gratuit ?
            </h3>
 <p className="text-gray-600 dark:text-gray-300 mb-6">
              Appelez-nous maintenant pour un <strong>devis gratuit</strong> et une intervention sous <strong>24-48h</strong> en Haute-Loire et Lozère !
            </p>
 <div className="space-y-3">
              <SmartPhoneLink
                onClick={() => {
                  trackPhoneClick('exit_popup');
                  trackEvent('exit_intent_call', { event_category: 'conversion' });
                }}
 className="flex items-center justify-center gap-3 w-full py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-indigo-300 transition-all"
              >
 <Phone className="w-5 h-5" />
                04 71 08 27 09
              </SmartPhoneLink>
              <button
                onClick={() => setShowPopup(false)}
 className="w-full py-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                Non merci, je continue ma visite
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============================================
// SCROLL DEPTH TRACKER - GA4
// ============================================
const ScrollDepthTracker = () => {
  const [trackedDepths, setTrackedDepths] = useState(new Set());

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);
      
      const milestones = [25, 50, 75, 90, 100];
      milestones.forEach(milestone => {
        if (scrollPercent >= milestone && !trackedDepths.has(milestone)) {
          trackScrollDepth(milestone);
          setTrackedDepths(prev => new Set([...prev, milestone]));
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [trackedDepths]);

  return null;
};

// ============================================
// HELP ASSISTANT - Aide Interactive
// ============================================
const HelpAssistant = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const messagesEndRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message
      setMessages([{
        role: 'assistant',
        content: "Posez votre question sur les diagnostics immobiliers obligatoires en France (vente ou location) ; les réponses sont fournies à titre informatif à partir de sources réglementaires officielles."
      }]);
      trackEvent('help_assistant_opened', { event_category: 'engagement' });
    }
  }, [isOpen, messages.length]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    trackEvent('help_assistant_message_sent', { 
      event_category: 'engagement',
      message_length: userMessage.length
    });

    // Mode statique IONOS - pas de backend
    // Réponse automatique pour rediriger vers le téléphone
    setTimeout(() => {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Pour une réponse personnalisée à votre question, contactez-nous directement au 04 71 08 27 09. Notre équipe est disponible du lundi au samedi de 8h à 22h." 
      }]);
      setIsLoading(false);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  // Panel positioning styles
  const panelStyle = isMobile 
    ? {
        // Mobile: centered with margins
        bottom: '80px',
        left: '16px',
        right: '16px',
        width: 'calc(100% - 32px)',
        maxWidth: '420px',
        height: '480px',
        maxHeight: '65vh',
        marginLeft: 'auto',
        marginRight: 'auto'
      }
    : {
        // Desktop: positioned to the left, aligned with hero section
        top: '100px',
        left: 'max(32px, calc(50% - 580px))',
        width: '400px',
        height: '520px',
        maxHeight: '70vh'
      };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        transition={{ duration: 0.2 }}
 className="fixed z-[90] flex flex-col overflow-hidden border border-gray-700 bg-gray-900 rounded-2xl shadow-2xl shadow-black/50"
        style={panelStyle}
        data-testid="help-assistant-window"
      >
        {/* Header */}
 <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-3 flex items-center justify-between">
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
 <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
 <h3 className="text-white font-semibold text-sm">Aide Interactive</h3>
 <p className="text-white/70 text-xs">Diagnostics immobiliers</p>
            </div>
          </div>
          <button
            onClick={onClose}
 className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Fermer l'aide"
          >
 <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Disclaimer */}
 <div className="bg-amber-900/30 border-b border-amber-700/30 px-4 py-2">
 <p className="text-amber-200/80 text-xs leading-relaxed">
            Cet assistant utilise une intelligence artificielle pour fournir des informations générales à partir de sources officielles. Les réponses peuvent contenir des erreurs ou des omissions et ne remplacent pas les textes réglementaires ni l'avis d'un professionnel.
          </p>
        </div>

        {/* Messages */}
 <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, index) => (
            <div
              key={index}
 className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
 className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-800 text-gray-100 border border-gray-700'
                }`}
              >
 <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {isLoading && (
 <div className="flex justify-start">
 <div className="bg-gray-800 text-gray-100 rounded-2xl px-4 py-3 border border-gray-700">
 <div className="flex items-center gap-2">
 <Loader2 className="w-4 h-4 animate-spin" />
 <span className="text-sm">Recherche en cours...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
 <div className="border-t border-gray-700 p-3 bg-gray-800/50">
 <div className="flex items-center gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Posez votre question..."
              disabled={isLoading}
 className="flex-1 bg-gray-800 border border-gray-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-indigo-500 disabled:opacity-50"
              data-testid="help-assistant-input"
            />
            <button
              onClick={sendMessage}
              disabled={isLoading || !inputValue.trim()}
 className="w-10 h-10 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-xl flex items-center justify-center transition-colors"
              aria-label="Envoyer"
              data-testid="help-assistant-send"
            >
 <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Integrated Help Assistant - Embedded in hero section (not floating)
const IntegratedHelpAssistant = ({ onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    // Mode statique IONOS - réponse automatique
    setTimeout(() => {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Pour une réponse personnalisée, contactez-nous au 04 71 08 27 09. Notre équipe est disponible du lundi au samedi de 8h à 22h." 
      }]);
      setIsLoading(false);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div 
 className="w-full rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-white/20 dark:border-gray-600/50"
      style={{ 
        height: '400px', 
        maxHeight: '50vh',
        background: 'linear-gradient(135deg, rgba(55, 48, 107, 0.95) 0%, rgba(79, 70, 120, 0.9) 50%, rgba(99, 88, 140, 0.85) 100%)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 25px 50px -12px rgba(99, 102, 241, 0.25), 0 0 0 1px rgba(255,255,255,0.1) inset'
      }}
      data-testid="integrated-help-assistant"
    >
      {/* Header - Glassmorphism style */}
      <div 
 className="px-4 py-3 flex items-center justify-between border-b border-white/10"
        style={{ background: 'linear-gradient(90deg, rgba(99, 102, 241, 0.4) 0%, rgba(168, 85, 247, 0.4) 50%, rgba(236, 72, 153, 0.4) 100%)' }}
      >
 <div className="flex items-center gap-2">
 <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
 <HelpCircle className="w-4 h-4 text-white" />
          </div>
          <div>
 <h3 className="text-white font-semibold text-sm">Aide Interactive</h3>
 <p className="text-white/60 text-xs">Propulsée par IA</p>
          </div>
        </div>
        <button
          onClick={onClose}
 className="w-8 h-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
          aria-label="Fermer"
        >
 <X className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Messages - Lighter background */}
      <div 
 className="flex-1 overflow-y-auto p-4 space-y-3"
        style={{ background: 'rgba(45, 40, 80, 0.5)' }}
      >
        {messages.length === 0 && (
 <div className="text-center py-6">
 <div className="w-12 h-12 bg-indigo-500/30 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3">
 <MessageCircle className="w-6 h-6 text-indigo-300" />
            </div>
 <p className="text-gray-200 text-sm">Posez-moi vos questions sur les diagnostics immobiliers !</p>
 <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {['DPE obligatoire ?', 'Prix diagnostic', 'Délai intervention'].map((q, i) => (
                <button
                  key={i}
                  onClick={() => { setInputValue(q); }}
 className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-gray-100 text-xs rounded-full border border-white/20 transition-colors backdrop-blur-sm"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {messages.map((msg, idx) => (
 <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
 <div className={`max-w-[85%] rounded-2xl px-4 py-2 backdrop-blur-sm ${
              msg.role === 'user' 
                ? 'bg-indigo-500/80 text-white' 
                : 'bg-white/15 text-gray-100 border border-white/20'
            }`}>
 <p className="text-sm leading-relaxed">{msg.content}</p>
            </div>
          </div>
        ))}
        
        {isLoading && (
 <div className="flex justify-start">
 <div className="bg-white/15 text-gray-100 rounded-2xl px-4 py-3 border border-white/20 backdrop-blur-sm">
 <div className="flex items-center gap-2">
 <Loader2 className="w-4 h-4 animate-spin text-indigo-300" />
 <span className="text-sm">Recherche...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input - Glassmorphism */}
      <div 
 className="p-3 border-t border-white/10"
        style={{ background: 'rgba(60, 55, 100, 0.6)' }}
      >
 <div className="flex items-center gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Votre question..."
            disabled={isLoading}
 className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-300 focus:outline-none focus:border-indigo-400 focus:bg-white/15 disabled:opacity-50 backdrop-blur-sm"
          />
          <button
            onClick={sendMessage}
            disabled={isLoading || !inputValue.trim()}
 className="w-10 h-10 bg-indigo-500 hover:bg-indigo-600 disabled:bg-white/10 disabled:cursor-not-allowed rounded-xl flex items-center justify-center transition-colors"
            aria-label="Envoyer"
          >
 <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Help Button for Header (Desktop)
const HelpButton = ({ onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
 <div className="relative">
      <button
        id="help-assistant-trigger"
        onClick={onClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
 className="relative h-11 px-5 flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 rounded-full transition-all shadow-lg shadow-indigo-200 dark:shadow-indigo-900 hover:shadow-indigo-300 whitespace-nowrap"
        data-testid="help-assistant-btn"
        aria-label="Ouvrir l'aide interactive"
      >
        {/* Animation pulsante */}
 <span className="absolute inset-0 rounded-full bg-indigo-400 animate-ping opacity-20" />
 <span className="absolute inset-0 rounded-full bg-purple-400 animate-ping opacity-15" style={{ animationDelay: '0.5s' }} />
        
 <HelpCircle className="w-4 h-4 relative z-10 flex-shrink-0" />
 <span className="relative z-10">Aide interactive</span>
      </button>
      
      {/* Info-bulle au survol (Desktop) */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
 className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-medium px-3 py-2 rounded-lg whitespace-nowrap z-50 shadow-lg pointer-events-none"
          >
            Aide interactive propulsée par IA
 <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-[6px] border-transparent border-b-purple-600" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Help Button for Mobile Header
const MobileHelpButton = ({ onClick }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const tooltipTimeoutRef = useRef(null);

  const handleTap = () => {
    if (tapCount === 0) {
      // Premier tap: afficher l'info-bulle
      setShowTooltip(true);
      setTapCount(1);
      
      // Auto-disparition après 3 secondes
      tooltipTimeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
        setTapCount(0);
      }, 3000);
    } else {
      // Second tap: ouvrir l'aide
      clearTimeout(tooltipTimeoutRef.current);
      setShowTooltip(false);
      setTapCount(0);
      onClick();
    }
  };

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  return (
 <div className="relative">
      <button
        id="help-assistant-trigger-mobile"
        onClick={handleTap}
 className="relative h-10 px-4 flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 active:from-indigo-600 active:via-purple-600 active:to-pink-600 rounded-full transition-all shadow-lg shadow-indigo-200 dark:shadow-indigo-900"
        aria-label="Aide interactive"
        data-testid="mobile-help-btn"
      >
        {/* Animation pulsante identique au bouton téléphone */}
 <span className="absolute inset-0 rounded-full bg-indigo-400 animate-ping opacity-25" />
 <span className="absolute inset-0 rounded-full bg-purple-400 animate-ping opacity-20" style={{ animationDelay: '0.5s' }} />
        
 <HelpCircle className="w-4 h-4 relative z-10" />
 <span className="relative z-10">Aide</span>
      </button>
      
      {/* Info-bulle mobile (tap) - repositionnée pour ne pas être coupée */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
 className="absolute top-full mt-2 right-0 bg-purple-600 text-white text-xs font-medium px-3 py-2 rounded-lg z-50 shadow-lg"
          >
 <span className="block whitespace-nowrap">Aide interactive propulsée par IA</span>
 <span className="block text-[10px] opacity-70 mt-1">Tapez à nouveau pour ouvrir</span>
 <div className="absolute bottom-full right-4 border-[6px] border-transparent border-b-purple-600" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Services data - SEO Optimized
const services = [
  {
    id: "dpe",
    slug: "diagnostic-performance-energetique-dpe",
    name: "DPE",
    fullName: "Diagnostic de Performance Énergétique",
    icon: Thermometer,
    description: "Le diagnostic de performance énergétique (DPE) est un document qui donne une estimation de la consommation énergétique d'un logement et son taux d'émission de gaz à effet de serre. Ce document vise à informer le candidat acquéreur ou locataire sur le bien qu'il projette d'acheter ou de louer.",
    longDescription: `Le DPE est obligatoire pour toute vente ou location d'un bien immobilier en France. Il évalue deux critères principaux :
    
• La consommation d'énergie primaire (étiquette énergie de A à G)
• Les émissions de gaz à effet de serre (étiquette climat de A à G)

Le nouveau DPE, en vigueur depuis juillet 2021, est devenu opposable, ce qui signifie que l'acquéreur ou le locataire peut se retourner contre le vendeur ou le bailleur en cas d'information erronée.

Depuis 2025, les logements classés G sont interdits à la location. Les classes F seront interdites en 2028, et les E en 2034.

CPPB réalise vos DPE en Haute-Loire (43) et Lozère (48) avec des équipements de pointe et une expertise certifiée B.2.C. Intervention au Puy-en-Velay, Yssingeaux, Langogne, Auroux, Chaudeyrac, La Bastide-Puylaurent.`,
    validity: "10 ans",
    required: "Vente & Location",
    metaTitle: "DPE Diagnostic Performance Énergétique Haute-Loire 43 | Le Puy-en-Velay Yssingeaux Langogne | CPPB Expert Certifié",
    metaDescription: "⭐ DPE obligatoire vente location Haute-Loire (43) Lozère (48). Expert certifié B.2.C. Le Puy-en-Velay 43000, Yssingeaux, Langogne, Auroux, Chaudeyrac. Passoire thermique classe G F. Devis GRATUIT ☎ 04 71 08 27 09. Rapport 72h.",
    metaKeywords: "DPE haute-loire, DPE 43, DPE le puy-en-velay, DPE 43000, diagnostic performance énergétique haute-loire, DPE yssingeaux, DPE langogne, DPE auroux, DPE chaudeyrac, DPE la bastide-puylaurent, DPE lozère 48, passoire thermique, classe G, classe F, étiquette énergie"
  },
  {
    id: "amiante",
    slug: "diagnostic-amiante",
    name: "Amiante",
    fullName: "Diagnostic Amiante",
    icon: Shield,
    description: "Le diagnostic amiante permet de repérer les matériaux et produits contenant de l'amiante dans les biens immobiliers construits avant le 1er juillet 1997. Ce diagnostic est obligatoire pour protéger la santé des occupants et des travailleurs amenés à intervenir sur le bâtiment.",
    longDescription: `L'amiante a été largement utilisé dans la construction jusqu'à son interdiction en 1997. Le diagnostic amiante est obligatoire pour tous les biens construits avant cette date.

Types de diagnostics amiante :
• Diagnostic Amiante avant Vente (DAV)
• Dossier Amiante Parties Privatives (DAPP)
• Diagnostic Amiante avant Travaux (DAT)
• Diagnostic Amiante avant Démolition

En cas de présence d'amiante, des mesures de protection ou de retrait peuvent être nécessaires.

Notre équipe certifiée intervient sur toute la Haute-Loire (43) et la Lozère (48) pour vos diagnostics amiante : Le Puy-en-Velay, Yssingeaux, Monistrol-sur-Loire, Langogne, Auroux.`,
    validity: "3 ans si positif / Illimité si négatif",
    required: "Vente & Location",
    metaTitle: "Diagnostic Amiante Haute-Loire 43 | Le Puy-en-Velay Yssingeaux Langogne | CPPB Expert Certifié",
    metaDescription: "⭐ Diagnostic amiante obligatoire biens avant 1997. Expert certifié Haute-Loire (43) Lozère (48). Le Puy-en-Velay, Yssingeaux, Langogne, Auroux, Chaudeyrac. DAV, DAPP, DAT. Devis GRATUIT ☎ 04 71 08 27 09",
    metaKeywords: "diagnostic amiante haute-loire, diagnostic amiante 43, amiante le puy-en-velay, repérage amiante, DAV, DAPP, diagnostic amiante avant travaux, amiante yssingeaux, amiante langogne, amiante auroux"
  },
  {
    id: "plomb",
    slug: "diagnostic-plomb-crep",
    name: "Plomb",
    fullName: "CREP - Constat de Risque d'Exposition au Plomb",
    icon: Droplets,
    description: "Le constat de risque d'exposition au plomb (Crep) est un document qui donne des informations sur la présence de plomb dans un logement. Il concerne uniquement les logements construits avant 1949. Le plomb est généralement présent dans les anciennes peintures de ces logements.",
    longDescription: `Le CREP (Constat de Risque d'Exposition au Plomb) est obligatoire pour tous les logements construits avant le 1er janvier 1949.

Le diagnostic mesure la concentration en plomb des revêtements (peintures, enduits) à l'aide d'un appareil à fluorescence X.

Seuils réglementaires :
• Concentration ≥ 1 mg/cm² : présence de plomb signalée
• Si dégradation : travaux obligatoires pour supprimer le risque d'exposition

Le plomb présente des risques sanitaires importants, notamment pour les enfants (saturnisme).

CPPB intervient rapidement pour vos diagnostics plomb en Haute-Loire (43) et Lozère (48) : Le Puy-en-Velay, Yssingeaux, Langogne, Auroux.`,
    validity: "1 an si positif / Illimitée si négatif",
    required: "Vente & Location",
    metaTitle: "Diagnostic Plomb CREP Haute-Loire 43 | Le Puy-en-Velay Yssingeaux Langogne | CPPB Expert",
    metaDescription: "⭐ CREP diagnostic plomb obligatoire logements avant 1949. Saturnisme risque enfants. Expert certifié Haute-Loire (43) Lozère (48). Le Puy-en-Velay, Yssingeaux, Langogne, Auroux. Devis GRATUIT ☎ 04 71 08 27 09",
    metaKeywords: "diagnostic plomb haute-loire, CREP haute-loire, diagnostic plomb 43, plomb le puy-en-velay, saturnisme, plomb peintures anciennes, plomb yssingeaux, plomb langogne, plomb auroux"
  },
  {
    id: "gaz",
    slug: "diagnostic-gaz",
    name: "Gaz",
    fullName: "Diagnostic Gaz",
    icon: Flame,
    description: "Le diagnostic gaz est généralement requis si l'installation intérieure de gaz a plus de 15 ans. Une attestation de conformité récente peut modifier la situation.",
    longDescription: `Le diagnostic gaz est généralement requis si l'installation intérieure de gaz a plus de 15 ans.
Une attestation de conformité récente peut modifier la situation.

L'obligation dépend du type d'alimentation : gaz de ville, citerne, puis bouteille.

En cas d'alimentation par bouteille, le diagnostic gaz n'est pas requis lorsqu'il existe un raccordement direct entre la plaque de cuisson et la bouteille, sans aucune partie fixe (uniquement un tuyau souple ou flexible, sans canalisation rigide).

Nos diagnostiqueurs certifiés interviennent sur toute la Haute-Loire (43) et la Lozère (48) : Le Puy-en-Velay, Yssingeaux, Langogne, Auroux.`,
    validity: "3 ans (vente) / 6 ans (location)",
    required: "Vente & Location",
    metaTitle: "Diagnostic Gaz Haute-Loire 43 | Le Puy-en-Velay Yssingeaux Langogne | CPPB Expert Certifié",
    metaDescription: "⭐ Diagnostic gaz obligatoire installations +15 ans. Sécurité chaudière, tuyauteries. Expert certifié Haute-Loire (43) Lozère (48). Le Puy-en-Velay, Yssingeaux, Langogne, Auroux. Devis GRATUIT ☎ 04 71 08 27 09",
    metaKeywords: "diagnostic gaz haute-loire, diagnostic gaz 43, gaz le puy-en-velay, installation gaz, sécurité gaz, gaz yssingeaux, gaz langogne, gaz auroux"
  },
  {
    id: "electricite",
    slug: "diagnostic-electricite",
    name: "Électricité",
    fullName: "Diagnostic Électricité",
    icon: Zap,
    description: "L'état de l'installation intérieure d'électricité est un diagnostic qui donne un aperçu de la sécurité des installations électriques d'un logement. Il est obligatoire pour toutes les installations électriques qui ont plus de 15 ans.",
    longDescription: `Le diagnostic électricité évalue la sécurité des installations électriques de plus de 15 ans.

Points de contrôle principaux :
• Présence d'un appareil général de commande et de protection
• Présence d'une prise de terre et de son circuit
• Présence de dispositifs différentiels
• Présence de dispositifs de protection contre les surintensités
• Absence de matériels vétustes ou inadaptés

Le diagnostic identifie les anomalies pouvant compromettre la sécurité des personnes.

CPPB réalise vos diagnostics électricité dans toute la Haute-Loire (43) et la Lozère (48) : Le Puy-en-Velay, Yssingeaux, Langogne, Auroux.`,
    validity: "3 ans (vente) / 6 ans (location)",
    required: "Vente & Location",
    metaTitle: "Diagnostic Électricité Haute-Loire 43 | Le Puy-en-Velay Yssingeaux Langogne | CPPB Expert",
    metaDescription: "⭐ Diagnostic électricité obligatoire installations +15 ans. Sécurité électrique, disjoncteur, prise terre. Expert Haute-Loire (43) Lozère (48). Le Puy-en-Velay, Yssingeaux, Langogne, Auroux. Devis GRATUIT ☎ 04 71 08 27 09",
    metaKeywords: "diagnostic électricité haute-loire, diagnostic électricité 43, électricité le puy-en-velay, installation électrique, sécurité électrique, électricité yssingeaux, électricité langogne, électricité auroux"
  },
  {
    id: "termites",
    slug: "diagnostic-termites",
    name: "Termites",
    fullName: "Diagnostic Termites",
    icon: Bug,
    description: "L'état relatif à la présence de termites, également appelé diagnostic termites, donne des informations sur la présence ou non d'insectes xylophages (termites en particulier) dans un logement.",
    longDescription: `Le diagnostic termites est obligatoire dans les zones définies par arrêté préfectoral comme étant infestées ou susceptibles de l'être.

Le diagnostic recherche :
• La présence de termites
• Les indices d'infestation (galeries, dégâts)
• Les conditions favorables au développement

Les termites peuvent causer des dégâts structurels importants aux bâtiments en s'attaquant au bois.

En Haute-Loire (43) et Lozère (48), aucune commune n'est concernée par ce diagnostic. Contactez CPPB pour vérifier si votre bien est concerné.`,
    validity: "6 mois",
    required: "Vente uniquement",
    metaTitle: "Diagnostic Termites Haute-Loire 43 | Le Puy-en-Velay Yssingeaux Langogne | CPPB Expert",
    metaDescription: "⭐ Diagnostic termites insectes xylophages zones à risque. Expert certifié Haute-Loire (43) Lozère (48). Le Puy-en-Velay, Yssingeaux, Langogne, Auroux. Devis GRATUIT ☎ 04 71 08 27 09",
    metaKeywords: "diagnostic termites haute-loire, diagnostic termites 43, termites le puy-en-velay, insectes xylophages, termites yssingeaux, termites langogne, termites auroux"
  },
  {
    id: "carrez",
    slug: "loi-carrez-mesurage",
    name: "Loi Carrez",
    fullName: "Certificat Loi Carrez",
    icon: Home,
    description: "La loi Carrez encadre le calcul de la superficie privative dans le cadre de la mise en vente d'un logement de copropriété et oblige que celle-ci soit mentionnée dans l'acte de vente.",
    longDescription: `Le certificat Loi Carrez est obligatoire pour la vente de tout lot de copropriété d'une surface au moins égale à 8 m².

Surfaces prises en compte :
• Planchers des locaux clos et couverts
• Hauteur sous plafond ≥ 1,80 m

Surfaces exclues :
• Balcons, terrasses, loggias
• Caves, garages, parkings
• Jardins
• Parties d'une hauteur < 1,80 m

Une erreur de plus de 5% sur la superficie peut entraîner une diminution proportionnelle du prix.

CPPB réalise vos mesurages Loi Carrez avec précision en Haute-Loire (43) et Lozère (48) : Le Puy-en-Velay, Yssingeaux, Langogne, Auroux.`,
    validity: "À chaque mutation ou en cas de travaux",
    required: "Vente uniquement",
    metaTitle: "Mesurage Loi Carrez Haute-Loire 43 | Le Puy-en-Velay Yssingeaux Langogne | CPPB Expert",
    metaDescription: "⭐ Certificat Loi Carrez mesurage superficie copropriété obligatoire. Expert certifié Haute-Loire (43) Lozère (48). Le Puy-en-Velay, Yssingeaux, Langogne, Auroux. Devis GRATUIT ☎ 04 71 08 27 09",
    metaKeywords: "loi carrez haute-loire, mesurage carrez 43, superficie copropriété, loi carrez le puy-en-velay, carrez yssingeaux, carrez langogne, carrez auroux"
  },
  {
    id: "erp",
    slug: "etat-risques-pollutions-erp",
    name: "ERP",
    fullName: "État des Risques et Pollutions",
    icon: AlertTriangle,
    description: "L'état des risques et pollutions (ERP) informe les acquéreurs et locataires sur les risques naturels (inondations, séismes), miniers, technologiques et de pollution des sols auxquels le bien est exposé.",
    longDescription: `L'ERP (anciennement ERNMT) est obligatoire pour toute vente ou location d'un bien situé dans une zone couverte par un plan de prévention des risques.

Risques concernés :
• Risques naturels : inondations, mouvements de terrain, séismes, avalanches, feux de forêt
• Risques miniers : effondrements, affaissements
• Risques technologiques : industriels, nucléaires
• Pollution des sols (SIS)
• Potentiel radon

L'ERP doit être établi moins de 6 mois avant la date de la transaction.

CPPB vous accompagne pour vos ERP en Haute-Loire (43) et Lozère (48) : Le Puy-en-Velay, Yssingeaux, Langogne, Auroux.`,
    validity: "6 mois",
    required: "Vente & Location",
    metaTitle: "ERP État Risques Pollutions Haute-Loire 43 | Le Puy-en-Velay Yssingeaux Langogne | CPPB",
    metaDescription: "⭐ ERP état des risques naturels, miniers, technologiques, radon obligatoire. Expert Haute-Loire (43) Lozère (48). Le Puy-en-Velay, Yssingeaux, Langogne, Auroux. Devis GRATUIT ☎ 04 71 08 27 09",
    metaKeywords: "ERP haute-loire, état risques pollutions 43, ERNMT, risques naturels, radon haute-loire, ERP le puy-en-velay, ERP yssingeaux, ERP langogne, ERP auroux"
  },
  {
    id: "assainissement",
    slug: "diagnostic-assainissement",
    name: "Assainissement",
    fullName: "Diagnostic Assainissement",
    icon: FileCheck,
    description: "Lorsqu'un logement n'est pas raccordé au réseau public de collecte des eaux usées, il doit être équipé d'une installation d'assainissement non collectif autonome. Cette installation doit faire l'objet d'un contrôle.",
    longDescription: `Le diagnostic assainissement non collectif est obligatoire pour la vente d'un bien non raccordé au tout-à-l'égout.

Points de contrôle :
• Existence de l'installation
• Implantation et accessibilité
• Fonctionnement et entretien
• Défauts d'usure ou d'entretien
• Risques sanitaires et environnementaux

Le contrôle est réalisé par le SPANC (Service Public d'Assainissement Non Collectif) de la commune.

En cas de non-conformité, l'acquéreur dispose d'un an après la vente pour réaliser les travaux de mise aux normes.

CPPB vous accompagne dans vos démarches en Haute-Loire (43) et Lozère (48) : Le Puy-en-Velay, Yssingeaux, Langogne, Auroux.`,
    validity: "3 ans",
    required: "Vente uniquement",
    metaTitle: "Diagnostic Assainissement Haute-Loire 43 | Le Puy-en-Velay Yssingeaux Langogne | CPPB",
    metaDescription: "⭐ Diagnostic assainissement non collectif fosse septique obligatoire vente. Expert Haute-Loire (43) Lozère (48). Le Puy-en-Velay, Yssingeaux, Langogne, Auroux. Devis GRATUIT ☎ 04 71 08 27 09",
    metaKeywords: "diagnostic assainissement haute-loire, assainissement non collectif 43, fosse septique, SPANC, assainissement le puy-en-velay, assainissement yssingeaux, assainissement auroux"
  },
  {
    id: "audit-energetique",
    slug: "audit-energetique",
    name: "Audit énergétique",
    fullName: "Audit Énergétique",
    icon: FileCheck,
    description: "L'audit énergétique est une étude approfondie qui analyse la performance énergétique d'un logement et propose des scénarios de travaux de rénovation. Il est distinct du DPE et obligatoire à la vente pour certains logements.",
    longDescription: `L'audit énergétique est une étude plus approfondie que le DPE. Il analyse en détail le logement et propose des scénarios de travaux de rénovation énergétique.

À quoi sert l'audit énergétique ?
Le DPE classe le logement de A à G selon sa performance énergétique. L'audit énergétique va plus loin en proposant des solutions concrètes de rénovation avec estimation des coûts et des gains énergétiques.

Quand est-il obligatoire ?
L'audit énergétique est obligatoire à la vente pour :
• Logements classés F ou G : depuis le 1er avril 2023
• Logements classés E : à partir du 1er janvier 2025
• Logements classés D : à partir du 1er janvier 2034

Comment ça se passe ?
• Visite du logement par un professionnel certifié
• Analyse des caractéristiques thermiques du bâtiment
• Remise d'un rapport avec scénarios de travaux chiffrés

Quelle est la durée de validité d'un audit énergétique ?
Un audit énergétique est valable 5 ans à compter de sa date de réalisation.
Il reste valable en cas de revente pendant cette période, sauf si des travaux modifiant la performance énergétique du logement ont été réalisés.

CPPB réalise vos audits énergétiques en Haute-Loire (43) et Lozère (48) : Le Puy-en-Velay, Yssingeaux, Langogne, Auroux.`,
    validity: "5 ans",
    required: "Vente (F, G, E, D selon calendrier)",
    metaTitle: "Audit Énergétique Haute-Loire 43 | Le Puy-en-Velay Yssingeaux Langogne | CPPB Expert",
    metaDescription: "⭐ Audit énergétique obligatoire vente logements F G E D. Différence DPE audit. Expert certifié Haute-Loire (43) Lozère (48). Le Puy-en-Velay, Yssingeaux, Langogne, Auroux. Devis GRATUIT ☎ 04 71 08 27 09",
    metaKeywords: "audit énergétique haute-loire, audit énergétique 43, audit energetique le puy-en-velay, différence DPE audit, audit obligatoire vente, passoire thermique, audit yssingeaux, audit langogne"
  }
];

// Testimonials data
const testimonials = [
  { name: "Client Google", rating: 5, date: "Septembre 2025", text: "Je recommande ! Service au top. Très professionnel et très sympathique.", source: "Google" },
  { name: "Eva Villedieu", rating: 5, date: "Mars 2023", text: "Dommage que l'on ne puisse pas mettre 10 étoiles ! Mon notaire m'avait conseillé 2 sociétés d'expertise. Très professionnel, explications claires, rapport détaillé reçu rapidement.", source: "Google" },
  { name: "Trustpilot", rating: 5, date: "Mars 2024", text: "Entreprise conseillée par mon notaire !! Super compétent ! J'ai apprécié toutes les explications qui m'ont permis de mieux comprendre le but de ces diagnostics.", source: "Trustpilot" },
  { name: "Client Trustpilot", rating: 5, date: "Novembre 2024", text: "Très bonnes indications. Patron très aimable. Entreprise sérieuse et très réactive. Tarifs compétitifs, je recommande.", source: "Trustpilot" },
  { name: "Client vérfié", rating: 5, date: "Mars 2024", text: "CPPB est une entreprise digne de confiance. J'ai fait appel à leur service sur la vente de ma maison secondaire et je dois avouer que le travail réalisé est irréprochable.", source: "Trustpilot" },
  { name: "Tofssie T.", rating: 5, date: "Novembre 2024", text: "Voilà quelqu'un de professionnel et consciencieux, qui explique ce qu'il fait, et qui donne de bons conseils, en plus très sympathique, pas d'attente pour la remise des documents.", source: "Google" }
];

// Review link helper - makes cards clickable to Google/Trustpilot
const GOOGLE_REVIEWS_URL = "https://g.page/r/CaB7lwVbLS-zEBE/";
const TRUSTPILOT_URL = "https://fr.trustpilot.com/review/diagnostic-immobilier-haute-loire-43.fr";

function getReviewLink(source) {
  if (!source) return null;
  const s = source.toLowerCase();
  if (s.includes("google")) return GOOGLE_REVIEWS_URL;
  if (s.includes("trustpilot")) return TRUSTPILOT_URL;
  return null;
}

// Validity table data
const validityData = [
  { diagnostic: "DPE", vente: "10 ans", location: "10 ans" },
  { diagnostic: "Amiante", vente: "3 ans si positif / Illimité si négatif", location: "3 ans si positif / Illimité si négatif" },
  { diagnostic: "Plomb (CREP)", vente: "1 an si positif / Illimitée si négatif", location: "6 ans si positif / Illimitée si négatif" },
  { diagnostic: "Gaz", vente: "3 ans", location: "6 ans" },
  { diagnostic: "Électricité", vente: "3 ans", location: "6 ans" },
  { diagnostic: "Termites", vente: "6 mois", location: "-" },
  { diagnostic: "Loi Carrez", vente: "À chaque mutation ou en cas de travaux", location: "-" },
  { diagnostic: "ERP", vente: "6 mois", location: "6 mois" },
  { diagnostic: "Assainissement", vente: "3 ans", location: "-" }
];

// Points forts data
const pointsForts = [
  { icon: Eye, title: "TRANSPARENCE", description: "Devis gratuit sur simple appel téléphonique." },
  { icon: Clock3, title: "SOUPLESSE", description: "En cas d'urgence, nous réalisons vos diagnostics immobiliers en 24 à 48 heures." },
  { icon: BadgeCheck, title: "QUALITÉ", description: "L'opérateur intervenant présente toutes les garanties de compétence. Nous sommes certifiés par B.2.C." },
  { icon: MessageCircle, title: "CONSEILS", description: "À réception du rapport, nous restons à la disposition du vendeur et de l'acquéreur pour toutes questions." }
];

// Google Maps links
const googleMapsLinks = [
  { url: "https://g.page/r/CaB7lwVbLS-zEBE/", label: "Le Puy-en-Velay" },
  { url: "https://g.page/r/CRNrfVfSBrCsEBE/", label: "Langogne" },
  { url: "https://g.page/r/CXOnPHKTWT6qEBE/", label: "Rauret" }
];

// Social links
const socialLinks = [
  { icon: TwitterIcon, url: "https://x.com/Diagnostic43", label: "X (Twitter)" },
  { icon: InstagramIcon, url: "https://www.instagram.com/diagnostics_immobilier_43_48/", label: "Instagram" },
  { icon: LinkedInIcon, url: "https://fr.linkedin.com/in/diagnostics-immobilier-haute-loire-diagnostics-immobilier-haute-loire-7615831b3", label: "LinkedIn" }
];

// Theme Toggle Button
const ThemeToggle = () => {
  const { isDark, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
 className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
      data-testid="theme-toggle"
      aria-label="Basculer le thème"
    >
      <AnimatePresence mode="wait">
        {isDark ? (
          <motion.div key="sun" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
 <Sun className="w-5 h-5 text-yellow-500" />
          </motion.div>
        ) : (
          <motion.div key="moon" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
 <Moon className="w-5 h-5 text-indigo-600" />
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
};

// Header Component with DPE Dropdown
const Header = ({ scrolled, mobileMenuOpen, setMobileMenuOpen, onHelpClick }) => {
  const [dpeMenuOpen, setDpeMenuOpen] = useState(false);
  const dpeMenuTimer = useRef(null); // Timer for delayed close
  const { isDark, toggleTheme } = useTheme();
  const { playClickSound } = useUISound();

  // Handle help button click with sound feedback
  const handleHelpClick = () => {
    playClickSound();
    onHelpClick();
  };

  // DPE Menu handlers with delay to prevent flickering
  const handleDpeMenuEnter = () => {
    if (dpeMenuTimer.current) {
      clearTimeout(dpeMenuTimer.current);
      dpeMenuTimer.current = null;
    }
    setDpeMenuOpen(true);
  };

  const handleDpeMenuLeave = () => {
    dpeMenuTimer.current = setTimeout(() => {
      setDpeMenuOpen(false);
    }, 120); // 120ms delay before closing
  };

  return (
    <header 
 className={`fixed top-0 left-0 right-0 z-50 transition-all duration-150 ${
        scrolled 
          ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg shadow-indigo-100/30 dark:shadow-gray-900/50 border-b border-gray-100/50 dark:border-gray-800/50' 
          : 'bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm'
      }`}
      style={{ height: '72px', minHeight: '72px', maxHeight: '72px' }}
      data-testid="main-header"
    >
      {/* Container with CSS Grid - 3 zones */}
      <div 
 className="max-w-[1200px] mx-auto px-4 sm:px-6 h-full"
        style={{ 
          display: 'grid', 
          gridTemplateColumns: 'auto 1fr auto', 
          alignItems: 'center',
          columnGap: '18px'
        }}
      >
        {/* ZONE GAUCHE - Logo + Titre */}
 <Link to="/" className="flex items-center gap-2 group min-w-0" data-testid="logo-link">
 <div className="w-10 h-10 flex-shrink-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-400 rounded-xl flex items-center justify-center shadow-md shadow-indigo-200/50 dark:shadow-indigo-900/50 group-hover:shadow-indigo-300 transition-all duration-150">
 <Home className="w-5 h-5 text-white" />
          </div>
 <div className="min-w-0">
 <span className="block font-bold text-sm sm:text-base text-gray-800 dark:text-white tracking-tight leading-tight">CPPB</span>
 <span className="block text-[10px] sm:text-[11px] text-indigo-500 dark:text-indigo-400 leading-tight truncate">
 <span className="hidden sm:inline">Diagnostics Immobiliers – DPE</span>
 <span className="sm:hidden text-[10px] leading-tight" style={{whiteSpace: 'normal', maxWidth: '120px'}}>Diagnostics Immobiliers – DPE</span>
            </span>
          </div>
        </Link>

        {/* ZONE CENTRALE - Navigation (hidden on mobile) */}
 <nav className="hidden lg:flex items-center justify-center gap-[18px]" data-testid="desktop-nav">
          {/* DPE Dropdown - Wrapper keeps dropdown open on hover */}
 <div 
            className="relative" 
            onMouseEnter={handleDpeMenuEnter} 
            onMouseLeave={handleDpeMenuLeave}
          >
 <button className="flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium text-sm transition-colors py-2">
              Diagnostics
 <ChevronDown className={`w-3.5 h-3.5 duration-150 ${dpeMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Invisible bridge to connect button and dropdown */}
            {dpeMenuOpen && (
              <div 
                className="absolute top-full left-0 h-3 w-64" 
                style={{ pointerEvents: 'auto' }} 
              />
            )}
            
            <AnimatePresence>
              {dpeMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
 className="absolute top-[calc(100%+4px)] left-0 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl shadow-indigo-100/50 dark:shadow-gray-900/50 border border-gray-100 dark:border-gray-700 py-2 max-h-[calc(100vh-96px)] overflow-y-auto z-50"
                  data-testid="dpe-dropdown"
                >
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/diagnostics/${service.slug}`}
 className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-indigo-50 dark:hover:bg-gray-700 transition-colors group"
                      onClick={() => setDpeMenuOpen(false)}
                    >
 <service.icon className="w-4 h-4 text-indigo-500 dark:text-indigo-400 " />
                      <div>
 <span className="block font-medium text-gray-800 dark:text-white text-sm">{service.name}{['Termites', 'Assainissement', 'Audit énergétique'].includes(service.name) && <sup className="ml-0.5" style={{ position: 'relative', top: '-0.45em', fontSize: '0.6em', lineHeight: 0 }}>*</sup>}</span>
 <span className="block text-[11px] text-gray-500 dark:text-gray-400">{service.fullName}</span>
                      </div>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

 <Link to="/expertise" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium text-sm transition-colors">Expertise</Link>
 <Link to="/validite" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium text-sm transition-colors">Validité</Link>
 <Link to="/avis" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium text-sm transition-colors">Avis</Link>
 <Link to="/actualites-diagnostics" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium text-sm transition-colors">Actualités</Link>
 <Link to="/contact" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium text-sm transition-colors">Contact</Link>
        </nav>

        {/* ZONE DROITE - Actions - High z-index */}
 <div className="flex items-center justify-end gap-3" style={{ position: 'relative', zIndex: 10001 }}>
          {/* Desktop actions */}
 <div className="hidden lg:flex items-center gap-3">
            {/* Aide interactive button - HIGH Z-INDEX to ensure clickability */}
            <button
              type="button"
              id="help-assistant-trigger"
              onClick={(e) => { e.stopPropagation(); handleHelpClick(); }}
              style={{ position: 'relative', zIndex: 10000 }}
 className="h-10 px-4 flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 rounded-full transition-all duration-150 shadow-md shadow-indigo-200/50 dark:shadow-indigo-900/50 hover:shadow-lg hover:shadow-indigo-300/50 hover:-translate-y-0.5"
              data-testid="help-assistant-btn"
            >
 <HelpCircle className="w-4 h-4" />
              <span>Aide interactive</span>
            </button>
            
            {/* Theme toggle - circular */}
            <button
              type="button"
              onClick={toggleTheme}
 className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-all duration-150"
              aria-label="Toggle dark mode"
            >
 {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            
            {/* Nous Contacter button */}
            <Link to="/contact">
 <button className="h-10 px-4 flex items-center gap-1.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 rounded-full transition-all duration-150 shadow-md shadow-indigo-200/50 dark:shadow-indigo-900/50 hover:shadow-lg hover:shadow-indigo-300/50 hover:-translate-y-0.5" data-testid="header-cta-btn">
                Nous Contacter
 <ChevronRight className="w-4 h-4" />
              </button>
            </Link>
          </div>

          {/* Mobile actions */}
 <div className="flex lg:hidden items-center gap-2.5">
            {/* Mobile Aide button */}
            <button
              id="help-assistant-trigger-mobile"
              onClick={handleHelpClick}
 className="h-[38px] px-3 flex items-center gap-1.5 text-xs font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 active:from-indigo-600 active:via-purple-600 active:to-pink-600 rounded-full transition-all duration-150 shadow-md shadow-indigo-200/50"
              data-testid="mobile-help-btn"
            >
 <HelpCircle className="w-3.5 h-3.5" />
              <span>Aide</span>
            </button>
            
            {/* Theme toggle mobile */}
            <button
              onClick={toggleTheme}
 className="w-[38px] h-[38px] flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 transition-all duration-150"
              aria-label="Toggle dark mode"
            >
 {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            
            {/* Burger menu */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
 className="w-[38px] h-[38px] flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 transition-all duration-150"
              data-testid="mobile-menu-btn"
            >
 {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
 className="lg:hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-indigo-100 dark:border-gray-800 overflow-hidden"             data-testid="mobile-menu"
          >
 <nav className="px-4 py-6 space-y-2">
              {/* DPE Submenu in Mobile */}
 <div className="border-b border-gray-100 dark:border-gray-800 pb-4 mb-4">
 <span className="block text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-3">Diagnostics</span>
 <div className="grid grid-cols-2 gap-2">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/diagnostics/${service.slug}`}
 className="flex items-center gap-2 p-2 rounded-lg hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
 <service.icon className="w-4 h-4 text-indigo-500" />
 <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{service.name}{['Termites', 'Assainissement', 'Audit énergétique'].includes(service.name) && <sup className="ml-0.5" style={{ position: 'relative', top: '-0.45em', fontSize: '0.6em', lineHeight: 0 }}>*</sup>}</span>
                    </Link>
                  ))}
                </div>
              </div>
              
 <Link to="/expertise" className="block text-gray-700 dark:text-gray-300 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Expertise</Link>
 <Link to="/validite" className="block text-gray-700 dark:text-gray-300 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Validité</Link>
 <Link to="/avis" className="block text-gray-700 dark:text-gray-300 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Avis</Link>
 <Link to="/actualites-diagnostics" className="block text-gray-700 dark:text-gray-300 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Actualités</Link>
 <Link to="/contact" className="block text-gray-700 dark:text-gray-300 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              
 <div className="pt-4 border-t border-indigo-100 dark:border-gray-800 space-y-3">
 <SmartPhoneLink className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-semibold">
 <Phone className="w-5 h-5" />
                  04 71 08 27 09
                </SmartPhoneLink>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

/* ===== LOCKED COMPONENT — DO NOT MODIFY =====
   Hero image is frozen for layout / SEO / LCP stability
   Any change here may break layout and is forbidden
   Path: /hero.webp (stable, no hash)
============================================== */
const LockedHero = () => (
  <div className="hero-image-frame">
    <img
      src="/hero.webp"
      alt="Diagnostic Immobilier – Haute-Loire 43"
      loading="eager"
      decoding="async"
    />
  </div>
);

/* ===== LOCKED COMPONENT — DO NOT MODIFY =====
   DPE module is production validated and frozen
   Any change here may break layout and is forbidden
============================================== */
// ═══════════════════════════════════════════════════════════════════════════
// SIMULATION DPE LIVE - Outil interactif - LOCKED
// ═══════════════════════════════════════════════════════════════════════════
const DPESimulator = () => {
  const [value, setValue] = useState(280);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Couleurs réglementaires DPE
  const DPE_CLASSES = [
    { letter: 'A', min: 0, max: 70, color: '#319834' },      // Vert foncé
    { letter: 'B', min: 71, max: 110, color: '#33cc31' },    // Vert clair
    { letter: 'C', min: 111, max: 180, color: '#cbfc34' },   // Jaune-vert
    { letter: 'D', min: 181, max: 250, color: '#fbfe06' },   // Jaune
    { letter: 'E', min: 251, max: 330, color: '#fbcc05' },   // Orange clair
    { letter: 'F', min: 331, max: 420, color: '#fc9935' },   // Orange
    { letter: 'G', min: 421, max: 500, color: '#fc0205' }    // Rouge
  ];
  
  const getCurrentClass = (val) => {
    return DPE_CLASSES.find(c => val >= c.min && val <= c.max) || DPE_CLASSES[6];
  };
  
  const currentClass = getCurrentClass(value);
  
  const handleChange = (e) => {
    if (!hasInteracted) setHasInteracted(true);
    setValue(parseInt(e.target.value));
  };

  return (
 <div className="mt-4 w-full flex justify-end dpe-host" data-testid="dpe-simulator">
 <div className="dpe-wrap max-w-[320px] md:max-w-[380px]">
        {/* Titre */}
 <div className="text-right mb-2">
 <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">
            Simulation DPE Live
          </span>
        </div>
        
        {/* Barre DPE - Compact */}
 <div className="dpe-card bg-white/95 dark:bg-gray-800/95 rounded-lg p-3 shadow-md border border-gray-200/50 dark:border-gray-700/50 backdrop-blur-sm">
          {/* Classes DPE */}
 <div className="flex mb-2">
            {DPE_CLASSES.map((cls) => (
              <div
                key={cls.letter}
 className={`flex-1 h-7 flex items-center justify-center text-xs font-bold text-white transition-all duration-200 first:rounded-l-lg last:rounded-r-lg ${
                  currentClass.letter === cls.letter ? 'scale-105 z-10 shadow-md' : 'opacity-70'
                }`}
                style={{ backgroundColor: cls.color }}
              >
                {cls.letter}
              </div>
            ))}
          </div>
          
          {/* Valeur actuelle */}
 <div className="text-center mb-2">
            <span 
 className="text-xl font-bold"
              style={{ color: currentClass.color }}
            >
              {value}
            </span>
 <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">kWh/m²/an</span>
            <span 
 className="ml-2 px-2 py-0.5 rounded text-white text-sm font-bold"
              style={{ backgroundColor: currentClass.color }}
            >
              Classe {currentClass.letter}
            </span>
          </div>
          
          {/* Curseur - dans le card */}
 <div className="relative h-5 flex items-center mt-2">
            <input
              type="range"
              min="0"
              max="500"
              value={value}
              onChange={handleChange}
 className="block m-0 p-0 w-full h-3 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #319834, #33cc31, #cbfc34, #fbfe06, #fbcc05, #fc9935, #fc0205)`,
              }}
              data-testid="dpe-slider"
            />
          </div>
          
          {/* Mentions légales - dans le card */}
          <p 
 className="mt-1 text-gray-400 dark:text-gray-500 text-right"
            style={{ fontSize: '6px', lineHeight: '1.2', margin: 0 }}
          >
            Simulation indicative
          </p>
        </div>
      </div>
    </div>
  );
};

// Hero Section - LCP OPTIMIZED - H1 renders immediately, NO JS blocking
const HeroSection = ({ helpAssistantOpen, onCloseHelp }) => {
  // NO useEffect, NO useState for first paint - pure render
  return (
 <section id="accueil" className="pt-[72px] min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 flex items-center overflow-hidden relative" data-testid="hero-section" style={{ contain: 'layout style' }}>
      {/* Decorative bubbles - simplified */}
 <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
 <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-indigo-200/40 to-purple-200/40 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-full blur-3xl"></div>
 <div className="absolute top-40 right-20 w-96 h-96 bg-gradient-to-br from-purple-200/30 to-pink-200/30 dark:from-purple-900/20 dark:to-pink-900/20 rounded-full blur-3xl"></div>
      </div>
      
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 w-full">
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start relative">
          
          {/* LEFT CONTENT - H1 RENDERS IMMEDIATELY (NO ANIMATION WRAPPER) */}
 <div>
            {/* Badge */}
 <div className="flex justify-end mb-4">
 <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/50 px-4 py-1 rounded-full border border-indigo-100 dark:border-indigo-800">
                Expert immobilier
              </span>
            </div>
            
            {/* H1 - LCP ELEMENT - NO ANIMATION, NO DELAY, VISIBLE IMMEDIATELY */}
 <h1 className="font-bold text-4xl sm:text-5xl lg:text-5xl text-gray-800 dark:text-white tracking-tight leading-tight mb-6">
              DIAGNOSTICS IMMOBILIERS<br />
 <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">HAUTE-LOIRE - LOZÈRE</span>
            </h1>
            
 <p className="text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-8">
              Le Puy-en-Velay - Langogne
            </p>
            
 <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-8 max-w-lg">
              Notre entreprise est spécialisée en diagnostics immobilier DPE obligatoires lors de la vente ou de la location d'un bien immobilier.
            </p>

 <div className="flex flex-wrap gap-4 mb-8">
              <PhoneButton className="h-14 px-8 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-white font-bold text-lg shadow-xl shadow-indigo-200 dark:shadow-indigo-900 hover:shadow-indigo-300 transition-all" trackingLabel="hero_call" testId="hero-call-btn">
 <Phone className="w-5 h-5 mr-2" />
                  04 71 08 27 09
              </PhoneButton>
              <Link to="/contact">
 <Button variant="outline" className="h-14 px-8 rounded-full border-2 border-indigo-200 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 font-bold text-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/50 hover:border-indigo-300 transition-all" data-testid="hero-contact-btn">
 <Mail className="w-5 h-5 mr-2" />
                  Nous Contacter
                </Button>
              </Link>
            </div>

            {/* Social Links */}
 <div className="flex items-center gap-4">
 <span className="text-indigo-500 dark:text-indigo-400 text-sm font-medium">Suivez-nous :</span>
              {socialLinks.map((social, index) => (
 <a key={index} href={social.url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/80 dark:bg-gray-800 hover:bg-white dark:hover:bg-gray-700 rounded-full flex items-center justify-center text-indigo-500 hover:text-purple-600 dark:text-indigo-400 dark:hover:text-purple-400 shadow-md hover:shadow-lg transition-all border border-indigo-100 dark:border-gray-700" aria-label={social.label}>
                  <social.icon />
                </a>
              ))}
            </div>
          </div>

          {/* ===== LOCKED HERO — DO NOT MODIFY ===== */}
          <div>
            <LockedHero />
              
            {/* ===== LOCKED DPE — DO NOT MODIFY ===== */}
            <DPESimulator />
              
            {/* Badge T. GIBERT */}
            <div className="mt-4 w-full max-w-[95%] md:max-w-[75%]" style={{ minHeight: '70px' }}>
              <div 
                className="badge-gibert flex flex-col px-3 md:px-5 py-2.5 md:py-3 text-sm font-semibold text-white rounded-2xl"
                style={{
                  background: 'linear-gradient(90deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
                  overflow: 'hidden',
                  boxShadow: 'none',
                  border: 'none',
                  position: 'relative',
                  minHeight: '65px'
                }}
              >
                <div className="flex items-center gap-2 md:gap-3">
                  <div 
                    className="flex-shrink-0 w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.2)' }}
                  >
                    <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-white" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="font-bold text-white text-xs md:text-sm leading-tight">T. GIBERT</span>
                    <span className="leading-tight" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '10px' }}>Diagnostiqueur Certifié</span>
                  </div>
                </div>
                <div className="flex flex-col items-end text-right mt-1.5">
                  <span className="leading-tight" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '10px' }}>+ de 15 ans d'expérience</span>
                  <span className="leading-tight" style={{ color: 'rgba(255,255,255,0.8)', fontSize: '10px' }}>Assurance professionnelle</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Expertise Section
const ExpertiseSection = () => {
  return (
 <section id="expertise" className="py-20 md:py-32 bg-white dark:bg-gray-900" data-testid="expertise-section" style={{ contain: 'layout style' }}>
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-16">
 <span className="text-indigo-600 dark:text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">Notre Expertise</span>
 <h2 className="font-bold text-3xl sm:text-4xl lg:text-5xl text-gray-800 dark:text-white tracking-tight mb-4">
            DIAGNOSTICS IMMOBILIERS AVANT VENTE & LOCATION
          </h2>
 <div className="w-20 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 mx-auto rounded-full"></div>
        </motion.div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInLeft} className="space-y-6">
            {[
              "Notre entreprise est spécialisée en <strong>diagnostics immobiliers obligatoires</strong> lors de la vente ou de la location d'un bien immobilier.",
              "Nous réalisons tous les diagnostics relatifs au <strong>Dossier de diagnostics techniques</strong>.",
              "Nous intervenons sur les départements de la <strong>Haute-Loire et de la Lozère (43 - 48)</strong>.",
              "Nos prestations comprennent les diagnostics immobiliers <strong>amiante, plomb, gaz, électricité, performance énergétique, assainissement</strong>.",
              "Tous nos diagnostics immobiliers sont réalisés par des <strong>professionnels certifiés par B.2.C</strong>."
            ].map((text, i) => (
 <div key={i} className="flex items-start gap-4">
 <div className={`w-3 h-3 bg-gradient-to-br ${i % 2 === 0 ? 'from-indigo-500 to-purple-500' : 'from-purple-500 to-pink-500'} rounded-full mt-2 flex-shrink-0`}></div>
 <p className="text-gray-700 dark:text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: text }} />
              </div>
            ))}
          </motion.div>

 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInRight} className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl p-8 text-white shadow-2xl shadow-indigo-200 dark:shadow-indigo-900">
 <div className="flex items-center gap-3 mb-6">
 <FileText className="w-8 h-8 text-white/90" />
 <h3 className="font-bold text-xl">Délais & Tarifs</h3>
            </div>
 <div className="space-y-4">
 <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
 <p className="text-white/80 text-sm mb-1">Délai de remise des rapports</p>
 <p className="font-bold text-3xl text-white">48/72 heures</p>
 <p className="text-white/70 text-sm">en moyenne</p>
              </div>
 <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
 <p className="text-white/80 text-sm mb-1">Tarifs</p>
 <p className="font-bold text-lg text-white">Communiqués sur simple appel téléphonique</p>
              </div>
 <SmartPhoneLink className="block">
 <Button className="w-full h-12 rounded-full bg-white text-indigo-600 hover:bg-indigo-50 font-bold mt-4 shadow-lg">
 <Phone className="w-5 h-5 mr-2" />
                  04 71 08 27 09
                </Button>
              </SmartPhoneLink>
            </div>
          </motion.div>
        </div>

        {/* Points Forts */}
 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="mb-20">
 <h3 className="font-bold text-2xl text-gray-800 dark:text-white text-center mb-12">
            POINTS FORTS
 <span className="block text-base font-normal text-gray-600 dark:text-gray-400 mt-2">Pour une prestation de qualité, notre cabinet vous offre les garanties suivantes</span>
          </h3>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pointsForts.map((point, index) => (
 <motion.div key={index} variants={scaleIn} className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-3xl p-6 border border-indigo-100 dark:border-gray-700 hover:shadow-xl hover:shadow-indigo-100 dark:hover:shadow-gray-900 transition-all group">
 <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-indigo-200 dark:shadow-indigo-900">
 <point.icon className="w-7 h-7 text-white" />
                </div>
 <h4 className="font-bold text-gray-800 dark:text-white mb-2">{point.title}</h4>
 <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{point.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Informations Complémentaires */}
 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 rounded-3xl p-8 md:p-12 border border-indigo-100 dark:border-gray-700">
 <h3 className="font-bold text-2xl text-gray-800 dark:text-white mb-8">Informations complémentaires</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: Lock, color: "text-indigo-600 dark:text-indigo-400", text: "Notre cabinet dispose d'une <strong>assurance en responsabilité civile professionnelle</strong>." },
              { icon: Scale, color: "text-purple-600 dark:text-purple-400", text: "Notre entreprise n'a <strong>aucun lien de nature à porter atteinte à son impartialité</strong>." },
              { icon: FileCheck, color: "text-indigo-600 dark:text-indigo-400", text: "L'ensemble des rapports répond aux <strong>différents textes législatifs</strong>." },
              { icon: Shield, color: "text-purple-600 dark:text-purple-400", text: "Nous garantissons la <strong>confidentialité absolue</strong> sur tous les dossiers." }
            ].map((item, i) => (
 <div key={i} className="flex items-start gap-4">
 <item.icon className={`w-6 h-6 ${item.color} flex-shrink-0 mt-1`} />
 <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: item.text }} />
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const navigate = useNavigate();
  
  // Firefox-proof navigation with #top anchor
  const goToService = (slug) => (e) => {
    e.preventDefault();
    navigate(`/diagnostics/${slug}#top`);
    // Force scroll top as backup
    requestAnimationFrame(() => window.scrollTo(0, 0));
    setTimeout(() => window.scrollTo(0, 0), 0);
  };
  
  return (
 <section id="diagnostics" className="py-20 md:py-32 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800" data-testid="services-section">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-16">
 <span className="text-indigo-200 font-semibold text-sm tracking-widest uppercase mb-4 block">LES DIAGNOSTICS</span>
 <h2 className="font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4">
            Diagnostics Immobiliers Obligatoires – Vente et Location
          </h2>
 <p className="text-lg text-indigo-100/80 max-w-2xl mx-auto">
            DPE, amiante, plomb, gaz, électricité : tous les diagnostics réglementaires réalisés par un expert certifié B.2.C en Haute-Loire (43) et Lozère (48).
          </p>
 <div className="w-20 h-1 bg-white/50 mx-auto mt-6 rounded-full"></div>
        </motion.div>

 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <motion.div key={service.id} variants={scaleIn}>
 <a 
                href={`/diagnostics/${service.slug}#top`}
                onClick={goToService(service.slug)}
                className="block bg-white/10 backdrop-blur-sm p-8 rounded-3xl border border-white/20 hover:bg-white/20 transition-all group h-full cursor-pointer" 
                data-testid={`service-card-${service.id}`}
              >
 <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-white/30 transition-all">
 <service.icon className="w-7 h-7 text-white" />
                </div>
 <h3 className="font-bold text-xl text-white mb-2">{service.name}{['Termites', 'Assainissement', 'Audit énergétique'].includes(service.name) && <sup className="ml-0.5 text-[9px]">*</sup>}</h3>
 <p className="text-sm text-indigo-200 font-medium mb-3">{service.fullName}</p>
 <p className="text-white/70 text-sm leading-relaxed mb-4 line-clamp-3">{service.description}</p>
                {['termites', 'audit énergétique', 'assainissement'].some((label) => service.name?.toLowerCase().includes(label)) && (
                  <p className="text-[9px] leading-tight text-white/45 italic -mt-2 mb-3">
                    {service.name?.toLowerCase().includes('assainissement')
                      ? '*si concerné, réalisé par le SPANC'
                      : '*si concerné, réalisé par entreprise partenaire'}
                  </p>
                )}
 <div className="flex items-center justify-between text-xs text-indigo-200/80 pt-4 border-t border-white/20">
 <span><Clock className="w-3 h-3 inline mr-1" /> {service.validity}</span>
 <span className="bg-white/20 px-2 py-1 rounded-full">{service.required}</span>
                </div>
 <div className="mt-4 flex items-center text-white/80 text-sm group-hover:text-white transition-colors">
 En savoir plus <ChevronRight className="w-4 h-4 ml-1 " />
                </div>
              </a>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Validity Table Section
const ValiditySection = () => {
  return (
 <section id="validite" className="py-20 md:py-32 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800" data-testid="validity-section" style={{ minHeight: '500px', contain: 'layout style' }}>
 <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-12">
 <span className="text-indigo-600 dark:text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">Durée de Validité</span>
 <h2 className="font-bold text-3xl sm:text-4xl text-gray-800 dark:text-white tracking-tight mb-4">
            Durée de Validité des Diagnostics – Vente et Location
          </h2>
 <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            La durée de validité varie selon le type de diagnostic et la nature de la transaction. Un diagnostic périmé doit être renouvelé avant la signature de l'acte.
          </p>
 <div className="w-20 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 mx-auto mt-6 rounded-full"></div>
        </motion.div>

 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="rounded-3xl border border-indigo-100 dark:border-gray-700 overflow-hidden shadow-xl shadow-indigo-100/50 dark:shadow-gray-900 bg-white dark:bg-gray-800" data-testid="validity-table">
   {/* Mobile: table can become too narrow on iPhones. Keep layout stable by allowing horizontal scroll. */}
   <div className="w-full overflow-x-auto">
     <table className="min-w-[560px] w-full">
            <thead>
 <tr className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white">
 <th className="py-4 sm:py-5 px-4 sm:px-6 text-left font-bold whitespace-nowrap">Diagnostic</th>
 <th className="py-4 sm:py-5 px-4 sm:px-6 text-left font-bold whitespace-nowrap">Vente</th>
 <th className="py-4 sm:py-5 px-4 sm:px-6 text-left font-bold whitespace-nowrap">Location</th>
              </tr>
            </thead>
            <tbody>
              {validityData.map((row, index) => (
 <tr key={index} className="border-b border-indigo-50 dark:border-gray-700 hover:bg-indigo-50/50 dark:hover:bg-gray-700/50 transition-colors">
 <td className="py-4 px-4 sm:px-6 font-semibold text-gray-800 dark:text-white whitespace-nowrap">{row.diagnostic}</td>
 <td className="py-4 px-4 sm:px-6 text-gray-600 dark:text-gray-400 text-xs sm:text-sm break-words">{row.vente}</td>
 <td className="py-4 px-4 sm:px-6 text-gray-600 dark:text-gray-400 text-xs sm:text-sm break-words">{row.location}</td>
                </tr>
              ))}
            </tbody>
          </table>
   </div>
        </motion.div>

 <p className="text-center text-sm text-gray-500 dark:text-gray-500 mt-6">
 <AlertTriangle className="w-4 h-4 inline mr-1" />
          Les durées peuvent varier selon les situations. Contactez-nous pour plus d'informations.
        </p>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
 <section id="avis" className="py-20 md:py-32 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800" data-testid="testimonials-section" style={{ minHeight: '450px', contain: 'layout style' }}>
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-12">
 <span className="text-indigo-200 font-semibold text-sm tracking-widest uppercase mb-4 block">Avis Clients</span>
 <h2 className="font-bold text-3xl sm:text-4xl text-white tracking-tight mb-4">
            44 Avis Clients – Note 5/5 sur Google
          </h2>
 <p className="text-indigo-100/80 max-w-xl mx-auto mb-6">
            La satisfaction de nos clients témoigne de notre engagement pour un service professionnel et réactif.
          </p>
          
          {/* Compteurs animés */}
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 max-w-3xl mx-auto">
 <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
 <div className="text-3xl md:text-4xl font-bold text-white">
                <AnimatedCounter end={44} duration={2} suffix="" />
              </div>
 <div className="text-indigo-200 text-sm">Avis Google</div>
            </div>
 <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
 <div className="text-3xl md:text-4xl font-bold text-white">
                <AnimatedCounter end={5} duration={1.5} suffix="/5" />
              </div>
 <div className="text-indigo-200 text-sm">Note moyenne</div>
            </div>
 <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
 <div className="text-3xl md:text-4xl font-bold text-white">
                <AnimatedCounter end={10} duration={2} suffix="+" />
              </div>
 <div className="text-indigo-200 text-sm">Ans d'expérience</div>
            </div>
 <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
 <div className="text-3xl md:text-4xl font-bold text-white">
                <AnimatedCounter end={2500} duration={2.5} suffix="+" />
              </div>
 <div className="text-indigo-200 text-sm">Diagnostics réalisés</div>
            </div>
          </div>
          
 <div className="flex flex-wrap items-center justify-center gap-8 mb-6">
 <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
 <div className="flex">
 {[...Array(5)].map((_, i) => (<Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />))}
              </div>
 <span className="text-white font-bold">5.0/5</span>
 <a href="https://g.page/r/CaB7lwVbLS-zEBE/" target="_blank" rel="noopener noreferrer" className="reviews-link text-indigo-200 text-sm">(44 avis Google)</a>
            </div>
 <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
 <div className="flex">
 {[...Array(5)].map((_, i) => (<Star key={i} className={`w-5 h-5 ${i < 4 ? 'fill-amber-400 text-amber-400' : 'fill-amber-400/50 text-amber-400/50'}`} />))}
              </div>
 <span className="text-white font-bold">4.6/5</span>
 <a href="https://fr.trustpilot.com/review/diagnostic-immobilier-haute-loire-43.fr" target="_blank" rel="noopener noreferrer" className="reviews-link text-indigo-200 text-sm">(19 avis Trustpilot)</a>
            </div>
          </div>
          
 <div className="w-20 h-1 bg-white/50 mx-auto rounded-full"></div>
        </motion.div>

 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => {
            const reviewLink = getReviewLink(testimonial.source);
            const CardContent = (
              <div className="review-card-inner">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (<Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />))}
                </div>
                <p className="text-white/90 text-sm leading-relaxed mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold text-white text-sm">{testimonial.name}</p>
                    <p className="text-xs text-indigo-200">{testimonial.date}</p>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full ${testimonial.source === 'Google' ? 'bg-blue-500/30 text-blue-200' : 'bg-green-500/30 text-green-200'}`}>
                    {testimonial.source}
                  </span>
                </div>
              </div>
            );
            
            return reviewLink ? (
              <motion.a 
                key={index} 
                href={reviewLink}
                target="_blank"
                rel="nofollow noopener noreferrer"
                variants={scaleIn} 
                className="review-card-link bg-white/10 backdrop-blur-sm p-6 rounded-3xl border-l-4 border-white/50" 
                data-testid={`testimonial-card-${index}`}
              >
                {CardContent}
              </motion.a>
            ) : (
              <motion.div key={index} variants={scaleIn} className="bg-white/10 backdrop-blur-sm p-6 rounded-3xl border-l-4 border-white/50" data-testid={`testimonial-card-${index}`}>
                {CardContent}
              </motion.div>
            );
          })}
        </motion.div>

 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="mt-12 text-center">
 <p className="text-indigo-200 mb-4">Retrouvez-nous sur Google Maps :</p>
 <div className="flex flex-wrap justify-center gap-4">
            {googleMapsLinks.map((link, index) => (
 <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-all border border-white/30">
 <MapPin className="w-4 h-4" />
 <span className="text-sm font-medium">{link.label}</span>
 <ExternalLink className="w-4 h-4" />
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Actualités Diagnostics Section
const ActualitesSection = ({ isArchivePage = false, maxItems = null }) => {
  // État initial avec 10 actualités statiques pour éviter le layout shift
  const staticNews = [
    { id: 'dpe-2025-interdiction-g', category: 'DPE', date: '2025-01-01', title: 'DPE 2025 : interdiction de location des logements classés G', summary: 'Depuis le 1er janvier 2025, les logements classés G au DPE sont interdits à la location.', source: 'Service-Public.fr', url: 'https://www.service-public.gouv.fr/particuliers/vosdroits/F16096' },
    { id: 'audit-energetique-vente-2024', category: 'Audit énergétique', date: '2024-04-01', title: 'Audit énergétique obligatoire pour certaines ventes immobilières', summary: "L'audit énergétique est désormais obligatoire pour la vente de logements classés F ou G.", source: 'Ministère Écologie', url: 'https://www.ecologie.gouv.fr/audit-energetique' },
    { id: 'amiante-obligations-vente', category: 'Amiante', date: '2024-03-15', title: 'Diagnostic amiante : quelles obligations en cas de vente', summary: 'Le diagnostic amiante reste obligatoire pour tout bien construit avant juillet 1997.', source: 'Service-Public.fr', url: 'https://www.service-public.fr/particuliers/vosdroits/F11155' },
    { id: 'plomb-logements-anciens', category: 'Plomb', date: '2024-02-10', title: 'Diagnostic plomb : obligation pour les logements anciens', summary: 'Le CREP est obligatoire pour tous les logements construits avant le 1er janvier 1949.', source: 'Service-Public.fr', url: 'https://www.service-public.fr/particuliers/vosdroits/F11156' },
    { id: 'diagnostic-electricite', category: 'Électricité', date: '2024-01-20', title: 'Diagnostic électricité : quand est-il obligatoire', summary: 'Les installations électriques de plus de 15 ans doivent être diagnostiquées avant vente.', source: 'Service-Public.fr', url: 'https://www.service-public.fr/particuliers/vosdroits/F17317' },
    { id: 'diagnostic-gaz', category: 'Gaz', date: '2024-01-15', title: 'Diagnostic gaz : sécurité des installations anciennes', summary: 'Les installations gaz de plus de 15 ans nécessitent un diagnostic obligatoire.', source: 'Service-Public.fr', url: 'https://www.service-public.fr/particuliers/vosdroits/F17318' },
    { id: 'erp-risques-naturels', category: 'ERP', date: '2024-01-05', title: 'ERP : état des risques et pollutions obligatoire', summary: "L'ERP informe sur les risques naturels, miniers, technologiques et radon du bien.", source: 'Service-Public.fr', url: 'https://www.service-public.fr/particuliers/vosdroits/F17307' },
    { id: 'loi-carrez-mesurage', category: 'Loi Carrez', date: '2024-01-02', title: 'Mesurage Loi Carrez : les règles en copropriété', summary: 'Le certificat Loi Carrez est obligatoire pour la vente de lots en copropriété.', source: 'Notaires.fr', url: 'https://www.notaires.fr/fr/immobilier-fiscalite/gestion-dun-bien-immobilier/superficie-carrez' },
    { id: 'renovation-energetique-aides', category: 'Rénovation', date: '2024-12-15', title: "MaPrimeRénov' : évolution des aides à la rénovation", summary: "Les aides MaPrimeRénov' évoluent pour encourager la rénovation globale des logements.", source: 'Économie.gouv.fr', url: 'https://www.economie.gouv.fr/particuliers/prime-renovation-energetique' },
    { id: 'interdiction-passoires-2028', category: 'DPE', date: '2024-11-20', title: '2028 : les logements classés F interdits à la location', summary: 'Après les G en 2025, les logements F seront interdits à la location dès 2028.', source: 'Legifrance', url: 'https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000043956924' }
  ];

  const [news, setNews] = useState(staticNews);
  const [showAll, setShowAll] = useState(isArchivePage);
  const [loadError, setLoadError] = useState(false);
  
  const HOMEPAGE_COUNT = 10;
  const ARCHIVE_COUNT = 40;
  const MAX_NEWS = 50;

  // Validate URL helper
  const isValidUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Check if URL is external
  const isExternalUrl = (url) => {
    return url && url.startsWith('http');
  };

  // Charger les actualités depuis /news.json
  useEffect(() => {
    const loadNews = async () => {
      try {
        const response = await fetch('/news.json', { cache: 'no-store' });
        
        if (!response.ok) {
          console.warn('news.json not found, using static fallback');
          return;
        }
        
        const data = await response.json();
        
        if (!Array.isArray(data) || data.length === 0) {
          console.warn('news.json empty or invalid, using static fallback');
          return;
        }
        
        // Filter news with valid title, date, and URL
        const validNews = data
          .filter(item => item && item.title && item.date && isValidUrl(item.url))
          .slice(0, MAX_NEWS);
        
        if (validNews.length === 0) {
          console.warn('No valid news in news.json, using static fallback');
          return;
        }
        
        // Sort by date DESC
        const sortedNews = validNews.sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });
        
        setNews(sortedNews);
      } catch (error) {
        console.warn('Failed to load news.json:', error.message);
        setLoadError(true);
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => loadNews(), { timeout: 2000 });
    } else {
      setTimeout(loadNews, 100);
    }
  }, []);

  // Determine how many to show
  const effectiveMax = maxItems || (isArchivePage ? ARCHIVE_COUNT : HOMEPAGE_COUNT);
  const displayedNews = showAll ? news.slice(0, effectiveMax) : news.slice(0, HOMEPAGE_COUNT);
  const hasMore = !isArchivePage && news.length > HOMEPAGE_COUNT;

  // Summary fallback
  const getSummary = (item) => {
    return item.summary || item.description || item.excerpt || "Voir l'actualité complète sur le site officiel.";
  };

  return (
 <section id="actualites" className="py-20 md:py-32 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800" data-testid="actualites-section" style={{ minHeight: '800px', contain: 'layout style' }}>
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-12">
 <span className="text-indigo-600 dark:text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">Actualités</span>
 <h2 className="font-bold text-3xl sm:text-4xl text-gray-800 dark:text-white tracking-tight mb-4">
            Actualités Diagnostics Immobiliers
          </h2>
 <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Restez informé des dernières évolutions réglementaires et des nouveautés du secteur
          </p>
 <div className="w-20 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 mx-auto mt-6 rounded-full"></div>
        </motion.div>

        {/* Grille d'actualités - Rendu synchrone, pas de loading state pour éviter CLS */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ minHeight: '700px' }}>
          {displayedNews.map((item, index) => (
            <a
              key={item.id || index}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
 className="news-card bg-white dark:bg-gray-800 rounded-3xl p-6 border border-indigo-100 dark:border-gray-700 hover:shadow-xl hover:shadow-indigo-100/50 dark:hover:shadow-gray-900 transition-all group"
              data-testid={`news-card-${item.id || index}`}
              style={{ minHeight: '140px' }}
            >
 <div className="flex items-start gap-4">
 <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ width: '48px', height: '48px', minWidth: '48px', minHeight: '48px' }}>
 <Newspaper className="w-6 h-6 text-white" />
                </div>
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2 mb-2 flex-wrap">
 <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/50 px-2 py-1 rounded-full">{item.category}</span>
 <span className="text-xs text-gray-500 dark:text-gray-500">{item.date}</span>
                  </div>
 <h3 className="font-bold text-gray-800 dark:text-white mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors line-clamp-2">{item.title}</h3>
                  {/* Résumé - Rendu synchrone avec fallback (Firefox fix) */}
 <p className="news-summary text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-2" data-testid="news-summary" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', minHeight: '2.5em', maxHeight: '3.2em', lineHeight: '1.6' }}>
                    {getSummary(item)}
                  </p>
 <div className="flex items-center text-sm text-indigo-600 dark:text-indigo-400">
 <span className="font-medium">{item.source}</span>
 <ExternalLink className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
        
        {hasMore && (
 <div className="mt-8 text-center">
            <button
              onClick={() => setShowAll(!showAll)}
 className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300"
              data-testid="news-voir-plus-btn"
            >
              {showAll ? (
                <>
 <ChevronUp className="w-5 h-5" />
                  Voir moins
                </>
              ) : (
                <>
 <ChevronDown className="w-5 h-5" />
                  Voir plus ({news.length - HOMEPAGE_COUNT} autres actualités)
                </>
              )}
            </button>
          </div>
        )}

        {/* Lien vers archive sur homepage */}
        {!isArchivePage && (
 <div className="mt-8 text-center">
            <Link 
              to="/actualites-diagnostics" 
              className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            >
              Toutes les actualités
 <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        )}

 <div className="mt-12 text-center">
 <p className="text-gray-600 dark:text-gray-400 text-sm">
            Sources : Service-Public.fr, Économie.gouv.fr, Legifrance, Écologie.gouv.fr, Notaires.fr, ANIL, Diagnostiqueur-immobilier.fr
          </p>
        </div>
      </div>
    </section>
  );
};

// Google Maps Section
const GoogleMapsSection = () => {
  const mapRef = useRef(null);
  const sectionRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);

  // Lazy loading : charger Google Maps seulement quand la section est visible
  useEffect(() => {
    const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    // Vérifier si la clé API existe et n'est pas "undefined"
    if (!googleMapsApiKey || googleMapsApiKey === 'undefined' || googleMapsApiKey.trim() === '') {
      // Pas de clé API : ne pas charger Google Maps
      setHasApiKey(false);
      return;
    }
    
    setHasApiKey(true);

    // IntersectionObserver pour lazy loading
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px', threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Charger le script Google Maps seulement quand visible ET clé présente
  useEffect(() => {
    if (!isVisible || !hasApiKey) return;
    
    const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    if (!window.google && !document.getElementById('google-maps-script')) {
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&loading=async&callback=initMap`;
      script.async = true;
      script.defer = true;
      
      window.initMap = () => {
        setMapLoaded(true);
      };
      
      document.head.appendChild(script);
    } else if (window.google) {
      setMapLoaded(true);
    }
  }, [isVisible, hasApiKey]);

  useEffect(() => {
    if (mapLoaded && mapRef.current && window.google) {
      // Locations for markers
      const locations = [
        { lat: 44.9167, lng: 3.7500, title: "Diagnostic Immobilier Haute-Loire – Rauret" },
        { lat: 45.0428, lng: 3.8853, title: "Diagnostic Immobilier Haute-Loire – Le Puy-en-Velay" },
        { lat: 44.7333, lng: 3.8500, title: "Diagnostic Immobilier Haute-Loire – Langogne" }
      ];

      // Calculate center and bounds
      const bounds = new window.google.maps.LatLngBounds();
      locations.forEach(loc => bounds.extend({ lat: loc.lat, lng: loc.lng }));

      // Create map
      const map = new window.google.maps.Map(mapRef.current, {
        center: bounds.getCenter(),
        zoom: 10,
        styles: [
          { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }
        ],
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      });

      // Fit bounds to show all markers
      map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });

      // Add markers (using legacy Marker - AdvancedMarkerElement requires mapId)
      locations.forEach(location => {
        new window.google.maps.Marker({
          position: { lat: location.lat, lng: location.lng },
          map: map,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#6366f1",
            fillOpacity: 1,
            strokeColor: "#4f46e5",
            strokeWeight: 3
          }
        });
      });
    }
  }, [mapLoaded]);

  return (
 <section ref={sectionRef} id="localisation" className="py-20 md:py-32 bg-white dark:bg-gray-900" data-testid="maps-section" style={{ minHeight: '500px', contain: 'layout style' }}>
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-12">
 <span className="text-indigo-600 dark:text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">Zone d&apos;intervention</span>
 <h2 className="font-bold text-3xl sm:text-4xl text-gray-800 dark:text-white tracking-tight mb-4">
            Diagnostiqueur Immobilier – Haute-Loire et Lozère
          </h2>
 <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Nous intervenons dans les départements 43 et 48 pour tous vos diagnostics immobiliers obligatoires : Le Puy-en-Velay, Langogne, Yssingeaux et communes environnantes.
          </p>
 <div className="w-20 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 mx-auto mt-6 rounded-full"></div>
        </motion.div>

 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Map with markers - ou fallback si pas de clé API */}
 <div className="rounded-3xl overflow-hidden shadow-xl shadow-indigo-100/50 dark:shadow-gray-900 border border-gray-100 dark:border-gray-700 h-[400px] bg-gray-100 dark:bg-gray-800">
            {hasApiKey ? (
 <div ref={mapRef} className="w-full h-full" />
            ) : (
              /* Fallback : carte statique si pas de clé API */
 <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 p-6">
 <MapPin className="w-16 h-16 text-indigo-500 mb-4" />
 <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Zone d'intervention</h3>
 <p className="text-gray-600 dark:text-gray-400 text-center">Haute-Loire (43) et Lozère (48)</p>
 <p className="text-gray-500 dark:text-gray-500 text-sm mt-2 text-center">Le Puy-en-Velay • Langogne • Yssingeaux</p>
              </div>
            )}
          </div>

          {/* Info Cards */}
 <div className="space-y-6">
 <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-3xl p-6 border border-indigo-100 dark:border-gray-700">
 <h3 className="font-bold text-xl text-gray-800 dark:text-white mb-4 flex items-center gap-2">
 <MapPin className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                Haute-Loire (43)
              </h3>
 <div className="flex flex-wrap gap-2">
                {['Le Puy-en-Velay', 'Yssingeaux', 'Monistrol-sur-Loire', 'Langeac', 'Cayres', 'Pradelles', 'Saugues'].map((city) => (
 <span key={city} className="px-3 py-1 bg-white dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300 border border-indigo-100 dark:border-gray-600">{city}</span>
                ))}
              </div>
            </div>

 <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-800 rounded-3xl p-6 border border-purple-100 dark:border-gray-700">
 <h3 className="font-bold text-xl text-gray-800 dark:text-white mb-4 flex items-center gap-2">
 <MapPin className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                Lozère (48)
              </h3>
 <div className="flex flex-wrap gap-2">
                {['Langogne', 'Auroux', 'Chaudeyrac', 'La Bastide-Puylaurent', 'Grandrieu', 'Châteauneuf-de-Randon'].map((city) => (
 <span key={city} className="px-3 py-1 bg-white dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300 border border-purple-100 dark:border-gray-600">{city}</span>
                ))}
              </div>
            </div>

 <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl p-6 text-white">
 <h3 className="font-bold text-xl mb-2">Intervention rapide</h3>
 <p className="text-white/80 mb-4">Nous intervenons sous 24 à 48h sur toute notre zone de couverture.</p>
              <SmartPhoneLink>
 <Button className="bg-white text-indigo-600 hover:bg-indigo-50 font-bold rounded-full">
 <Phone className="w-5 h-5 mr-2" />
                  04 71 08 27 09
                </Button>
              </SmartPhoneLink>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
 <section id="contact-form" className="py-20 md:py-32 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800" data-testid="contact-section" style={{ minHeight: '600px', contain: 'layout style' }}>
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
 <span className="text-indigo-600 dark:text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">Contact</span>
 <h2 className="font-bold text-3xl sm:text-4xl text-gray-800 dark:text-white tracking-tight mb-4">
            Devis Gratuit – Diagnostic Immobilier
          </h2>
 <p className="text-lg text-gray-600 dark:text-gray-400 mb-12 max-w-2xl mx-auto">
            Contactez-nous pour obtenir un devis personnalisé. Intervention rapide sous 24 à 48 heures en Haute-Loire et Lozère. Rapport de diagnostic transmis sous 72 heures.
          </p>
        </motion.div>

 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
 <SmartPhoneLink className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl p-8 text-white hover:shadow-2xl hover:shadow-indigo-200 dark:hover:shadow-indigo-900 transition-all group" data-testid="contact-phone-card">
 <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 ">
 <Phone className="w-8 h-8 text-white" />
            </div>
 <h3 className="font-bold text-xl mb-2">Téléphone</h3>
 <p className="text-white font-bold text-2xl mb-2">04 71 08 27 09</p>
 <p className="text-indigo-100 text-sm">Lun - Sam: 8h00 - 22h00</p>
          </SmartPhoneLink>

 <motion.a variants={scaleIn} href="mailto:contact@cppb.fr" className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl p-8 text-white hover:shadow-2xl hover:shadow-indigo-200 dark:hover:shadow-indigo-900 transition-all group" data-testid="contact-email-card">
 <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 ">
 <Mail className="w-8 h-8 text-white" />
            </div>
 <h3 className="font-bold text-xl mb-2">Email</h3>
 <p className="text-white font-bold text-xl mb-2">contact@cppb.fr</p>
 <p className="text-indigo-100 text-sm">Réponse sous 24h</p>
          </motion.a>
        </motion.div>

 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-lg mx-auto mb-8 border border-indigo-100 dark:border-gray-700 shadow-xl shadow-indigo-100/30 dark:shadow-gray-900">
 <div className="flex items-center justify-center gap-3 mb-4">
 <MapPin className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
 <h3 className="font-bold text-xl text-gray-800 dark:text-white">Adresses</h3>
          </div>
 <div className="space-y-4">
            <div>
 <p className="font-semibold text-indigo-600 dark:text-indigo-400 mb-1">Siège social</p>
 <p className="text-gray-700 dark:text-gray-300">Rauret Bas, 43340 Rauret</p>
            </div>
            <div>
 <p className="font-semibold text-indigo-600 dark:text-indigo-400 mb-1">Bureaux</p>
 <p className="text-gray-700 dark:text-gray-300">Le Puy-en-Velay et Langogne</p>
            </div>
          </div>
 <p className="text-gray-500 dark:text-gray-500 text-sm mt-4">
 <Clock className="w-4 h-4 inline mr-1" />
            Lun - Sam: 8h00 - 22h00
          </p>
        </motion.div>

 <div className="flex items-center justify-center gap-4">
 <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">Suivez-nous :</span>
          {socialLinks.map((social, index) => (
 <a key={index} href={social.url} target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 rounded-full flex items-center justify-center text-white shadow-lg shadow-indigo-200 dark:shadow-indigo-900 transition-all" aria-label={social.label}>
              <social.icon />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section - Questions fréquentes réglementation
const FAQSection = () => {
  const faqs = [
    {
      question: "Quels diagnostics sont obligatoires pour vendre un bien en Haute-Loire ?",
      answer: "Pour une vente immobilière en Haute-Loire (43), les diagnostics obligatoires comprennent : le DPE (performance énergétique), l'état des risques et pollutions (ERP), et selon l'année de construction : le diagnostic amiante (avant juillet 1997), le diagnostic plomb/CREP (avant 1949), ainsi que l'état des installations électriques et gaz (si plus de 15 ans). Le certificat Loi Carrez est requis pour les lots en copropriété."
    },
    {
      question: "Quelle est la durée de validité du DPE ?",
      answer: "Le Diagnostic de Performance Énergétique (DPE) est valide 10 ans. Attention : les DPE réalisés entre 2018 et juin 2021 doivent être renouvelés car ils ne sont plus conformes à la réglementation actuelle. Depuis 2021, le DPE est devenu opposable juridiquement, renforçant son importance lors des transactions immobilières."
    },
    {
      question: "Qu'est-ce qu'une passoire thermique et quelles sont les obligations ?",
      answer: "Une passoire thermique est un logement classé F ou G au DPE, caractérisé par une très mauvaise performance énergétique. Depuis janvier 2025, les logements classés G sont interdits à la location. Cette interdiction s'étendra aux logements classés F en 2028, puis aux logements classés E en 2034. Un audit énergétique est obligatoire pour la vente des logements F et G."
    }
  ];

  return (
 <section className="py-16 bg-white dark:bg-gray-900 border-t border-indigo-100 dark:border-gray-800" data-testid="faq-section" style={{ minHeight: '450px', contain: 'layout style' }}>
 <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-2 text-center">
          Questions Fréquentes – Diagnostics Immobiliers
        </h2>
 <p className="text-gray-600 dark:text-gray-400 text-sm text-center mb-8">
          Informations essentielles sur la réglementation en vigueur
        </p>
 <div className="space-y-4">
          {faqs.map((faq, index) => (
 <div key={index} className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-2xl p-5 border border-indigo-100 dark:border-gray-700">
 <h3 className="font-semibold text-gray-800 dark:text-white mb-2 flex items-start gap-2">
 <HelpCircle className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                {faq.question}
              </h3>
 <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed pl-7">
                {faq.answer}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Affiliations Section
const AffiliationsSection = () => {
  return (
 <section className="py-16 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 border-t border-indigo-100 dark:border-gray-800" data-testid="affiliations-section" style={{ minHeight: '200px', contain: 'layout style' }}>
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
 <h2 className="font-bold text-xl text-gray-800 dark:text-white mb-8">Certifications et Affiliations Professionnelles</h2>
 <div className="flex flex-wrap items-center justify-center gap-8">
 <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 px-6 py-4 rounded-2xl shadow-md border border-indigo-100 dark:border-gray-700">
 <p className="text-gray-700 dark:text-gray-300 font-medium">Institut de l'expertise Paris</p>
          </div>
 <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 px-6 py-4 rounded-2xl shadow-md border border-indigo-100 dark:border-gray-700 flex items-center gap-3">
 <img src="https://customer-assets.emergentagent.com/job_6d336c5f-babc-4029-abd9-e55bb98f4e64/artifacts/cv7ffiya_images.png" alt="CNEI" className="h-10 w-auto" width="80" height="40" loading="lazy" decoding="async" style={{mixBlendMode: 'multiply'}} />
 <p className="text-gray-700 dark:text-gray-300 font-medium">Membre CNEI Paris</p>
          </div>
 <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 px-6 py-4 rounded-2xl shadow-md border border-indigo-100 dark:border-gray-700">
 <p className="text-gray-700 dark:text-gray-300 font-medium">Certifié B.2.C</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// =====================================================
// SEO INTERNAL LINKS SECTION - Maillage interne SEO
// =====================================================
const SEOLinksSection = () => {
  return (
 <section className="py-12 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800" data-testid="seo-links-section" style={{ minHeight: '200px', contain: 'layout style' }}>
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Diagnostics par type */}
 <div className="mb-10">
 <h2 className="font-bold text-lg text-gray-800 dark:text-white mb-4">Nos Diagnostics Immobiliers</h2>
 <div className="flex flex-wrap gap-3">
            <Link to="/diagnostics/diagnostic-performance-energetique-dpe" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">DPE Haute-Loire</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostics/diagnostic-amiante" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Amiante Haute-Loire</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostics/diagnostic-plomb-crep" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Plomb CREP</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostics/diagnostic-gaz" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Diagnostic Gaz</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostics/diagnostic-electricite" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Diagnostic Électricité</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostics/diagnostic-termites" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Termites</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostics/loi-carrez-mesurage" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Loi Carrez</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostics/etat-risques-pollutions-erp" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">ERP État des Risques</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostics/audit-energetique" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Audit Énergétique</Link>
          </div>
        </div>
        
        {/* Zones d'intervention Haute-Loire */}
 <div className="mb-10">
 <h2 className="font-bold text-lg text-gray-800 dark:text-white mb-4">Diagnostic Immobilier en Haute-Loire (43)</h2>
 <div className="flex flex-wrap gap-3">
            <Link to="/diagnostic-immobilier-le-puy-en-velay" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Diagnostic immobilier Le Puy-en-Velay</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier-yssingeaux-43200" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Diagnostic immobilier Yssingeaux</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier-monistrol-sur-loire-43120" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Diagnostic immobilier Monistrol-sur-Loire</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier-brioude-43100" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Diagnostic immobilier Brioude</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier-rauret-43340" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Diagnostic immobilier Rauret</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier/sainte-sigolene" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Sainte-Sigolène</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier/aurec-sur-loire" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Aurec-sur-Loire</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier/langeac" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Langeac</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier/retournac" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Retournac</Link>
          </div>
        </div>
        
        {/* Zones d'intervention Lozère */}
 <div className="mb-10">
 <h2 className="font-bold text-lg text-gray-800 dark:text-white mb-4">Diagnostic Immobilier en Lozère (48)</h2>
 <div className="flex flex-wrap gap-3">
            <Link to="/diagnostic-immobilier-langogne-48300" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Diagnostic immobilier Langogne</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier/auroux" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Auroux</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier/chaudeyrac" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Chaudeyrac</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier/la-bastide-puylaurent" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">La Bastide-Puylaurent</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier/grandrieu" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Grandrieu</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/diagnostic-immobilier/chateauneuf-de-randon" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Châteauneuf-de-Randon</Link>
          </div>
        </div>

        {/* Services par transaction */}
 <div>
 <h2 className="font-bold text-lg text-gray-800 dark:text-white mb-4">Informations Utiles</h2>
 <div className="flex flex-wrap gap-3">
            <Link to="/validite" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Durée de validité des diagnostics</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/expertise" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Notre expertise certifiée</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/avis" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Avis clients (44 avis - 5/5)</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/actualites" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Actualités DPE et rénovation</Link>
            <span className="text-gray-300 dark:text-gray-600">•</span>
            <Link to="/contact" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">Demander un devis gratuit</Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer - Structure professionnelle conforme au droit français
const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
 <footer className="bg-gray-900 text-white" data-testid="footer">
      {/* Main Footer Content */}
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          
          {/* Colonne 1 — Entreprise */}
          <div>
 <h3 className="font-bold text-lg text-white mb-4">Diagnostic Immobilier Haute-Loire 43</h3>
 <div className="space-y-2 text-sm text-gray-400">
 <p className="font-semibold text-gray-300">SARL CPPB</p>
              <p>RCS Le Puy-en-Velay : 492 270 731</p>
              <p>Siège social : Rauret-Bas – 43340 Rauret</p>
 <div className="pt-3 space-y-2">
 <SmartPhoneLink className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
 <Phone className="w-4 h-4" />
                  +33 (0)4 71 08 27 09
                </SmartPhoneLink>
 <a href="mailto:contact@cppb.fr" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
 <Mail className="w-4 h-4" />
                  contact@cppb.fr
                </a>
              </div>
            </div>
          </div>

          {/* Colonne 2 — Nos Diagnostics (Maillage SEO) */}
          <div>
 <h3 className="font-bold text-lg text-white mb-4">Nos Diagnostics</h3>
 <ul className="space-y-2 text-sm">
              <li><Link to="/diagnostics/diagnostic-performance-energetique-dpe" className="text-gray-400 hover:text-white transition-colors">DPE - Performance Énergétique</Link></li>
              <li><Link to="/diagnostics/diagnostic-amiante" className="text-gray-400 hover:text-white transition-colors">Diagnostic Amiante</Link></li>
              <li><Link to="/diagnostics/diagnostic-plomb-crep" className="text-gray-400 hover:text-white transition-colors">Diagnostic Plomb (CREP)</Link></li>
              <li><Link to="/diagnostics/diagnostic-gaz" className="text-gray-400 hover:text-white transition-colors">Diagnostic Gaz</Link></li>
              <li><Link to="/diagnostics/diagnostic-electricite" className="text-gray-400 hover:text-white transition-colors">Diagnostic Électricité</Link></li>
              <li><Link to="/diagnostics/loi-carrez-mesurage" className="text-gray-400 hover:text-white transition-colors">Loi Carrez - Mesurage</Link></li>
              <li><Link to="/diagnostics/etat-risques-pollutions-erp" className="text-gray-400 hover:text-white transition-colors">ERP - État des Risques</Link></li>
              <li><Link to="/diagnostics/audit-energetique" className="text-gray-400 hover:text-white transition-colors">Audit Énergétique</Link></li>
            </ul>
          </div>

          {/* Colonne 3 — Zones d'intervention (Maillage SEO) */}
          <div>
 <h3 className="font-bold text-lg text-white mb-4">Zones d'intervention</h3>
 <ul className="space-y-2 text-sm">
              <li><Link to="/diagnostic-immobilier-le-puy-en-velay" className="text-gray-400 hover:text-white transition-colors">Le Puy-en-Velay (43000)</Link></li>
              <li><Link to="/diagnostic-immobilier-yssingeaux-43200" className="text-gray-400 hover:text-white transition-colors">Yssingeaux (43200)</Link></li>
              <li><Link to="/diagnostic-immobilier-monistrol-sur-loire-43120" className="text-gray-400 hover:text-white transition-colors">Monistrol-sur-Loire (43120)</Link></li>
              <li><Link to="/diagnostic-immobilier-brioude-43100" className="text-gray-400 hover:text-white transition-colors">Brioude (43100)</Link></li>
              <li><Link to="/diagnostic-immobilier-langogne-48300" className="text-gray-400 hover:text-white transition-colors">Langogne (48300)</Link></li>
              <li><Link to="/diagnostic-immobilier-rauret-43340" className="text-gray-400 hover:text-white transition-colors">Rauret (43340)</Link></li>
            </ul>
 <div className="mt-4 flex items-center gap-3">
              {socialLinks.map((social, index) => (
                <a 
                  key={index} 
                  href={social.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
 className="w-9 h-9 bg-gray-800 hover:bg-indigo-600 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-all"                   aria-label={social.label}
                >
                  <social.icon />
                </a>
              ))}
            </div>
          </div>

          {/* Colonne 4 — Navigation & Légal */}
          <div>
 <h3 className="font-bold text-lg text-white mb-4">Navigation</h3>
 <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Accueil</Link></li>
              <li><Link to="/diagnostics" className="text-gray-400 hover:text-white transition-colors">Tous nos diagnostics</Link></li>
              <li><Link to="/expertise" className="text-gray-400 hover:text-white transition-colors">Notre expertise</Link></li>
              <li><Link to="/validite" className="text-gray-400 hover:text-white transition-colors">Durée de validité</Link></li>
              <li><Link to="/avis" className="text-gray-400 hover:text-white transition-colors">Avis clients</Link></li>
              <li><Link to="/actualites-diagnostics" className="text-gray-400 hover:text-white transition-colors">Toutes les actualités</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact & Devis</Link></li>
            </ul>
 <div className="mt-4 pt-4 border-t border-gray-800">
 <ul className="space-y-2 text-sm">
                <li>
 <Link to="/mentions-legales" className="text-gray-500 hover:text-white transition-colors flex items-center gap-2">
 <FileCheck className="w-4 h-4" />
                    Mentions légales
                  </Link>
                </li>
                <li>
 <Link to="/politique-confidentialite" className="text-gray-500 hover:text-white transition-colors flex items-center gap-2">
 <Shield className="w-4 h-4" />
                    Politique de confidentialité
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Copyright */}
 <div className="border-t border-gray-800">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
 <p className="text-center text-sm text-gray-500">
            © {currentYear} SARL CPPB – Diagnostic Immobilier Haute-Loire 43 – Tous droits réservés
          </p>
        </div>
      </div>
    </footer>
  );
};

// Help Callout Overlay - SOUS le header, en overlay fixe
const HelpCalloutOverlay = ({ isAssistantOpen }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false); // Track if already shown
  const [position, setPosition] = useState(null);
  const appearTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate position dynamically - SOUS le header
  const calculatePosition = useCallback(() => {
    const buttonId = isMobile ? 'help-assistant-trigger-mobile' : 'help-assistant-trigger';
    const button = document.getElementById(buttonId);
    
    if (!button) return;
    
    const rect = button.getBoundingClientRect();
    const headerHeight = 72; // Fixed header height
    
    // Position: UNDER the header, aligned with button
    const bubbleWidth = isMobile ? 180 : 220;
    const edgePadding = isMobile ? 16 : 8; // More padding on mobile for visual breathing
    
    let bubbleX = rect.left + rect.width / 2 - bubbleWidth / 2;
    bubbleX = Math.max(edgePadding, Math.min(window.innerWidth - bubbleWidth - edgePadding, bubbleX));
    const bubbleY = headerHeight + 12; // 12px below header
    
    // Arrow: from top of bubble to bottom of button
    const arrowStartX = bubbleX + bubbleWidth / 2;
    const arrowStartY = bubbleY;
    const arrowEndX = rect.left + rect.width / 2;
    const arrowEndY = rect.bottom + 4;
    
    setPosition({
      bubble: { x: bubbleX, y: bubbleY, width: bubbleWidth },
      arrow: { startX: arrowStartX, startY: arrowStartY, endX: arrowEndX, endY: arrowEndY }
    });
  }, [isMobile]);

  // Recalculate on scroll/resize when visible
  useEffect(() => {
    if (!isVisible) return;
    
    const timer1 = setTimeout(calculatePosition, 50);
    const timer2 = setTimeout(calculatePosition, 200);
    
    const handleUpdate = () => requestAnimationFrame(calculatePosition);
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isVisible, calculatePosition]);

  // Show callout ONCE after 10 seconds, display for 10 seconds
  useEffect(() => {
    if (hasShown || isAssistantOpen) return;

    // Appear after 10 seconds
    appearTimeoutRef.current = setTimeout(() => {
      if (document.visibilityState === 'visible' && !isAssistantOpen) {
        setIsVisible(true);
        setHasShown(true);
        
        // Hide after 10 seconds of display
        hideTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, 10000);
      }
    }, 10000);

    return () => {
      if (appearTimeoutRef.current) clearTimeout(appearTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [hasShown, isAssistantOpen]);

  // Handle tab visibility - pause if tab hidden
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible' && isVisible) {
        setIsVisible(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isVisible]);

  // Hide if assistant opens
  useEffect(() => {
    if (isAssistantOpen && isVisible) {
      setIsVisible(false);
    }
  }, [isAssistantOpen, isVisible]);

  if (!isVisible) return null;

  // Build arrow path (vertical arrow pointing UP)
  let pathD = '';
  let arrowHeadPoints = '';
  
  if (position?.arrow) {
    const { startX, startY, endX, endY } = position.arrow;
    // Curved path from top of bubble to bottom of button
    const ctrlY = (startY + endY) / 2;
    pathD = `M ${startX} ${startY} Q ${startX} ${ctrlY}, ${endX} ${endY}`;
    
    // Arrow head (triangle pointing up)
    const headSize = 6;
    arrowHeadPoints = `${endX},${endY - 2} ${endX - headSize},${endY + headSize} ${endX + headSize},${endY + headSize}`;
  }

  return (
    <>
      {/* SVG Arrow - Fixed overlay pointing UP to button */}
      {position?.arrow && (
        <svg 
 className="fixed inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 9999 }}
        >
          <defs>
            <linearGradient id="helpCalloutGrad" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="#8B5CF6" />
              <stop offset="100%" stopColor="#EC4899" />
            </linearGradient>
            <filter id="helpCalloutGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="0" stdDeviation="2" floodColor="#8B5CF6" floodOpacity="0.5"/>
            </filter>
          </defs>
          <g filter="url(#helpCalloutGlow)">
            <path 
              d={pathD}
              stroke="url(#helpCalloutGrad)" 
              strokeWidth="2" 
              strokeLinecap="round"
              fill="none"
            />
            <polygon 
              points={arrowHeadPoints}
              fill="url(#helpCalloutGrad)"
            />
          </g>
        </svg>
      )}

      {/* Bubble - Fixed overlay UNDER header */}
 <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 9998 }}>
        <AnimatePresence>
          {isVisible && position?.bubble && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
 className="absolute"
              style={{ left: position.bubble.x, top: position.bubble.y }}
            >
              {/* Soft pulsating animation - subtle scale + glow every ~2s */}
              <motion.div
                animate={{ 
                  scale: [1, 1.015, 1],
                  boxShadow: [
                    '0 8px 24px -6px rgba(139, 92, 246, 0.4)',
                    '0 12px 32px -4px rgba(139, 92, 246, 0.55)',
                    '0 8px 24px -6px rgba(139, 92, 246, 0.4)'
                  ]
                }}
                transition={{ 
                  duration: 2, 
                  repeat: Infinity, 
                  ease: "easeInOut",
                  repeatType: "loop"
                }}
 className="relative"
              >
                {/* Soft glow ring - subtle, not aggressive */}
                <motion.span 
 className="absolute -inset-1 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-2xl opacity-0"
                  animate={{ opacity: [0, 0.15, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
                
                {/* Main bubble - NO close button, NO click interaction */}
                <div 
 className="relative bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl px-4 py-2.5"
                  style={{ boxShadow: '0 8px 24px -6px rgba(139, 92, 246, 0.4)' }}
                >
                  {/* Exact text */}
 <p className={`text-white font-semibold ${isMobile ? 'text-xs' : 'text-sm'} whitespace-nowrap`}>
                    Quels diagnostics pour mon bien ?
                  </p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

// Floating Phone Button avec animation pulsante
// Hook pour gérer le lien téléphone (Desktop → /contact, Mobile → tel:)
const usePhoneLink = () => {
  const [isDesktop, setIsDesktop] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkDesktop = () => {
      setIsDesktop(window.matchMedia("(min-width: 1024px)").matches);
    };
    
    checkDesktop();
    window.addEventListener("resize", checkDesktop);
    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  const handlePhoneClick = (e, trackingLabel) => {
    trackPhoneClick(trackingLabel);
    if (isDesktop) {
      e.preventDefault();
      navigate("/contact");
    }
    // Sur mobile, le lien tel: fonctionne normalement
  };

  return { isDesktop, handlePhoneClick, phoneHref: isDesktop ? "/contact" : "tel:0471082709" };
};

// Composant PhoneButton réutilisable (Desktop → /contact, Mobile → appel)
const PhoneButton = ({ children, className, trackingLabel, testId }) => {
  const { handlePhoneClick, phoneHref } = usePhoneLink();
  
  return (
    <a
      href={phoneHref}
      onClick={(e) => handlePhoneClick(e, trackingLabel || 'phone_button')}
      data-testid={testId}
    >
      <Button className={className}>
        {children}
      </Button>
    </a>
  );
};

const FloatingPhone = () => {
  const { handlePhoneClick, phoneHref } = usePhoneLink();
  
  return (
    <a 
      href={phoneHref}
      onClick={(e) => handlePhoneClick(e, 'floating_button')}
      className="floating-phone-btn"
      data-testid="floating-phone-btn"
      aria-label="Appeler CPPB Diagnostics"
    >
      {/* Cercle pulsant - position absolute relative au bouton */}
      <span className="floating-phone-pulse" />
      <span className="floating-phone-pulse floating-phone-pulse-delayed" />
      
      {/* Bouton principal */}
      <div className="relative w-16 h-16 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-2xl shadow-indigo-300 dark:shadow-indigo-900 group-hover:shadow-indigo-400 transition-shadow duration-300">
        <Phone className="w-7 h-7 text-white" />
      </div>
    </a>
  );
};

// Portal wrapper pour éviter les problèmes de transform parent
// NOTE: Hidden in embed mode via CSS in DiagnosticDetailPage + JS check here
const FloatingPhonePortal = () => {
  if (typeof document === "undefined") return null;
  // Don't render in embed mode
  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get('embed') === '1') {
    return null;
  }
  return createPortal(<FloatingPhone />, document.body);
};

// Mentions Légales Page
const MentionsLegalesPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>Mentions Légales | CPPB Diagnostic Immobilier Haute-Loire 43</title>
        <meta name="description" content="Mentions légales du site CPPB Diagnostic Immobilier Haute-Loire 43. SARL CPPB, RCS Le Puy-en-Velay 492 270 731, Rauret-Bas 43340 Rauret." />
        <link rel="canonical" href="https://www.diagnostic-immobilier-haute-loire-43.fr/mentions-legales" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
 <main className="pt-20">
        {/* Hero */}
 <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 py-16">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
 <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
 <ArrowLeft className="w-5 h-5" />
                Retour à l'accueil
              </Link>
 <h1 className="font-bold text-3xl sm:text-4xl text-white">Mentions Légales</h1>
            </motion.div>
          </div>
        </section>

        {/* Content */}
 <section className="py-16 bg-white dark:bg-gray-900">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
 <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="prose prose-lg dark:prose-invert max-w-none">
              
              {/* Éditeur du site */}
 <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-3xl p-8 mb-8 border border-indigo-100 dark:border-gray-700">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6 flex items-center gap-3">
 <Building className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
                  Éditeur du site
                </h2>
                
 <div className="space-y-4 text-gray-700 dark:text-gray-300">
 <p className="font-semibold text-lg text-indigo-600 dark:text-indigo-400">CPPB / HAUTE-LOIRE</p>
                  <p><strong>RCS :</strong> 492 270 731</p>
                  <p>Le site <strong>DIAGNOSTIC IMMOBILIER HAUTE-LOIRE 43</strong> est édité par la <strong>SARL CPPB</strong>.</p>
                  <p><strong>Nom commercial :</strong> Diagnostic Immobilier Haute-Loire 43</p>
                  <p><strong>Siège social :</strong> Rauret-Bas – 43340 RAURET – France</p>
                  <p><strong>RCS Le Puy-en-Velay :</strong> 492 270 731</p>
                  <p><strong>Responsable de la publication :</strong> M. Gibert</p>
                  
 <div className="flex flex-col sm:flex-row gap-4 mt-6">
 <SmartPhoneLink className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 hover:underline">
 <Phone className="w-5 h-5" />
                      +33 (0)4 71 08 27 09
                    </SmartPhoneLink>
 <a href="mailto:contact@cppb.fr" className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 hover:underline">
 <Mail className="w-5 h-5" />
                      contact@cppb.fr
                    </a>
                  </div>
                </div>
              </div>

              {/* Hébergement */}
 <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-800 rounded-3xl p-8 mb-8 border border-purple-100 dark:border-gray-700">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6 flex items-center gap-3">
 <Shield className="w-7 h-7 text-purple-600 dark:text-purple-400" />
                  Hébergement
                </h2>
                
 <div className="space-y-2 text-gray-700 dark:text-gray-300">
 <p className="font-semibold text-lg text-purple-600 dark:text-purple-400">1&1 Internet SARL (IONOS)</p>
                  <p>7, place de la Gare</p>
                  <p>BP 70109</p>
                  <p>57201 Sarreguemines Cedex – France</p>
                </div>
              </div>

              {/* Propriété intellectuelle */}
 <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 mb-8 border border-gray-200 dark:border-gray-700 shadow-lg">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6">Propriété intellectuelle</h2>
 <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  L'ensemble du contenu de ce site (textes, images, vidéos, logos, marques) est protégé par le droit d'auteur et le droit des marques. 
                  Toute reproduction, représentation, modification, publication ou adaptation de tout ou partie des éléments du site est interdite sans l'autorisation préalable écrite de CPPB.
                </p>
              </div>

              {/* Protection des données */}
 <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 mb-8 border border-gray-200 dark:border-gray-700 shadow-lg">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6">Protection des données personnelles (RGPD)</h2>
 <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                  <p>
                    Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, 
                    vous disposez d'un droit d'accès, de rectification, de suppression et d'opposition aux données personnelles vous concernant.
                  </p>
                  <p>
 Pour exercer ces droits, vous pouvez nous contacter par email à <a href="mailto:contact@cppb.fr" className="text-indigo-600 dark:text-indigo-400 hover:underline">contact@cppb.fr</a> ou                     par courrier à l'adresse du siège social.
                  </p>
                  <p>
                    Les données collectées via le formulaire de contact sont utilisées uniquement pour répondre à vos demandes et ne sont jamais transmises à des tiers.
                  </p>
                </div>
              </div>

              {/* Cookies */}
 <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 mb-8 border border-gray-200 dark:border-gray-700 shadow-lg">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6">Cookies</h2>
 <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                  <p>
                    Ce site n'utilise pas de cookies de navigation.
                  </p>
                  <p>
                    Conformément à la réglementation, aucun cookie non essentiel n'est déposé sans votre consentement préalable.
                  </p>
                  <p>
                    Ce site utilise <strong>Google Analytics GA4</strong> (ID : G-2J21ZVQ4MJ) pour mesurer l'audience et améliorer l'expérience utilisateur.
                  </p>
                  <p>
                    Les données collectées sont anonymisées et ne permettent pas d'identifier personnellement les visiteurs.
                  </p>
                </div>
              </div>

              {/* Limitation de responsabilité */}
 <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 border border-gray-200 dark:border-gray-700 shadow-lg">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6">Limitation de responsabilité</h2>
 <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  CPPB s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur ce site. 
                  Toutefois, CPPB ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition sur ce site. 
                  En conséquence, CPPB décline toute responsabilité pour toute imprécision, inexactitude ou omission portant sur des informations disponibles sur ce site.
                </p>
              </div>

            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingPhonePortal />
    </div>
  );
};

// Politique de Confidentialité Page - Conforme RGPD
const PolitiqueConfidentialitePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>Politique de Confidentialité | CPPB Diagnostic Immobilier Haute-Loire 43</title>
        <meta name="description" content="Politique de confidentialité et protection des données personnelles de CPPB Diagnostic Immobilier. Conforme RGPD." />
        <link rel="canonical" href="https://www.diagnostic-immobilier-haute-loire-43.fr/politique-confidentialite" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
 <main className="pt-20">
        {/* Hero */}
 <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 py-16">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
 <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
 <ArrowLeft className="w-5 h-5" />
                Retour à l'accueil
              </Link>
 <h1 className="font-bold text-3xl sm:text-4xl text-white">Politique de Confidentialité</h1>
            </motion.div>
          </div>
        </section>

        {/* Content */}
 <section className="py-16 bg-white dark:bg-gray-900">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
              
              {/* Introduction */}
 <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-3xl p-8 mb-8 border border-indigo-100 dark:border-gray-700">
 <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                  Les données personnelles collectées sur ce site sont traitées par <strong>SARL CPPB</strong>, dans le cadre de son activité de diagnostics immobiliers.
                </p>
              </div>

              {/* Données collectées */}
 <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 mb-8 border border-gray-200 dark:border-gray-700 shadow-lg">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6 flex items-center gap-3">
 <FileCheck className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
                  Données collectées
                </h2>
 <ul className="space-y-3 text-gray-700 dark:text-gray-300">
 <li className="flex items-start gap-3">
 <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Données de contact transmises volontairement (formulaire, email, téléphone).</span>
                  </li>
 <li className="flex items-start gap-3">
 <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Données de navigation anonymisées via Google Analytics (GA4).</span>
                  </li>
                </ul>
              </div>

              {/* Finalités du traitement */}
 <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 mb-8 border border-gray-200 dark:border-gray-700 shadow-lg">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6 flex items-center gap-3">
 <Eye className="w-7 h-7 text-purple-600 dark:text-purple-400" />
                  Finalités du traitement
                </h2>
 <ul className="space-y-3 text-gray-700 dark:text-gray-300">
 <li className="flex items-start gap-3">
 <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Répondre aux demandes de contact et de devis.</span>
                  </li>
 <li className="flex items-start gap-3">
 <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span>Mesurer l'audience et améliorer le site.</span>
                  </li>
                </ul>
              </div>

              {/* Base légale */}
 <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 mb-8 border border-gray-200 dark:border-gray-700 shadow-lg">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6 flex items-center gap-3">
 <Scale className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
                  Base légale
                </h2>
 <ul className="space-y-3 text-gray-700 dark:text-gray-300">
 <li className="flex items-start gap-3">
 <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span><strong>Consentement de l'utilisateur</strong> (cookies et statistiques).</span>
                  </li>
 <li className="flex items-start gap-3">
 <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span><strong>Intérêt légitime</strong> pour la gestion des demandes clients.</span>
                  </li>
                </ul>
              </div>

              {/* Cookies */}
 <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-800 rounded-3xl p-8 mb-8 border border-purple-100 dark:border-gray-700">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6 flex items-center gap-3">
 <Lock className="w-7 h-7 text-purple-600 dark:text-purple-400" />
                  Cookies et mesure d'audience
                </h2>
 <div className="space-y-4 text-gray-700 dark:text-gray-300">
                  <p>
                    Ce site utilise <strong>Google Analytics GA4</strong> (ID : G-2J21ZVQ4MJ) pour mesurer l'audience et améliorer l'expérience utilisateur.
                  </p>
                  <p>
                    Les données collectées sont anonymisées et ne permettent pas d'identifier personnellement les visiteurs.
                  </p>
                </div>
              </div>

              {/* Durée de conservation */}
 <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 mb-8 border border-gray-200 dark:border-gray-700 shadow-lg">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6 flex items-center gap-3">
 <Clock className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
                  Durée de conservation
                </h2>
 <ul className="space-y-3 text-gray-700 dark:text-gray-300">
 <li className="flex items-start gap-3">
 <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span><strong>Données de contact :</strong> durée strictement nécessaire au traitement de la demande.</span>
                  </li>
 <li className="flex items-start gap-3">
 <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <span><strong>Données statistiques :</strong> conformément aux paramètres Google Analytics.</span>
                  </li>
                </ul>
              </div>

              {/* Droits des utilisateurs */}
 <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 rounded-3xl p-8 border border-indigo-100 dark:border-gray-700">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6 flex items-center gap-3">
 <Shield className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
                  Droits des utilisateurs
                </h2>
 <div className="space-y-4 text-gray-700 dark:text-gray-300">
                  <p>
                    Conformément au <strong>RGPD</strong>, vous disposez des droits suivants :
                  </p>
 <ul className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {['Accès', 'Rectification', 'Effacement', 'Opposition', 'Limitation', 'Portabilité'].map((right) => (
 <li key={right} className="flex items-center gap-2">
 <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>{right}</span>
                      </li>
                    ))}
                  </ul>
 <div className="mt-6 p-4 bg-white dark:bg-gray-700 rounded-2xl border border-indigo-200 dark:border-gray-600">
 <p className="font-semibold text-gray-800 dark:text-white mb-2">Pour exercer vos droits :</p>
 <a href="mailto:contact@cppb.fr" className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 hover:underline font-medium">
 <Mail className="w-5 h-5" />
                      contact@cppb.fr
                    </a>
                  </div>
                </div>
              </div>

            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingPhonePortal />
    </div>
  );
};

// Diagnostic Detail Page
const DiagnosticDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = services.find(s => s.slug === slug);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);
  
  // Detect embed mode via ?embed=1 query parameter
  const isEmbed = new URLSearchParams(window.location.search).get('embed') === '1';
  
  // Set data-embed on BOTH <html> and <body> for embed-specific CSS
  useEffect(() => {
    if (isEmbed) {
      document.documentElement.dataset.embed = '1';
      document.body.dataset.embed = '1';
      document.body.style.overscrollBehavior = 'contain';
    }
    return () => {
      document.documentElement.dataset.embed = '0';
      document.body.dataset.embed = '0';
      document.body.style.overscrollBehavior = '';
    };
  }, [isEmbed]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return (
 <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
 <div className="text-center">
 <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Diagnostic non trouvé</h1>
 <Link to="/" className="text-indigo-600 dark:text-indigo-400 hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }
  
  // EMBED MODE: Minimal "app view" layout - no header, footer, back button, floating phone
  if (isEmbed) {
    return (
      <div className="font-sans dark:bg-gray-900 min-h-screen" data-embed-page="true">
        {/* TOP ANCHOR - Firefox scroll fix */}
        <div id="top" style={{ position: 'absolute', top: 0 }} />
        
        {/* Embed-only styles - RESPONSIVE FIX */}
        <style>{`
          /* Hide floating elements */
          body[data-embed="1"] .floating-phone-btn,
          body[data-embed="1"] .floating-phone,
          body[data-embed="1"] [data-floating-call],
          body[data-embed="1"] [data-testid="floating-phone-btn"] {
            display: none !important;
          }
          body[data-embed="1"] {
            overscroll-behavior: contain;
          }
          
          /* EMBED RESPONSIVE FIX - Force all content to fit viewport */
          [data-embed-page="true"] {
            width: 100% !important;
            max-width: 100vw !important;
            overflow-x: hidden !important;
            box-sizing: border-box !important;
          }
          [data-embed-page="true"] * {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          [data-embed-page="true"] section {
            width: 100% !important;
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          [data-embed-page="true"] .max-w-4xl {
            max-width: 100% !important;
            width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          [data-embed-page="true"] h1 {
            font-size: clamp(1.25rem, 4vw, 1.875rem) !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
          }
          [data-embed-page="true"] h2 {
            font-size: clamp(1.125rem, 3.5vw, 1.5rem) !important;
          }
          [data-embed-page="true"] p {
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            hyphens: auto !important;
          }
        `}</style>
        
        <main className="pt-0">
          {/* Hero - compact for embed */}
          <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 py-12 px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <service.icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <span className="text-indigo-200 text-sm font-medium">{service.required}</span>
                  <h1 className="font-bold text-2xl sm:text-3xl text-white">{service.fullName}{['Termites', 'Assainissement', 'Audit énergétique'].includes(service.name) && <sup className="ml-0.5" style={{ position: 'relative', top: '-0.5em', fontSize: '0.5em', lineHeight: 0 }}>*</sup>}</h1>
                </div>
              </div>
              
              <p className="text-lg text-indigo-100/90 leading-relaxed mb-6">{service.description}</p>
    {['Termites', 'Assainissement', 'Audit énergétique'].includes(service.name) && (
      <p className="text-[10px] leading-tight text-indigo-100/60 italic -mt-3 mb-6">
        {service.name === 'Assainissement'
          ? '*si concerné, réalisé par le SPANC'
          : '*si concerné, réalisé par entreprise partenaire'}
      </p>
    )}
              
              <div className="flex flex-wrap gap-3">
                <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm">
                  <Clock className="w-4 h-4 inline mr-2" />
                  Validité: {service.validity}
                </div>
                <a href="tel:0471082709" className="bg-white text-indigo-600 hover:bg-indigo-50 font-bold rounded-full px-4 py-2 text-sm flex items-center gap-2 transition-colors">
                  <Phone className="w-4 h-4" />
                  04 71 08 27 09
                </a>
              </div>
            </div>
          </section>

          {/* Content */}
          <section className="py-12 bg-white dark:bg-gray-900 px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-bold text-xl text-gray-800 dark:text-white mb-4">En savoir plus sur le {service.name}</h2>
              <div className="prose prose-base dark:prose-invert max-w-none">
                {service.longDescription.split('\n').map((paragraph, i) => (
                  <p key={i} className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3 text-sm">{paragraph}</p>
                ))}
              </div>
            </div>
          </section>

          {/* CTA - compact */}
          <section className="py-10 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="font-bold text-xl text-gray-800 dark:text-white mb-3">Besoin d'un {service.name} ?</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">Contactez-nous pour un devis gratuit.</p>
              <a href="tel:0471082709" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-white font-bold shadow-lg transition-all">
                <Phone className="w-5 h-5" />
                04 71 08 27 09
              </a>
            </div>
          </section>
        </main>
        
        {/* NO Footer, NO FloatingPhone, NO Header, NO StaticWizard in embed mode */}
      </div>
    );
  }

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      {/* TOP ANCHOR - Firefox scroll fix */}
      <div id="top" style={{ position: 'absolute', top: 0 }} />
      
      {/* SEO Meta Tags Dynamiques */}
      <Helmet>
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDescription} />
        {service.metaKeywords && <meta name="keywords" content={service.metaKeywords} />}
        <link rel="canonical" href={`https://www.diagnostic-immobilier-haute-loire-43.fr/diagnostics/${service.slug}`} />
        <meta property="og:title" content={service.metaTitle} />
        <meta property="og:description" content={service.metaDescription} />
        <meta property="og:url" content={`https://www.diagnostic-immobilier-haute-loire-43.fr/diagnostics/${service.slug}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={service.metaTitle} />
        <meta name="twitter:description" content={service.metaDescription} />
      </Helmet>
      
      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)} />
      
 <main className="pt-20">
        {/* Hero */}
 <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 py-20">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
 <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
 <ArrowLeft className="w-5 h-5" />
                Retour
              </button>
              
 <div className="flex items-center gap-4 mb-6">
 <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
 <service.icon className="w-8 h-8 text-white" />
                </div>
                <div>
 <span className="text-indigo-200 text-sm font-medium">{service.required}</span>
 <h1 className="font-bold text-3xl sm:text-4xl text-white">{service.fullName}{['Termites', 'Assainissement', 'Audit énergétique'].includes(service.name) && <sup className="ml-0.5" style={{ position: 'relative', top: '-0.5em', fontSize: '0.5em', lineHeight: 0 }}>*</sup>}</h1>
                </div>
              </div>
              
 <p className="text-xl text-indigo-100/90 leading-relaxed mb-8">{service.description}</p>
    {['Termites', 'Assainissement', 'Audit énergétique'].includes(service.name) && (
      <p className="text-[10px] leading-tight text-indigo-100/60 italic -mt-5 mb-8">
        {service.name === 'Assainissement'
          ? '*si concerné, réalisé par le SPANC'
          : '*si concerné, réalisé par entreprise partenaire'}
      </p>
    )}
              
 <div className="flex flex-wrap gap-4">
 <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white">
 <Clock className="w-4 h-4 inline mr-2" />
                  Validité: {service.validity}
                </div>
                <SmartPhoneLink>
 <Button className="bg-white text-indigo-600 hover:bg-indigo-50 font-bold rounded-full">
 <Phone className="w-5 h-5 mr-2" />
                    04 71 08 27 09
                  </Button>
                </SmartPhoneLink>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Content */}
 <section className="py-16 bg-white dark:bg-gray-900">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-6">En savoir plus sur le {service.name} en Haute-Loire et Lozère</h2>
 <div className="prose prose-lg dark:prose-invert max-w-none">
                {service.longDescription.split('\n').map((paragraph, i) => (
 <p key={i} className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
 <section className="py-16 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-4">Besoin d'un {service.name} ?</h2>
 <p className="text-gray-600 dark:text-gray-400 mb-8">Contactez-nous pour un devis gratuit. Intervention rapide en Haute-Loire et Lozère.</p>
 <div className="flex flex-wrap justify-center gap-4">
                <SmartPhoneLink>
 <Button className="h-14 px-8 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-white font-bold text-lg shadow-xl">
 <Phone className="w-5 h-5 mr-2" />
                    04 71 08 27 09
                  </Button>
                </SmartPhoneLink>
                <Link to="/contact">
 <Button variant="outline" className="h-14 px-8 rounded-full border-2 border-indigo-200 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 font-bold text-lg">
 <FileText className="w-5 h-5 mr-2" />
                    Demander un devis
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Other Services */}
 <section className="py-16 bg-white dark:bg-gray-900">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-8 text-center">Autres Diagnostics</h2>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {services.filter(s => s.id !== service.id).slice(0, 4).map((otherService) => (
 <Link key={otherService.id} to={`/diagnostics/${otherService.slug}`} className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-800 p-4 rounded-2xl border border-indigo-100 dark:border-gray-700 hover:shadow-lg transition-all group">
 <otherService.icon className="w-8 h-8 text-indigo-600 dark:text-indigo-400 mb-2 " />
 <h3 className="font-bold text-gray-800 dark:text-white">{otherService.name}</h3>
 <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">{otherService.fullName}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingPhonePortal />
      <HelpCalloutOverlay isAssistantOpen={helpAssistantOpen} />
      <StaticWizard isOpen={helpAssistantOpen} onClose={() => setHelpAssistantOpen(false)} />
    </div>
  );
};

// ============================================
// PAGES DÉDIÉES SEO - Routes propres
// ============================================

const DOMAIN = "https://www.diagnostic-immobilier-haute-loire-43.fr";

// Page Diagnostics dédiée
const DiagnosticsPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": DOMAIN },
      { "@type": "ListItem", "position": 2, "name": "Diagnostics", "item": `${DOMAIN}/diagnostics` }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Quels diagnostics sont obligatoires pour vendre ?", "acceptedAnswer": { "@type": "Answer", "text": "Pour une vente : DPE, amiante (avant 1997), plomb (avant 1949), gaz et électricité (+15 ans), termites (zones), ERP, Loi Carrez (copropriété), assainissement (non raccordé)." } },
      { "@type": "Question", "name": "Quels diagnostics pour une location ?", "acceptedAnswer": { "@type": "Answer", "text": "Pour une location : DPE, amiante (parties privatives), plomb (avant 1949), gaz et électricité (+15 ans), ERP." } },
      { "@type": "Question", "name": "Combien coûte un pack diagnostics complet ?", "acceptedAnswer": { "@type": "Answer", "text": "Le tarif dépend de la surface et des diagnostics requis. Appelez le 04 71 08 27 09 pour un devis gratuit personnalisé." } }
    ]
  };

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>Diagnostics immobiliers obligatoires | DPE Amiante Plomb Gaz | CPPB</title>
        <meta name="description" content="Tous vos diagnostics vente/location : DPE, amiante, plomb (CREP), gaz, électricité, ERP, Carrez. Haute-Loire 43 & Lozère 48. Devis gratuit 04 71 08 27 09." />
        <link rel="canonical" href={`${DOMAIN}/diagnostics`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Diagnostics immobiliers obligatoires | CPPB" />
        <meta property="og:description" content="DPE, amiante, plomb, gaz, électricité, ERP, Carrez. Expert certifié B.2.C en Haute-Loire et Lozère." />
        <meta property="og:url" content={`${DOMAIN}/diagnostics`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Diagnostics immobiliers obligatoires | CPPB" />
        <meta name="twitter:description" content="DPE, amiante, plomb, gaz, électricité. Expert certifié Haute-Loire Lozère." />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)} />
      
 <main className="pt-20">
 <section className="py-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600">
 <div className="max-w-6xl mx-auto px-4 text-center text-white">
 <h1 className="text-4xl md:text-5xl font-bold mb-4">Diagnostics Immobiliers Obligatoires</h1>
 <p className="text-xl text-indigo-100 max-w-3xl mx-auto">DPE, amiante, plomb, gaz, électricité : tous les diagnostics réglementaires pour vente et location en Haute-Loire (43) et Lozère (48).</p>
          </div>
        </section>
        <ServicesSection />
        
 <section className="py-16 bg-white dark:bg-gray-900">
 <div className="max-w-4xl mx-auto px-4">
 <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Diagnostics immobiliers en Haute-Loire et Lozère</h2>
 <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              CPPB réalise l'ensemble des <strong>diagnostics immobiliers obligatoires</strong> pour la vente et la location de biens en <strong>Haute-Loire (43)</strong> et <strong>Lozère (48)</strong>. 
              Notre diagnostiqueur certifié <strong>B.2.C</strong> intervient rapidement à <strong>Le Puy-en-Velay (43000)</strong>, <strong>Yssingeaux (43200)</strong>, <strong>Langogne (48300)</strong> et communes environnantes.
            </p>
 <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
              Le <strong>Dossier de Diagnostics Techniques (DDT)</strong> comprend selon votre situation : DPE, diagnostic amiante, plomb (CREP), gaz, électricité, termites, ERP, Loi Carrez et assainissement.
            </p>
 <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              <strong>Devis gratuit</strong> et intervention sous 24-48h. Appelez le <SmartPhoneLink className="text-indigo-600 font-semibold">04 71 08 27 09</SmartPhoneLink>.
            </p>
          </div>
        </section>
      </main>
      
      <Footer />
      <FloatingPhonePortal />
    </div>
  );
};

// Page Expertise dédiée
const ExpertisePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": DOMAIN },
      { "@type": "ListItem", "position": 2, "name": "Expertise", "item": `${DOMAIN}/expertise` }
    ]
  };

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>Expertise & certification B.2.C | Diagnostiqueur CPPB</title>
        <meta name="description" content="Diagnostiqueur certifié B.2.C. Méthode, conformité réglementaire, délais et qualité de rapport. Intervention rapide 43/48." />
        <link rel="canonical" href={`${DOMAIN}/expertise`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Expertise & certification B.2.C | CPPB" />
        <meta property="og:description" content="Diagnostiqueur certifié B.2.C. Qualité et conformité garanties." />
        <meta property="og:url" content={`${DOMAIN}/expertise`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Expertise B.2.C | CPPB" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)} />
      
 <main className="pt-20">
 <section className="py-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600">
 <div className="max-w-6xl mx-auto px-4 text-center text-white">
 <h1 className="text-4xl md:text-5xl font-bold mb-4">Notre Expertise</h1>
 <p className="text-xl text-indigo-100 max-w-3xl mx-auto">Diagnostiqueur certifié B.2.C, méthode rigoureuse et conformité réglementaire.</p>
          </div>
        </section>
        <ExpertiseSection />
      </main>
      
      <Footer />
      <FloatingPhonePortal />
    </div>
  );
};

// Page Validité dédiée
const ValiditePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": DOMAIN },
      { "@type": "ListItem", "position": 2, "name": "Validité", "item": `${DOMAIN}/validite` }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Quelle est la durée de validité d'un DPE ?", "acceptedAnswer": { "@type": "Answer", "text": "Le DPE est valable 10 ans. Un DPE périmé doit être refait avant toute transaction." } },
      { "@type": "Question", "name": "Le diagnostic amiante expire-t-il ?", "acceptedAnswer": { "@type": "Answer", "text": "Si négatif, le diagnostic amiante a une validité illimitée. Si positif (présence d'amiante), il est valable 3 ans." } },
      { "@type": "Question", "name": "Validité diagnostic électricité vente vs location ?", "acceptedAnswer": { "@type": "Answer", "text": "Le diagnostic électricité est valable 3 ans pour une vente et 6 ans pour une location." } }
    ]
  };

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>Durée de validité des diagnostics | DPE Amiante Plomb Gaz Électricité</title>
        <meta name="description" content="Validités officielles : DPE 10 ans, amiante 3 ans/illimité, plomb 1-6 ans, gaz et électricité 3-6 ans, ERP 6 mois. Infos claires vente/location 43/48." />
        <link rel="canonical" href={`${DOMAIN}/validite`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Durée de validité des diagnostics | CPPB" />
        <meta property="og:description" content="Toutes les durées de validité des diagnostics immobiliers obligatoires." />
        <meta property="og:url" content={`${DOMAIN}/validite`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)} />
      
 <main className="pt-20">
 <section className="py-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600">
 <div className="max-w-6xl mx-auto px-4 text-center text-white">
 <h1 className="text-4xl md:text-5xl font-bold mb-4">Durée de Validité des Diagnostics</h1>
 <p className="text-xl text-indigo-100 max-w-3xl mx-auto">Consultez les durées de validité officielles pour chaque diagnostic immobilier, en vente comme en location.</p>
          </div>
        </section>
        <ValiditySection />
      </main>
      
      <Footer />
      <FloatingPhonePortal />
    </div>
  );
};

// Page Avis dédiée
const AvisPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": DOMAIN },
      { "@type": "ListItem", "position": 2, "name": "Avis clients", "item": `${DOMAIN}/avis` }
    ]
  };

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>Avis clients & confiance | CPPB Diagnostic Immobilier 43/48</title>
        <meta name="description" content="Retours clients, satisfaction, sérieux et rapidité. CPPB intervient en Haute-Loire et Lozère. Note 5/5 sur Google et Trustpilot." />
        <link rel="canonical" href={`${DOMAIN}/avis`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Avis clients | CPPB Diagnostics Immobiliers" />
        <meta property="og:description" content="44 avis 5 étoiles. Clients satisfaits en Haute-Loire et Lozère." />
        <meta property="og:url" content={`${DOMAIN}/avis`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)} />
      
 <main className="pt-20">
 <section className="py-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600">
 <div className="max-w-6xl mx-auto px-4 text-center text-white">
 <h1 className="text-4xl md:text-5xl font-bold mb-4">Avis Clients</h1>
 <p className="text-xl text-indigo-100 max-w-3xl mx-auto">Découvrez les retours de nos clients satisfaits en Haute-Loire et Lozère.</p>
          </div>
        </section>
        <TestimonialsSection />
      </main>
      
      <Footer />
      <FloatingPhonePortal />
    </div>
  );
};

// Page Archive Actualités Canonique — /actualites-diagnostics
const ActualitesDiagnosticsPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": DOMAIN },
      { "@type": "ListItem", "position": 2, "name": "Actualités diagnostics", "item": `${DOMAIN}/actualites-diagnostics` }
    ]
  };

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>Actualités diagnostics immobiliers – Haute-Loire | CPPB</title>
        <meta name="description" content="Actualités DPE, audit énergétique, obligations vente et location. Réglementation diagnostics immobiliers Haute-Loire et Lozère." />
        <link rel="canonical" href={`${DOMAIN}/actualites-diagnostics`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Actualités diagnostics immobiliers – Haute-Loire | CPPB" />
        <meta property="og:description" content="Évolutions réglementaires DPE, audit énergétique, calendrier 2025-2034." />
        <meta property="og:url" content={`${DOMAIN}/actualites-diagnostics`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)} />
      
 <main className="pt-20">
 <section className="py-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600">
 <div className="max-w-6xl mx-auto px-4 text-center text-white">
 <h1 className="text-4xl md:text-5xl font-bold mb-4">Actualités Diagnostics Immobiliers</h1>
 <p className="text-xl text-indigo-100 max-w-3xl mx-auto">Suivez les évolutions du DPE, audit énergétique et obligations réglementaires en Haute-Loire et Lozère.</p>
          </div>
        </section>
        <ActualitesSection isArchivePage={true} maxItems={40} />
      </main>
      
      <Footer />
      <FloatingPhonePortal />
    </div>
  );
};

// Page Alias Actualités — avec canonical vers /actualites-diagnostics
const ActualitesAliasPage = ({ aliasPath }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Titres selon l'alias
  const aliasTitles = {
    'actualites-immobilieres': 'Actualités Immobilières',
    'actualites-haute-loire': 'Actualités Haute-Loire',
    'infos-diagnostics': 'Infos Diagnostics',
    'veille-immobiliere': 'Veille Immobilière'
  };

  const title = aliasTitles[aliasPath] || 'Actualités';

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{title} – Diagnostics Haute-Loire | CPPB</title>
        <meta name="description" content="Actualités diagnostics immobiliers Haute-Loire et Lozère. DPE, audit énergétique, réglementation." />
        {/* Canonical vers la page principale pour éviter duplicate content */}
        <link rel="canonical" href={`${DOMAIN}/actualites-diagnostics`} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)} />
      
 <main className="pt-20">
 <section className="py-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600">
 <div className="max-w-6xl mx-auto px-4 text-center text-white">
 <h1 className="text-4xl md:text-5xl font-bold mb-4">{title}</h1>
 <p className="text-xl text-indigo-100 max-w-3xl mx-auto">Suivez les évolutions du DPE, audit énergétique et obligations réglementaires.</p>
          </div>
        </section>
        <ActualitesSection isArchivePage={true} maxItems={40} />
      </main>
      
      <Footer />
      <FloatingPhonePortal />
    </div>
  );
};

// Page Actualités dédiée (ancienne - redirige vers canonical)
const ActualitesPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": DOMAIN },
      { "@type": "ListItem", "position": 2, "name": "Actualités", "item": `${DOMAIN}/actualites` }
    ]
  };

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>Actualités réglementation diagnostics | DPE audit énergétique | CPPB</title>
        <meta name="description" content="Évolutions DPE, obligations vente/location, calendrier interdiction passoires thermiques. Infos utiles propriétaires et bailleurs." />
        <link rel="canonical" href={`${DOMAIN}/actualites`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Actualités diagnostics immobiliers | CPPB" />
        <meta property="og:description" content="Évolutions réglementaires DPE, audit énergétique, calendrier 2025-2034." />
        <meta property="og:url" content={`${DOMAIN}/actualites`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)} />
      
 <main className="pt-20">
 <section className="py-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600">
 <div className="max-w-6xl mx-auto px-4 text-center text-white">
 <h1 className="text-4xl md:text-5xl font-bold mb-4">Actualités & Réglementation</h1>
 <p className="text-xl text-indigo-100 max-w-3xl mx-auto">Suivez les évolutions du DPE, audit énergétique et obligations réglementaires.</p>
          </div>
        </section>
        <ActualitesSection isArchivePage={true} />
      </main>
      
      <Footer />
      <FloatingPhonePortal />
    </div>
  );
};

// Page Contact dédiée
const ContactPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": DOMAIN },
      { "@type": "ListItem", "position": 2, "name": "Contact", "item": `${DOMAIN}/contact` }
    ]
  };

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>Contact & devis gratuit | Diagnostic Immobilier 43/48 | CPPB</title>
        <meta name="description" content="Devis rapide par téléphone ou email. Intervention 24-48h au Puy-en-Velay, Yssingeaux, Langogne. Appelez le 04 71 08 27 09." />
        <link rel="canonical" href={`${DOMAIN}/contact`} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Contact & devis gratuit | CPPB" />
        <meta property="og:description" content="Devis gratuit, intervention rapide 24-48h en Haute-Loire et Lozère." />
        <meta property="og:url" content={`${DOMAIN}/contact`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)} />
      
 <main className="pt-20">
 <section className="py-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600">
 <div className="max-w-6xl mx-auto px-4 text-center text-white">
 <h1 className="text-4xl md:text-5xl font-bold mb-4">Contactez-nous</h1>
 <p className="text-xl text-indigo-100 max-w-3xl mx-auto">Devis gratuit et intervention rapide en Haute-Loire (43) et Lozère (48).</p>
 <SmartPhoneLink className="inline-flex items-center gap-2 mt-6 px-8 py-4 bg-white text-indigo-600 font-bold rounded-full hover:shadow-xl transition-all">
 <Phone className="w-5 h-5" />04 71 08 27 09
            </SmartPhoneLink>
          </div>
        </section>
        <GoogleMapsSection />
        <ContactSection />
      </main>
      
      <Footer />
      <FloatingPhonePortal />
    </div>
  );
};

// Schema.org complet pour SEO
const getHomeSchemas = () => {
  const domain = "https://www.diagnostic-immobilier-haute-loire-43.fr";
  
  // LocalBusiness + ProfessionalService - SCHEMA ENRICHI SEO LOCAL
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "ProfessionalService"],
    "@id": `${domain}/#organization`,
    "name": "Diagnostic Immobilier Haute-Loire 43",
    "alternateName": ["CPPB", "CPPB Diagnostics Immobiliers", "Diagnostic Immobilier Haute-Loire", "Diagnostiqueur immobilier Haute-Loire"],
    "description": "Expert en diagnostics immobiliers obligatoires en Haute-Loire (43) et Lozère (48). DPE, amiante, plomb, gaz, électricité, termites, Carrez, ERP, audit énergétique. Certifié B.2.C. Intervention rapide 24-48h. 44 avis 5 étoiles.",
    "url": domain,
    "logo": `${domain}/logo512.png`,
    "image": [
      `${domain}/logo512.png`,
      "https://customer-assets.emergentagent.com/job_git-port/artifacts/g4v4rmxa_Etat%20h%C3%A9ritier.jpeg"
    ],
    "telephone": "+33471082709",
    "email": "contact@cppb.fr",
    "priceRange": "€€",
    "currenciesAccepted": "EUR",
    "paymentAccepted": "Chèque, Virement bancaire, Espèces",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Rauret-Bas",
      "addressLocality": "Rauret",
      "postalCode": "43340",
      "addressRegion": "Haute-Loire",
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 44.9167,
      "longitude": 3.8667
    },
    "hasMap": "https://g.page/r/CRNrfVfSBrCsEBE/",
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Haute-Loire", "sameAs": "https://fr.wikipedia.org/wiki/Haute-Loire" },
      { "@type": "AdministrativeArea", "name": "Lozère", "sameAs": "https://fr.wikipedia.org/wiki/Loz%C3%A8re_(d%C3%A9partement)" },
      { "@type": "City", "name": "Le Puy-en-Velay", "sameAs": "https://fr.wikipedia.org/wiki/Le_Puy-en-Velay" },
      { "@type": "City", "name": "Yssingeaux" },
      { "@type": "City", "name": "Monistrol-sur-Loire" },
      { "@type": "City", "name": "Brioude" },
      { "@type": "City", "name": "Langogne" },
      { "@type": "City", "name": "Rauret" }
    ],
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "08:00", "closes": "19:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "09:00", "closes": "18:00" }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": "44"
    },
    "review": [
      {
        "@type": "Review",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
        "author": { "@type": "Person", "name": "M. Poncel" },
        "reviewBody": "Service exceptionnel ! Intervenu rapidement pour le DPE de notre appartement au Puy. Rapports clairs et précis."
      },
      {
        "@type": "Review",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
        "author": { "@type": "Person", "name": "Mme Robert" },
        "reviewBody": "Très professionnel et réactif. Diagnostic complet pour la vente de notre maison à Yssingeaux."
      }
    ],
    "sameAs": [
      "https://www.instagram.com/diagnostics_immobilier_43_48/",
      "https://x.com/Diagnostic43",
      "https://fr.linkedin.com/in/diagnostics-immobilier-haute-loire-diagnostics-immobilier-haute-loire-7615831b3",
      "https://g.page/r/CRNrfVfSBrCsEBE/",
      "https://www.facebook.com/profile.php?id=100063654614849"
    ],
    "knowsAbout": [
      "DPE Diagnostic Performance Énergétique",
      "Diagnostic Amiante",
      "Diagnostic Plomb CREP",
      "Diagnostic Gaz",
      "Diagnostic Électricité",
      "Diagnostic Termites",
      "Mesurage Loi Carrez",
      "ERP État des Risques et Pollutions",
      "Audit Énergétique Réglementaire"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Diagnostics Immobiliers Obligatoires",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "DPE - Diagnostic Performance Énergétique", "description": "Évaluation de la consommation énergétique et émissions de gaz à effet de serre. Obligatoire pour vente et location.", "areaServed": "Haute-Loire, Lozère" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Diagnostic Amiante", "description": "Repérage amiante avant vente, avant travaux ou avant démolition. Obligatoire pour biens construits avant 1997.", "areaServed": "Haute-Loire, Lozère" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Diagnostic Plomb CREP", "description": "Constat de Risque d'Exposition au Plomb. Obligatoire pour logements construits avant 1949.", "areaServed": "Haute-Loire, Lozère" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Diagnostic Gaz", "description": "État de l'installation intérieure de gaz. Obligatoire pour installations de plus de 15 ans.", "areaServed": "Haute-Loire, Lozère" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Diagnostic Électricité", "description": "État de l'installation intérieure d'électricité. Obligatoire pour installations de plus de 15 ans.", "areaServed": "Haute-Loire, Lozère" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Diagnostic Termites", "description": "État relatif à la présence de termites. Obligatoire dans les zones à risque.", "areaServed": "Haute-Loire, Lozère" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mesurage Loi Carrez", "description": "Attestation de superficie privative pour lots de copropriété.", "areaServed": "Haute-Loire, Lozère" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "ERP État des Risques et Pollutions", "description": "Information sur les risques naturels, miniers, technologiques, sismiques et radon.", "areaServed": "Haute-Loire, Lozère" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Audit Énergétique Réglementaire", "description": "Audit énergétique obligatoire pour vente de passoires thermiques (F ou G). Propositions de travaux de rénovation.", "areaServed": "Haute-Loire, Lozère" } }
      ]
    },
    "founder": {
      "@type": "Person",
      "name": "T. GIBERT",
      "jobTitle": "Diagnostiqueur immobilier certifié"
    },
    "foundingDate": "2006",
    "legalName": "SARL CPPB",
    "taxID": "FR492270731",
    "slogan": "Expert diagnostics immobiliers certifié B.2.C - Intervention 24-48h"
  };

  // WebSite Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${domain}/#website`,
    "url": domain,
    "name": "CPPB Diagnostics Immobiliers Haute-Loire Lozère",
    "description": "Diagnostics immobiliers obligatoires : DPE, amiante, plomb, gaz, électricité en Haute-Loire (43) et Lozère (48)",
    "publisher": { "@id": `${domain}/#organization` },
    "potentialAction": {
      "@type": "SearchAction",
      "target": { "@type": "EntryPoint", "urlTemplate": `${domain}/?s={search_term_string}` },
      "query-input": "required name=search_term_string"
    }
  };

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": domain },
      { "@type": "ListItem", "position": 2, "name": "Diagnostics", "item": `${domain}/diagnostics` },
      { "@type": "ListItem", "position": 3, "name": "Contact", "item": `${domain}/contact` }
    ]
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "Quels diagnostics sont obligatoires pour une vente ?", "acceptedAnswer": { "@type": "Answer", "text": "Pour une vente immobilière, les diagnostics obligatoires incluent : DPE, amiante (avant 1997), plomb (avant 1949), gaz et électricité (installations +15 ans), termites (zones concernées), ERP, Loi Carrez (copropriété) et assainissement (non raccordé)." } },
      { "@type": "Question", "name": "Combien de temps pour recevoir mes diagnostics ?", "acceptedAnswer": { "@type": "Answer", "text": "Chez CPPB, vous recevez vos rapports de diagnostics sous 48 à 72 heures après l'intervention. En cas d'urgence, nous proposons des interventions sous 24h." } },
      { "@type": "Question", "name": "Intervenez-vous en Lozère ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui, CPPB intervient dans toute la Haute-Loire (43) et une grande partie de la Lozère (48), notamment à Langogne, Auroux, Chaudeyrac, La Bastide-Puylaurent, Grandrieu." } },
      { "@type": "Question", "name": "Quelle est la durée de validité d'un DPE ?", "acceptedAnswer": { "@type": "Answer", "text": "Le DPE (Diagnostic de Performance Énergétique) est valable 10 ans, sauf si des travaux de rénovation énergétique sont réalisés." } },
      { "@type": "Question", "name": "Êtes-vous certifié B.2.C ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui, CPPB est certifié B.2.C pour tous les diagnostics immobiliers obligatoires. Cette certification garantit notre expertise et la conformité de nos rapports." } }
    ]
  };

  return { localBusinessSchema, websiteSchema, breadcrumbSchema, faqSchema };
};

// Main Home Component
const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);
  
  const schemas = getHomeSchemas();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
 <div className="font-sans dark:bg-gray-900">
      {/* SEO Meta Tags et Schema.org */}
      <Helmet>
        <title>Diagnostic Immobilier Haute-Loire (43) & Lozère (48) | CPPB Certifié</title>
        <meta name="description" content="DPE, amiante, plomb, gaz, électricité, ERP, Carrez. Intervention 24-48h au Puy-en-Velay, Yssingeaux, Langogne. Devis gratuit : 04 71 08 27 09." />
        <link rel="canonical" href="https://www.diagnostic-immobilier-haute-loire-43.fr/" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Diagnostic Immobilier Haute-Loire Lozère | CPPB Expert Certifié" />
        <meta property="og:description" content="DPE, amiante, plomb, gaz, électricité. Certifié B.2.C. Devis gratuit 04 71 08 27 09" />
        <meta property="og:url" content="https://www.diagnostic-immobilier-haute-loire-43.fr/" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="CPPB Diagnostics Immobiliers" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Diagnostic Immobilier Haute-Loire Lozère | CPPB" />
        <meta name="twitter:description" content="Expert diagnostics immobiliers certifié B.2.C. Devis gratuit." />
        <meta name="geo.region" content="FR-43" />
        <meta name="geo.placename" content="Le Puy-en-Velay" />
        <script type="application/ld+json">{JSON.stringify(schemas.localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(schemas.websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(schemas.breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(schemas.faqSchema)}</script>
      </Helmet>
      
      <Header 
        scrolled={scrolled} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
        onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)}
      />
      
      <main>
        <HeroSection helpAssistantOpen={helpAssistantOpen} onCloseHelp={() => setHelpAssistantOpen(false)} />
        
        {/* Above the fold - Chargé immédiatement */}
        <ExpertiseSection />
        <ServicesSection />
        
        {/* Below the fold - Lazy loaded with accurate skeleton heights */}
        <LazySection fallback={<SectionSkeleton height="500px" />}>
          <ValiditySection />
        </LazySection>
        
        <LazySection fallback={<SectionSkeleton height="450px" />}>
          <TestimonialsSection />
        </LazySection>
        
        {/* Sections chargées après interaction utilisateur */}
        <InteractionLoad fallback={<SectionSkeleton height="500px" />}>
          <ActualitesSection />
        </InteractionLoad>
        
        <InteractionLoad fallback={<SectionSkeleton height="500px" />}>
          <GoogleMapsSection />
        </InteractionLoad>
        
        <InteractionLoad fallback={<SectionSkeleton height="600px" />}>
          <ContactSection />
        </InteractionLoad>
        
        <InteractionLoad fallback={<SectionSkeleton height="450px" />}>
          <FAQSection />
        </InteractionLoad>
        
        <InteractionLoad fallback={<SectionSkeleton height="200px" />}>
          <AffiliationsSection />
        </InteractionLoad>
        
        {/* Section maillage SEO - liens internes pour Google */}
        <InteractionLoad fallback={<SectionSkeleton height="200px" />}>
          <SEOLinksSection />
        </InteractionLoad>
      </main>

      <Footer />
      <FloatingPhonePortal />
      <HelpCalloutOverlay isAssistantOpen={helpAssistantOpen} />
      {/* StaticWizard - ALL screen sizes */}
      <Suspense fallback={null}>
        <StaticWizard isOpen={helpAssistantOpen} onClose={() => setHelpAssistantOpen(false)} />
      </Suspense>
      <IdleLoad fallback={null} delay={2000}>
        <ExitIntentPopup />
      </IdleLoad>
      <IdleLoad fallback={null} delay={1000}>
        <ScrollDepthTracker />
      </IdleLoad>
    </div>
  );
};

// ============================================
// PAGE VILLE GÉOLOCALISÉE - SEO LOCAL
// ============================================
const VillePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);

  const ville = villesData.find(v => v.slug === slug);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (ville) {
      trackPageView(`ville_${ville.slug}`, `Diagnostic Immobilier ${ville.name}`);
    }
  }, [ville]);

  if (!ville) {
    return (
 <div className="min-h-screen flex items-center justify-center dark:bg-gray-900">
 <div className="text-center">
 <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Ville non trouvée</h1>
 <Link to="/" className="text-indigo-600 hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  const metaTitle = `Diagnostic Immobilier ${ville.name} (${ville.cp}) | DPE ${ville.dept} ${ville.deptNum} | CPPB Expert Certifié`;
  const metaDescription = `⭐ Diagnostic immobilier à ${ville.name} (${ville.cp}). DPE, Amiante, Plomb, Gaz, Électricité, Termites, Carrez, ERP. Expert certifié B.2.C en ${ville.dept}. Devis GRATUIT ☎ 04 71 08 27 09. Intervention 24-48h.`;
  const metaKeywords = `diagnostic immobilier ${ville.name.toLowerCase()}, DPE ${ville.name.toLowerCase()}, DPE ${ville.cp}, diagnostiqueur ${ville.name.toLowerCase()}, diagnostic immobilier ${ville.cp}, diagnostic amiante ${ville.name.toLowerCase()}, diagnostic plomb ${ville.name.toLowerCase()}, diagnostic gaz ${ville.name.toLowerCase()}, diagnostic électricité ${ville.name.toLowerCase()}`;

  return (
 <div className="font-sans dark:bg-gray-900 min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <link rel="canonical" href={`https://www.diagnostic-immobilier-haute-loire-43.fr/diagnostic-immobilier/${ville.slug}`} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://www.diagnostic-immobilier-haute-loire-43.fr/diagnostic-immobilier/${ville.slug}`} />
        <meta property="og:type" content="website" />
        <meta name="geo.placename" content={`${ville.name}, ${ville.dept}, France`} />
        <meta name="geo.region" content={`FR-${ville.deptNum}`} />
      </Helmet>

      <Header scrolled={scrolled} mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} onHelpClick={() => setHelpAssistantOpen(!helpAssistantOpen)} />

 <main className="pt-20">
        {/* Hero */}
 <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 py-20">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
 <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
 <ArrowLeft className="w-5 h-5" />
                Retour
              </button>

 <div className="flex items-center gap-4 mb-6">
 <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
 <MapPin className="w-8 h-8 text-white" />
                </div>
                <div>
 <span className="text-indigo-200 text-sm font-medium">{ville.dept} ({ville.deptNum})</span>
 <h1 className="font-bold text-3xl sm:text-4xl text-white">Diagnostic Immobilier {ville.name}</h1>
                </div>
              </div>

 <p className="text-xl text-indigo-100/90 leading-relaxed mb-8">
                Expert en diagnostics immobiliers à <strong>{ville.name} ({ville.cp})</strong>. 
                CPPB intervient pour tous vos diagnostics obligatoires : DPE, Amiante, Plomb, Gaz, Électricité, Termites, Loi Carrez, ERP.
                {ville.hasBureau && " Notre bureau est situé à Langogne."}
                {ville.isChefLieu && ` ${ville.name} est le chef-lieu du département.`}
              </p>

 <div className="flex flex-wrap gap-4">
 <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white">
 <MapPin className="w-4 h-4 inline mr-2" />
                  {ville.cp} {ville.name}
                </div>
                <SmartPhoneLink onClick={() => trackPhoneClick(`ville_${ville.slug}`)}>
 <Button className="bg-white text-indigo-600 hover:bg-indigo-50 font-bold rounded-full">
 <Phone className="w-5 h-5 mr-2" />
                    04 71 08 27 09
                  </Button>
                </SmartPhoneLink>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services disponibles */}
 <section className="py-16 bg-white dark:bg-gray-900">
 <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-8 text-center">
                Nos diagnostics immobiliers à {ville.name}
              </h2>

 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/diagnostics/${service.slug}`}
 className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-800 rounded-2xl p-6 hover:shadow-lg hover:shadow-indigo-100 dark:hover:shadow-gray-900 transition-all group"
                  >
 <div className="flex items-center gap-4 mb-4">
 <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center ">
 <service.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
 <h3 className="font-bold text-lg text-gray-800 dark:text-white">{service.name}</h3>
 <span className="text-sm text-indigo-600 dark:text-indigo-400">{service.required}</span>
                      </div>
                    </div>
 <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2">{service.description}</p>
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pourquoi CPPB */}
 <section className="py-16 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-8 text-center">
                Pourquoi choisir CPPB à {ville.name} ?
              </h2>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg">
 <CheckCircle className="w-8 h-8 text-green-500 mb-4" />
 <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Expert certifié B.2.C</h3>
 <p className="text-gray-600 dark:text-gray-300">Certification reconnue, gage de qualité et de professionnalisme pour vos diagnostics.</p>
                </div>
 <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg">
 <Clock className="w-8 h-8 text-indigo-500 mb-4" />
 <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Intervention rapide 24-48h</h3>
 <p className="text-gray-600 dark:text-gray-300">Besoin urgent ? Nous intervenons rapidement à {ville.name} et dans tout le {ville.dept}.</p>
                </div>
 <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg">
 <Star className="w-8 h-8 text-yellow-500 mb-4" />
 <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2"><a href="https://g.page/r/CaB7lwVbLS-zEBE/" target="_blank" rel="noopener noreferrer" className="reviews-link">44 avis 5 étoiles Google</a></h3>
 <p className="text-gray-600 dark:text-gray-300">Nos clients nous recommandent. Découvrez leurs témoignages sur Google.</p>
                </div>
 <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg">
 <FileCheck className="w-8 h-8 text-purple-500 mb-4" />
 <h3 className="font-bold text-lg text-gray-800 dark:text-white mb-2">Rapport sous 72h</h3>
 <p className="text-gray-600 dark:text-gray-300">Recevez votre rapport complet rapidement pour finaliser votre transaction.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
 <section className="py-16 bg-white dark:bg-gray-900">
 <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-4">
                Besoin d'un diagnostic immobilier à {ville.name} ?
              </h2>
 <p className="text-gray-600 dark:text-gray-300 mb-8">
                Demandez votre devis gratuit dès maintenant. Intervention rapide dans tout le {ville.dept} ({ville.deptNum}).
              </p>
 <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <SmartPhoneLink onClick={() => trackPhoneClick(`ville_${ville.slug}_cta`)}>
 <Button className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-bold rounded-full px-8 py-4 hover:shadow-lg hover:shadow-indigo-300 transition-all">
 <Phone className="w-5 h-5 mr-2" />
                    04 71 08 27 09
                  </Button>
                </SmartPhoneLink>
                <a href="#devis">
 <Button variant="outline" className="border-2 border-indigo-600 text-indigo-600 dark:text-indigo-400 font-bold rounded-full px-8 py-4 hover:bg-indigo-50 dark:hover:bg-gray-800 transition-all">
 <Send className="w-5 h-5 mr-2" />
                    Demander un devis
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Autres villes */}
 <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
 <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
 <h2 className="font-bold text-2xl text-gray-800 dark:text-white mb-8 text-center">
                Nous intervenons également dans ces villes
              </h2>
 <div className="flex flex-wrap justify-center gap-3">
                {villesData.filter(v => v.slug !== slug && v.deptNum === ville.deptNum).slice(0, 8).map((v) => (
                  <Link
                    key={v.slug}
                    to={`/diagnostic-immobilier/${v.slug}`}
 className="px-4 py-2 bg-white dark:bg-gray-800 rounded-full text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all text-sm"
                  >
                    {v.name} ({v.cp})
                  </Link>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingPhonePortal />
      <HelpCalloutOverlay isAssistantOpen={helpAssistantOpen} />
      <StaticWizard isOpen={helpAssistantOpen} onClose={() => setHelpAssistantOpen(false)} />
      <ScrollDepthTracker />
    </div>
  );
};

// ============================================
// PAGES LOCALES SEO - 3 IMPLANTATIONS GOOGLE BUSINESS
// ============================================
// ============================================
// DONNÉES SEO LOCALES - DOMINATION GOOGLE LOCAL
// ============================================
const implantations = {
  "rauret-43340": {
    name: "Rauret", fullName: "Rauret (Siège social)", cp: "43340", dept: "Haute-Loire", deptNum: "43",
    googleMapsLink: "https://maps.app.goo.gl/HPxAytfwd4MH6yx58",
    description: "Siège social de CPPB Diagnostics Immobilier à Rauret en Haute-Loire. Point central pour nos interventions dans le sud de la Haute-Loire et le nord de la Lozère.",
    metaTitle: "Diagnostic Immobilier Rauret 43340 | CPPB Siège Social Haute-Loire | DPE Amiante Plomb",
    metaDescription: "⭐ CPPB Siège social Rauret 43340 - Expert diagnostic immobilier certifié B.2.C. DPE, Amiante, Plomb, Gaz, Électricité. Devis GRATUIT ☎ 04 71 08 27 09",
    zones: ["Cayres", "Pradelles", "Saugues", "Langeac", "Costaros", "Le Bouchet-Saint-Nicolas"],
    lat: 44.9167, lng: 3.7500,
    content: `Notre siège social de Rauret est stratégiquement situé au cœur de la Haute-Loire (43), à proximité du Lac du Bouchet et du Lac d'Issarlès. Cette position centrale nous permet d'intervenir rapidement dans tout le sud du département ainsi que dans le nord de la Lozère limitrophe.

Rauret, petite commune du canton de Pradelles, bénéficie d'un patrimoine naturel exceptionnel. Nos diagnostiqueurs certifiés B.2.C connaissent parfaitement les spécificités du bâti local : maisons en pierre volcanique, fermes rénovées, chalets de montagne et résidences secondaires.

Nous intervenons régulièrement à Cayres (43510), Pradelles (43420), Saugues (43170), Langeac (43300), Costaros (43490) et Le Bouchet-Saint-Nicolas (43510). Notre expertise couvre tous les types de biens : maisons individuelles, appartements, locaux commerciaux et terrains.

Pour vendre ou louer un bien immobilier à Rauret ou dans les environs, le Dossier de Diagnostics Techniques (DDT) est obligatoire. Il comprend selon votre situation : DPE (obligatoire depuis 2006, validité 10 ans), diagnostic amiante (biens avant 1997), diagnostic plomb CREP (logements avant 1949), diagnostics gaz et électricité (installations +15 ans), ERP et mesurage Loi Carrez.

Notre certification B.2.C garantit la conformité de nos rapports aux normes en vigueur. Intervention sous 24-48h, rapport détaillé sous 72h.`,
    faq: [
      { q: "Où se situe le siège social de CPPB ?", a: "Le siège social de CPPB est situé à Rauret Bas (43340), en Haute-Loire. Cette implantation centrale permet des interventions rapides dans le sud du département et le nord de la Lozère." },
      { q: "Quelles communes autour de Rauret sont couvertes ?", a: "Depuis Rauret, nous intervenons à Cayres, Pradelles, Saugues, Langeac, Costaros, Le Bouchet-Saint-Nicolas et toutes les communes dans un rayon de 40 km." },
      { q: "Quels diagnostics pour vendre à Rauret ?", a: "Pour vendre à Rauret (43340) : DPE, amiante (si avant 1997), plomb (si avant 1949), gaz/électricité (si +15 ans), ERP, et Carrez pour copropriétés." },
      { q: "Quel délai d'intervention à Rauret ?", a: "Intervention sous 24-48h, rapport sous 72h. Contactez le 04 71 08 27 09." },
      { q: "Les diagnostics sont-ils obligatoires pour une location à Rauret ?", a: "Oui, pour louer un bien à Rauret vous devez fournir : DPE, diagnostic plomb (si avant 1949), amiante (si avant 1997), gaz et électricité (si installations +15 ans), et ERP. Les logements classés G sont interdits à la location depuis janvier 2025." }
    ]
  },
  "le-puy-en-velay": {
    name: "Le Puy-en-Velay", fullName: "Le Puy-en-Velay (Bureau principal)", cp: "43000", dept: "Haute-Loire", deptNum: "43",
    googleMapsLink: "https://maps.app.goo.gl/49h9z8yZszx5wNSY9",
    description: "Bureau principal CPPB au Puy-en-Velay, chef-lieu de la Haute-Loire. Intervention rapide pour tous vos diagnostics immobiliers dans l'agglomération ponote.",
    metaTitle: "Diagnostic Immobilier Le Puy-en-Velay 43000 | CPPB Expert DPE Haute-Loire | Devis Gratuit",
    metaDescription: "⭐ Diagnostic immobilier Le Puy-en-Velay 43000 - CPPB expert certifié B.2.C. DPE obligatoire, Amiante, Plomb, Gaz, Électricité. 44 avis 5/5. Devis GRATUIT ☎ 04 71 08 27 09",
    zones: ["Espaly-Saint-Marcel", "Brives-Charensac", "Chadrac", "Vals-près-le-Puy", "Polignac", "Saint-Germain-Laprade", "Aiguilhe"],
    lat: 45.0428, lng: 3.8853,
    content: `Le Puy-en-Velay, préfecture de la Haute-Loire et ville classée au patrimoine mondial de l'UNESCO pour ses chemins de Saint-Jacques-de-Compostelle, est au cœur de notre zone d'intervention principale. Notre bureau ponot nous permet d'assurer des diagnostics immobiliers rapides dans toute l'agglomération.

Cette ville historique de 20 000 habitants présente un patrimoine bâti remarquable : immeubles anciens du centre-ville, maisons bourgeoises du XIXe siècle, constructions en pierre volcanique et quartiers résidentiels modernes. Nos diagnostiqueurs maîtrisent les spécificités de chaque type de bien.

Nous intervenons quotidiennement au Puy-en-Velay (43000) et dans toute son agglomération : Espaly-Saint-Marcel, Brives-Charensac, Chadrac, Vals-près-le-Puy, Polignac, Saint-Germain-Laprade, Aiguilhe. Notre connaissance du marché immobilier local est un atout pour les propriétaires, agences immobilières et notaires.

Le marché immobilier ponot est dynamique avec un prix moyen de 1 500 €/m² pour les appartements et 1 800 €/m² pour les maisons. Pour toute transaction, le DDT complet est obligatoire. Nos packs diagnostics vente et location sont adaptés aux besoins des vendeurs et bailleurs de l'agglomération.

Depuis 2025, les logements classés G au DPE sont interdits à la location. En 2028, ce sera le tour des logements classés F. Anticipez vos travaux de rénovation énergétique grâce à notre expertise en audit énergétique.`,
    faq: [
      { q: "CPPB intervient-il au Puy-en-Velay ?", a: "Oui, CPPB dispose d'un bureau principal au Puy-en-Velay (43000). Nous intervenons dans toute l'agglomération ponote : Espaly, Brives-Charensac, Chadrac, Vals-près-le-Puy, Polignac." },
      { q: "Combien coûte un DPE au Puy-en-Velay ?", a: "Le tarif varie selon la surface et le type de bien. Devis gratuit au 04 71 08 27 09. Packs diagnostics disponibles à prix préférentiel." },
      { q: "Diagnostics obligatoires pour louer au Puy ?", a: "Pour louer : DPE, amiante (si avant 1997), plomb CREP (si avant 1949), gaz/électricité (si +15 ans), et ERP. Les logements classés G sont interdits à la location depuis 2025." },
      { q: "Délai pour un diagnostic au Puy-en-Velay ?", a: "Intervention sous 24-48h, rapport sous 72h. Créneaux prioritaires pour urgences notaire." },
      { q: "Quels quartiers du Puy-en-Velay couvrez-vous ?", a: "Nous intervenons dans tous les quartiers : centre historique, Corsac, Val Vert, Guitard, Taulhac, Ours-Mons, ainsi que les communes limitrophes d'Espaly, Brives-Charensac et Chadrac." }
    ]
  },
  "langogne-48300": {
    name: "Langogne", fullName: "Langogne (Bureau Lozère)", cp: "48300", dept: "Lozère", deptNum: "48",
    googleMapsLink: "https://maps.app.goo.gl/Ku7kh3jdoz4bx234A",
    description: "Bureau CPPB à Langogne, porte d'entrée de la Lozère. Intervention rapide pour les diagnostics immobiliers dans le nord du département 48.",
    metaTitle: "Diagnostic Immobilier Langogne 48300 | CPPB Expert DPE Lozère | Devis Gratuit",
    metaDescription: "⭐ Diagnostic immobilier Langogne 48300 Lozère - CPPB expert certifié B.2.C. DPE, Amiante, Plomb, Gaz, Électricité. Devis GRATUIT ☎ 04 71 08 27 09",
    zones: ["Auroux", "Chaudeyrac", "La Bastide-Puylaurent", "Grandrieu", "Châteauneuf-de-Randon", "Luc", "Naussac-Fontanes"],
    lat: 44.7333, lng: 3.8500,
    content: `Langogne, sous-préfecture de la Lozère située aux confins du Gévaudan et du Velay, est notre point d'ancrage pour le département 48. Cette ville de 3 000 habitants, bordée par le Lac de Naussac, constitue un carrefour stratégique pour nos interventions en Lozère.

Le patrimoine bâti lozérien présente des caractéristiques uniques : maisons en granite, fermes cévenoles, constructions traditionnelles adaptées au climat montagnard. Nos diagnostiqueurs certifiés connaissent parfaitement ces spécificités architecturales.

Depuis Langogne, nous couvrons tout le nord de la Lozère : Auroux (48600), Chaudeyrac (48170), La Bastide-Puylaurent (48250), Grandrieu (48600), Châteauneuf-de-Randon (48170), Luc (48250) et Naussac-Fontanes (48300). Notre expertise s'étend également aux communes limitrophes de Haute-Loire.

Le marché immobilier lozérien offre des opportunités attractives avec des prix moyens inférieurs à 1 200 €/m². Que vous vendiez une résidence principale, une résidence secondaire ou un bien locatif, CPPB réalise l'ensemble des diagnostics obligatoires du DDT.

La Lozère, département le moins peuplé de France, attire de nombreux acquéreurs en quête de nature et de tranquillité. Nos rapports de diagnostics conformes aux normes nationales facilitent vos transactions avec les notaires et agents immobiliers locaux.`,
    faq: [
      { q: "CPPB intervient-il en Lozère ?", a: "Oui, CPPB dispose d'un bureau à Langogne (48300) pour des interventions rapides dans le nord de la Lozère : Auroux, Chaudeyrac, La Bastide-Puylaurent, Grandrieu." },
      { q: "Diagnostics obligatoires pour vendre à Langogne ?", a: "Pour vendre à Langogne (48300) : DPE, amiante (si avant 1997), plomb CREP (si avant 1949), gaz/électricité (si +15 ans), ERP, et Carrez pour copropriétés." },
      { q: "Prix d'un DPE à Langogne ?", a: "Le prix dépend de la surface et du type de bien. Devis gratuit au 04 71 08 27 09. Tarifs compétitifs pour la Lozère." },
      { q: "Délai pour diagnostic à Langogne ?", a: "Intervention sous 24-48h depuis notre bureau de Langogne. Rapport sous 72h." },
      { q: "Couvrez-vous tout le département de la Lozère ?", a: "Nous couvrons principalement le nord de la Lozère depuis notre bureau de Langogne : secteurs de Langogne, Grandrieu, Châteauneuf-de-Randon, La Bastide-Puylaurent. Pour le sud du département (Mende, Florac), contactez-nous pour vérifier notre disponibilité." }
    ]
  },
  "yssingeaux-43200": {
    name: "Yssingeaux", fullName: "Yssingeaux (Bureau Nord Haute-Loire)", cp: "43200", dept: "Haute-Loire", deptNum: "43",
    googleMapsLink: "https://maps.app.goo.gl/49h9z8yZszx5wNSY9",
    description: "Bureau CPPB à Yssingeaux pour le nord de la Haute-Loire. Intervention rapide pour diagnostics immobiliers dans le Meygal et vers Saint-Étienne.",
    metaTitle: "Diagnostic Immobilier Yssingeaux 43200 | CPPB Expert DPE Haute-Loire Nord | Devis Gratuit",
    metaDescription: "⭐ Diagnostic immobilier Yssingeaux 43200 - CPPB expert certifié B.2.C. DPE, Amiante, Plomb, Gaz, Électricité. Proche Saint-Étienne. Devis GRATUIT ☎ 04 71 08 27 09",
    zones: ["Le Chambon-sur-Lignon", "Tence", "Saint-Julien-Chapteuil", "Retournac", "Sainte-Sigolène", "Monistrol-sur-Loire"],
    lat: 45.1417, lng: 4.1250,
    content: `Yssingeaux, sous-préfecture de la Haute-Loire située dans le massif du Meygal, est notre bureau de référence pour le nord du département. Cette ville dynamique de 7 500 habitants constitue un pôle économique majeur entre Le Puy-en-Velay et Saint-Étienne.

Le pays yssingelais présente un patrimoine immobilier diversifié : fermes traditionnelles du Meygal, maisons de village en pierre, constructions plus récentes des zones résidentielles. Notre expertise couvre tous ces types de biens avec une connaissance approfondie du marché local.

Depuis Yssingeaux, nous intervenons dans tout le nord de la Haute-Loire : Le Chambon-sur-Lignon (43400), Tence (43190), Saint-Julien-Chapteuil (43260), Retournac (43130), Sainte-Sigolène (43600) et jusqu'à Monistrol-sur-Loire (43120). Notre proximité avec la Loire facilite également les interventions vers Firminy et Saint-Étienne.

Le marché immobilier yssingelais est particulièrement actif, porté par l'attractivité du cadre de vie et la proximité de la métropole stéphanoise. Les prix moyens oscillent entre 1 400 et 1 800 €/m², avec une forte demande pour les maisons individuelles.

Le secteur touristique du Meygal (Lac de Lavalette, stations de ski) génère également des transactions de résidences secondaires nécessitant des diagnostics complets avant vente ou mise en location saisonnière.`,
    faq: [
      { q: "CPPB intervient-il à Yssingeaux ?", a: "Oui, CPPB dispose d'un bureau à Yssingeaux (43200) pour le nord de la Haute-Loire. Nous couvrons tout le pays yssingelais : Le Chambon-sur-Lignon, Tence, Saint-Julien-Chapteuil, Retournac, Sainte-Sigolène." },
      { q: "Diagnostics obligatoires pour vendre à Yssingeaux ?", a: "Pour vendre à Yssingeaux (43200) : DPE (validité 10 ans), amiante (biens avant 1997), plomb CREP (logements avant 1949), gaz et électricité (installations +15 ans), ERP et Carrez pour copropriétés." },
      { q: "Combien coûte un DPE à Yssingeaux ?", a: "Le tarif DPE varie selon la surface et le type de bien. Devis gratuit au 04 71 08 27 09. Packs diagnostics complets à prix avantageux." },
      { q: "Intervenez-vous vers Saint-Étienne ?", a: "Notre zone d'intervention principale est la Haute-Loire, mais nous pouvons intervenir vers Firminy et le sud de Saint-Étienne Métropole. Contactez-nous pour vérifier la disponibilité." },
      { q: "Délai d'intervention à Yssingeaux ?", a: "Intervention sous 24-48h, rapport de diagnostics sous 72h. Créneaux urgents disponibles pour les notaires." }
    ]
  },
  "brioude-43100": {
    name: "Brioude", fullName: "Brioude (Bureau Brivadois)", cp: "43100", dept: "Haute-Loire", deptNum: "43",
    googleMapsLink: "https://maps.app.goo.gl/49h9z8yZszx5wNSY9",
    description: "Bureau CPPB à Brioude pour le Brivadois. Intervention rapide pour diagnostics immobiliers dans la vallée de l'Allier.",
    metaTitle: "Diagnostic Immobilier Brioude 43100 | CPPB Expert DPE Brivadois | Devis Gratuit",
    metaDescription: "⭐ Diagnostic immobilier Brioude 43100 Haute-Loire - CPPB expert certifié B.2.C. DPE, Amiante, Plomb, Gaz, Électricité. Vallée Allier. Devis GRATUIT ☎ 04 71 08 27 09",
    zones: ["Paulhaguet", "Lavoûte-Chilhac", "Langeac", "Sainte-Florine", "Blesle", "Vieille-Brioude"],
    lat: 45.2944, lng: 3.3856,
    content: `Brioude, troisième ville de Haute-Loire avec sa célèbre basilique Saint-Julien, est notre point d'ancrage pour le Brivadois et la vallée de l'Allier. Cette cité de caractère aux 6 500 habitants présente un riche patrimoine architectural nécessitant une expertise diagnostic pointue.

La vallée de l'Allier, classée site Natura 2000, traverse un territoire au bâti varié : maisons anciennes du centre historique de Brioude, fermes traditionnelles du Brivadois, constructions modernes des zones périurbaines. Nos diagnostiqueurs certifiés B.2.C maîtrisent ces différentes typologies.

Depuis Brioude, nous couvrons tout le Brivadois : Paulhaguet (43230), Lavoûte-Chilhac (43380), Langeac (43300), Sainte-Florine (43250), Blesle (43450) et Vieille-Brioude (43100). Notre expertise s'étend également vers le nord jusqu'à Issoire et le sud vers Langeac.

Le marché immobilier brivadois offre des opportunités intéressantes avec des prix moyens autour de 1 300 €/m². La proximité de l'A75 et de la gare SNCF renforce l'attractivité de ce territoire pour les acquéreurs en quête d'un cadre de vie préservé.

Le patrimoine bâti ancien de Brioude (nombreux immeubles du XVIIIe et XIXe siècle) nécessite souvent des diagnostics amiante et plomb. Notre expertise garantit des rapports conformes pour sécuriser vos transactions.`,
    faq: [
      { q: "CPPB intervient-il à Brioude ?", a: "Oui, CPPB dispose d'un bureau à Brioude (43100) pour le Brivadois. Nous intervenons dans toute la vallée de l'Allier : Paulhaguet, Lavoûte-Chilhac, Langeac, Sainte-Florine, Blesle." },
      { q: "Quels diagnostics pour vendre à Brioude ?", a: "Pour vendre à Brioude (43100) : DPE obligatoire, amiante (si avant 1997), plomb CREP (si avant 1949), gaz et électricité (si +15 ans), ERP et Carrez pour copropriétés." },
      { q: "Prix diagnostic immobilier à Brioude ?", a: "Tarifs compétitifs adaptés au marché brivadois. Devis gratuit au 04 71 08 27 09. Packs diagnostics vente et location disponibles." },
      { q: "Délai pour diagnostic à Brioude ?", a: "Intervention sous 24-48h, rapport sous 72h. Disponibilité pour urgences notariales." },
      { q: "Le centre historique de Brioude nécessite-t-il des diagnostics spécifiques ?", a: "Oui, les immeubles anciens du centre de Brioude (souvent avant 1949) nécessitent généralement un diagnostic plomb CREP et souvent un diagnostic amiante. Notre expertise du bâti ancien garantit des rapports précis." }
    ]
  },
  "monistrol-sur-loire-43120": {
    name: "Monistrol-sur-Loire", fullName: "Monistrol-sur-Loire (Bureau Est)", cp: "43120", dept: "Haute-Loire", deptNum: "43",
    googleMapsLink: "https://maps.app.goo.gl/49h9z8yZszx5wNSY9",
    description: "Bureau CPPB à Monistrol-sur-Loire pour l'est de la Haute-Loire. Intervention rapide vers Firminy et la vallée de l'Ondaine.",
    metaTitle: "Diagnostic Immobilier Monistrol-sur-Loire 43120 | CPPB Expert DPE | Devis Gratuit",
    metaDescription: "⭐ Diagnostic immobilier Monistrol-sur-Loire 43120 - CPPB expert certifié B.2.C. DPE, Amiante, Plomb, Gaz, Électricité. Proche Firminy. Devis GRATUIT ☎ 04 71 08 27 09",
    zones: ["Sainte-Sigolène", "Bas-en-Basset", "Aurec-sur-Loire", "Saint-Didier-en-Velay", "Saint-Just-Malmont", "Pont-Salomon"],
    lat: 45.2936, lng: 4.1722,
    content: `Monistrol-sur-Loire, ville de 9 000 habitants aux portes de la métropole stéphanoise, est notre bureau de référence pour l'est de la Haute-Loire. Sa situation stratégique sur l'axe Le Puy - Saint-Étienne en fait un pôle économique dynamique.

Le secteur monistrolien présente un marché immobilier très actif, porté par la proximité de Saint-Étienne et la qualité de vie préservée. Le patrimoine bâti mêle constructions anciennes du bourg historique et lotissements résidentiels récents.

Depuis Monistrol-sur-Loire, nous intervenons dans tout l'est du département : Sainte-Sigolène (43600), Bas-en-Basset (43210), Aurec-sur-Loire (43110), Saint-Didier-en-Velay (43140), Saint-Just-Malmont (43240) et Pont-Salomon (43330). Notre zone s'étend jusqu'à Firminy (42700) et la vallée de l'Ondaine.

Le marché immobilier local est parmi les plus dynamiques de Haute-Loire, avec des prix moyens entre 1 600 et 2 200 €/m². La demande est forte pour les maisons individuelles, les appartements en centre-ville et les terrains constructibles.

La présence industrielle historique (plasturgie, textile) implique une vigilance particulière sur les diagnostics amiante pour les biens construits avant 1997. Notre certification B.2.C garantit des rapports conformes aux réglementations en vigueur.`,
    faq: [
      { q: "CPPB intervient-il à Monistrol-sur-Loire ?", a: "Oui, CPPB dispose d'un bureau à Monistrol-sur-Loire (43120) pour l'est de la Haute-Loire. Nous couvrons : Sainte-Sigolène, Bas-en-Basset, Aurec-sur-Loire, Saint-Didier-en-Velay, Saint-Just-Malmont." },
      { q: "Diagnostics obligatoires pour vendre à Monistrol ?", a: "Pour vendre à Monistrol-sur-Loire (43120) : DPE, amiante (si avant 1997), plomb CREP (si avant 1949), gaz et électricité (si +15 ans), ERP et Carrez pour copropriétés." },
      { q: "Intervenez-vous à Firminy ?", a: "Oui, depuis notre bureau de Monistrol nous intervenons également à Firminy (42700) et dans la vallée de l'Ondaine. Contactez-nous au 04 71 08 27 09." },
      { q: "Prix DPE à Monistrol-sur-Loire ?", a: "Tarifs adaptés au marché local. Devis gratuit au 04 71 08 27 09. Packs diagnostics vente et location à prix préférentiel." },
      { q: "Délai pour diagnostic à Monistrol ?", a: "Intervention sous 24-48h, rapport sous 72h. Disponibilité pour créneaux urgents." }
    ]
  }
};

const LocalPage = ({ locKey }) => {
  const [scrolled, setScrolled] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  
  const loc = implantations[locKey];
  const otherLocs = Object.entries(implantations).filter(([k]) => k !== locKey);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [locKey]);
  
  if (!loc) return <div className="min-h-screen flex items-center justify-center">Page non trouvée</div>;

  const localSchema = {
    "@context": "https://schema.org", "@type": "LocalBusiness",
    "@id": `https://www.diagnostic-immobilier-haute-loire-43.fr/diagnostic-immobilier-${locKey}#business`,
    "name": `CPPB Diagnostics Immobilier - ${loc.name}`, "description": loc.description,
    "telephone": "+33471082709", "email": "contact@cppb.fr",
    "address": { "@type": "PostalAddress", "addressLocality": loc.name, "postalCode": loc.cp, "addressRegion": loc.dept, "addressCountry": "FR" },
    "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"], "opens": "08:00", "closes": "22:00" },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "144" },
    "sameAs": [loc.googleMapsLink]
  };
  
  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": loc.faq.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
  };

  return (
 <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>{loc.metaTitle}</title>
        <meta name="description" content={loc.metaDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://www.diagnostic-immobilier-haute-loire-43.fr/diagnostic-immobilier-${locKey}`} />
        <script type="application/ld+json">{JSON.stringify(localSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

 <header className={`fixed top-0 left-0 right-0 z-50 transition-all ${scrolled ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg' : 'bg-white/90 dark:bg-gray-900/90'}`}>
 <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
 <Link to="/" className="flex items-center gap-2">
 <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center"><Home className="w-5 h-5 text-white" /></div>
 <span className="font-bold text-gray-800 dark:text-white">CPPB</span>
          </Link>
 <SmartPhoneLink className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full font-medium">
 <Phone className="w-4 h-4" /><span className="hidden sm:inline">04 71 08 27 09</span>
          </SmartPhoneLink>
        </div>
      </header>

 <section className="pt-24 pb-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500">
 <div className="max-w-6xl mx-auto px-4 text-center text-white">
 <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
 <MapPin className="w-4 h-4" /><span className="text-sm font-medium">{loc.dept} ({loc.deptNum})</span>
          </div>
 <h1 className="text-3xl md:text-5xl font-bold mb-4">Diagnostic Immobilier<br /><span className="text-yellow-300">{loc.name}</span> ({loc.cp})</h1>
 <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">{loc.description}</p>
 <div className="flex flex-col sm:flex-row gap-4 justify-center">
 <SmartPhoneLink className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-indigo-600 font-bold rounded-full hover:shadow-xl transition-all">
 <Phone className="w-5 h-5" />Appeler : 04 71 08 27 09
            </SmartPhoneLink>
 <a href={loc.googleMapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/20 text-white font-semibold rounded-full hover:bg-white/30 transition-all">
 <ExternalLink className="w-5 h-5" />Voir sur Google Maps
            </a>
          </div>
        </div>
      </section>

 <section className="py-12 -mt-8">
 <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-3 gap-4">
 <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
 <Phone className="w-8 h-8 text-indigo-600 mb-3" />
 <h3 className="font-semibold text-gray-800 dark:text-white mb-2">Téléphone</h3>
 <SmartPhoneLink className="text-indigo-600 font-medium">04 71 08 27 09</SmartPhoneLink>
          </div>
 <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
 <Mail className="w-8 h-8 text-purple-600 mb-3" />
 <h3 className="font-semibold text-gray-800 dark:text-white mb-2">Email</h3>
 <a href="mailto:contact@cppb.fr" className="text-purple-600 font-medium">contact@cppb.fr</a>
          </div>
 <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
 <Clock className="w-8 h-8 text-pink-600 mb-3" />
 <h3 className="font-semibold text-gray-800 dark:text-white mb-2">Horaires</h3>
 <p className="text-gray-600 dark:text-gray-300">Lun-Sam : 8h - 22h</p>
          </div>
        </div>
      </section>

 <section className="py-16 bg-white dark:bg-gray-800">
 <div className="max-w-6xl mx-auto px-4">
 <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-8 text-center">Nos diagnostics à {loc.name}</h2>
 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[{n:"DPE",i:Thermometer},{n:"Amiante",i:Shield},{n:"Plomb",i:Droplets},{n:"Gaz",i:Flame},{n:"Électricité",i:Zap},{n:"Termites",i:Bug},{n:"ERP",i:AlertTriangle},{n:"Carrez",i:Home}].map(s => (
 <div key={s.n} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
 <s.i className="w-8 h-8 text-indigo-500 mb-2" />
 <h3 className="font-semibold text-gray-800 dark:text-white">{s.n}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

 <section className="py-16 bg-gray-50 dark:bg-gray-900">
 <div className="max-w-6xl mx-auto px-4">
 <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-8 text-center">Communes desservies depuis {loc.name}</h2>
 <div className="flex flex-wrap justify-center gap-3">
 {loc.zones.map(z => <span key={z} className="px-4 py-2 bg-white dark:bg-gray-800 rounded-full border text-gray-700 dark:text-gray-300">{z}</span>)}
          </div>
        </div>
      </section>

 <section className="py-16 bg-white dark:bg-gray-800 text-center">
 <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Notre fiche Google Maps</h2>
 <a href={loc.googleMapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-full hover:shadow-xl">
 <MapPin className="w-5 h-5" />Voir la fiche - {loc.name}<ExternalLink className="w-4 h-4" />
        </a>
 <div className="mt-4 flex justify-center gap-1 text-yellow-500">{[...Array(5)].map((_,i) => <Star key={i} className="w-5 h-5 fill-current" />)}<a href="https://g.page/r/CaB7lwVbLS-zEBE/" target="_blank" rel="noopener noreferrer" className="reviews-link text-gray-600 ml-2">44 avis</a></div>
      </section>

 <section className="py-16 bg-gray-50 dark:bg-gray-900">
 <div className="max-w-4xl mx-auto px-4">
 <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-8 text-center">Questions fréquentes - {loc.name}</h2>
 <div className="space-y-3">
            {loc.faq.map((f,i) => (
 <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border overflow-hidden">
 <button onClick={() => setFaqOpen(faqOpen===i?null:i)} className="w-full px-5 py-4 flex justify-between items-center text-left">
 <span className="font-medium text-gray-800 dark:text-white">{f.q}</span>
 {faqOpen===i ? <ChevronUp className="w-5 h-5 text-indigo-500" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </button>
 {faqOpen===i && <div className="px-5 pb-4 text-gray-600 dark:text-gray-300">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTENU SEO RICHE - 1000+ mots */}
      {loc.content && (
 <section className="py-16 bg-white dark:bg-gray-800">
 <div className="max-w-4xl mx-auto px-4">
 <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-8">Diagnostic immobilier à {loc.name} ({loc.cp})</h2>
 <div className="prose prose-lg dark:prose-invert max-w-none">
              {loc.content.split('\n\n').map((paragraph, idx) => (
 <p key={idx} className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{paragraph}</p>
              ))}
            </div>
            
            {/* NAP - Name Address Phone visible */}
 <div className="mt-10 p-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-700 dark:to-gray-700 rounded-2xl">
 <h3 className="font-bold text-gray-800 dark:text-white mb-4">CPPB Diagnostics Immobilier - {loc.name}</h3>
 <div className="grid md:grid-cols-3 gap-4 text-gray-700 dark:text-gray-300">
 <div className="flex items-center gap-2">
 <MapPin className="w-5 h-5 text-indigo-600" />
 <span>{loc.cp} {loc.name}, {loc.dept}</span>
                </div>
 <div className="flex items-center gap-2">
 <Phone className="w-5 h-5 text-indigo-600" />
                  <SmartPhoneLink className="font-semibold text-indigo-600">04 71 08 27 09</SmartPhoneLink>
                </div>
 <div className="flex items-center gap-2">
 <Mail className="w-5 h-5 text-indigo-600" />
                  <a href="mailto:contact@cppb.fr" className="text-indigo-600">contact@cppb.fr</a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* MAILLAGE INTERNE - Liens vers services */}
 <section className="py-12 bg-gray-50 dark:bg-gray-900">
 <div className="max-w-6xl mx-auto px-4">
 <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6 text-center">Nos services de diagnostic</h2>
 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { name: "DPE", slug: "diagnostic-performance-energetique-dpe" },
              { name: "Amiante", slug: "diagnostic-amiante" },
              { name: "Plomb CREP", slug: "diagnostic-plomb-crep" },
              { name: "Gaz", slug: "diagnostic-gaz" },
              { name: "Électricité", slug: "diagnostic-electricite" },
              { name: "Termites", slug: "diagnostic-termites" },
              { name: "ERP", slug: "etat-risques-pollutions-erp" },
              { name: "Loi Carrez", slug: "loi-carrez-mesurage" }
            ].map(service => (
 <Link key={service.slug} to={`/diagnostics/${service.slug}`} className="flex items-center gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg hover:shadow-md transition-all border">
 <ChevronRight className="w-4 h-4 text-indigo-500" />
 <span className="text-gray-700 dark:text-gray-300 font-medium">{service.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

 <section className="py-12 bg-gray-100 dark:bg-gray-800">
 <div className="max-w-6xl mx-auto px-4">
 <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6 text-center">Nos autres implantations en Haute-Loire et Lozère</h2>
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherLocs.map(([k, l]) => (
 <Link key={k} to={`/diagnostic-immobilier-${k}`} className="flex items-center gap-4 p-4 bg-white dark:bg-gray-700 rounded-xl hover:shadow-lg transition-all">
 <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center"><MapPin className="w-6 h-6 text-white" /></div>
 <div><h3 className="font-semibold text-gray-800 dark:text-white">{l.name}</h3><p className="text-sm text-gray-500">{l.cp} - {l.dept}</p></div>
 <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BREADCRUMB visible */}
 <section className="py-6 bg-white dark:bg-gray-900 border-t">
 <div className="max-w-6xl mx-auto px-4">
 <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
 <Link to="/" className="hover:text-indigo-600">Accueil</Link>
 <ChevronRight className="w-4 h-4" />
 <Link to="/diagnostics" className="hover:text-indigo-600">Diagnostics</Link>
 <ChevronRight className="w-4 h-4" />
 <span className="text-gray-800 dark:text-white font-medium">{loc.name}</span>
          </nav>
        </div>
      </section>

 <section className="py-16 bg-gradient-to-r from-indigo-600 to-purple-600 text-center">
 <h2 className="text-2xl font-bold text-white mb-4">Besoin d'un diagnostic à {loc.name} ?</h2>
 <p className="text-white/90 mb-8">Devis gratuit • Intervention 24-48h • Rapport 72h</p>
 <SmartPhoneLink className="inline-flex items-center gap-3 px-10 py-5 bg-white text-indigo-600 font-bold text-lg rounded-full hover:shadow-2xl">
 <Phone className="w-6 h-6" />04 71 08 27 09
        </SmartPhoneLink>
      </section>

 <footer className="py-8 bg-gray-900 text-center">
 <p className="text-gray-400 text-sm">© 2025 CPPB Diagnostics Immobilier - Expert certifié B.2.C</p>
 <div className="mt-4 flex justify-center gap-4">
 <Link to="/" className="text-gray-400 hover:text-white text-sm">Accueil</Link>
 <Link to="/diagnostics" className="text-gray-400 hover:text-white text-sm">Diagnostics</Link>
 <Link to="/contact" className="text-gray-400 hover:text-white text-sm">Contact</Link>
 <Link to="/mentions-legales" className="text-gray-400 hover:text-white text-sm">Mentions légales</Link>
        </div>
      </footer>
    </div>
  );
};

const RauretPage = () => <LocalPage locKey="rauret-43340" />;
const LePuyPage = () => <LocalPage locKey="le-puy-en-velay" />;
const LangognePage = () => <LocalPage locKey="langogne-48300" />;
const YssingeauxPage = () => <LocalPage locKey="yssingeaux-43200" />;
const BrioudePage = () => <LocalPage locKey="brioude-43100" />;
const MonistrolPage = () => <LocalPage locKey="monistrol-sur-loire-43120" />;

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<HomePage />} />
            {/* Routes SEO propres - Pages dédiées */}
            <Route path="/diagnostics" element={<DiagnosticsPage />} />
            <Route path="/expertise" element={<ExpertisePage />} />
            <Route path="/validite" element={<ValiditePage />} />
            <Route path="/avis" element={<AvisPage />} />
            <Route path="/actualites" element={<ActualitesPage />} />
            {/* Page canonique Actualités */}
            <Route path="/actualites-diagnostics" element={<ActualitesDiagnosticsPage />} />
            {/* Pages alias Actualités (canonical vers /actualites-diagnostics) */}
            <Route path="/actualites-immobilieres" element={<ActualitesAliasPage aliasPath="actualites-immobilieres" />} />
            <Route path="/actualites-haute-loire" element={<ActualitesAliasPage aliasPath="actualites-haute-loire" />} />
            <Route path="/infos-diagnostics" element={<ActualitesAliasPage aliasPath="infos-diagnostics" />} />
            <Route path="/veille-immobiliere" element={<ActualitesAliasPage aliasPath="veille-immobiliere" />} />
            <Route path="/contact" element={<ContactPage />} />
            {/* Pages diagnostics détaillées */}
            <Route path="/diagnostics/:slug" element={<DiagnosticDetailPage />} />
            <Route path="/diagnostic-immobilier/:slug" element={<VillePage />} />
            {/* Pages locales SEO - 6 implantations Google Business */}
            <Route path="/diagnostic-immobilier-rauret-43340" element={<RauretPage />} />
            <Route path="/diagnostic-immobilier-le-puy-en-velay" element={<LePuyPage />} />
            <Route path="/diagnostic-immobilier-langogne-48300" element={<LangognePage />} />
            <Route path="/diagnostic-immobilier-yssingeaux-43200" element={<YssingeauxPage />} />
            <Route path="/diagnostic-immobilier-brioude-43100" element={<BrioudePage />} />
            <Route path="/diagnostic-immobilier-monistrol-sur-loire-43120" element={<MonistrolPage />} />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialitePage />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
          <ExitIntentPopup />
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
